import { sessionManager } from "@/broadcastChannel/sessionManager";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import FieldInput from "@/dashboard/components/fundings/FieldInput";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import usePostMultipart from "@/hooks/usePostMultipart";
import { cn } from "@/libs/utils";
import {
  buildUserAccessBatchEndpoints,
  mergeBatchAccessPayloads,
  readBatchAccessResponses,
} from "@/libs/subcontractorAccess";
import {
  AlertTriangle,
  ArrowRightLeft,
  BriefcaseBusiness,
  CalendarDays,
  ExternalLink,
  Info,
  Link2,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  SynthesesCalendarEvent,
  SynthesesReminderItem,
} from "./SynthesesRightRail";
import {
  getSynthesesMetricToneClasses,
  type SynthesesMetricTone,
} from "./synthesesMetricTones";
import useSynthesesGroupedContractsRead, {
  SynthesesGroupedContractNode,
  SynthesesGroupedFinanceurItem,
} from "./useSynthesesGroupedContractsRead";
import {
  removeLandPaymentRowsFromGroupedData,
  moveLandPaymentRowsInGroupedData,
  replaceLandPaymentRowsInGroupedData,
  replaceReconciledLandPaymentRowsInGroupedData,
  replaceLandPaymentStatusRowsInGroupedData,
} from "./synthesesGroupedContractsCacheUtils";
import { ProjectLandPaymentsTimelinePanel } from "./SynthesesFinanceursReadOnlyBoard";

type SubcontractorItem = {
  id: number;
  name: string;
  email: string;
  fullAccess: boolean;
  memberIds: number[];
  memberOptions: IntervenantRecipientOption[];
  memberCount: number;
};

type IntervenantRecipientOption = {
  id: number;
  label: string;
};

type FinanceurOption = {
  id: number;
  name: string;
};

type AccessProjectItem = {
  id: number;
  name: string;
  programName: string;
};

type ExistingContractOption = {
  id: string;
  label: string;
  contract: SynthesesGroupedContractNode;
  years: string[];
};

type AccessProgramResponse = Array<{
  id?: number;
  name?: string;
  access?: boolean;
  projects?: Array<{
    id?: number;
    name?: string;
    access?: boolean;
  }>;
}>;

type EditableProjectRowDraft = {
  id: string;
  lineId: number | null;
  realFundingId: number | null;
  projectId: number | null;
  projectName: string;
  title: string;
  amountEuro: string;
  desiredFundingIds: number[];
  desiredFundingAllocations: DesiredFundingAllocationDraft[];
  initialDesiredFundingIds: number[];
  desiredFundingSelectionTouched: boolean;
};

type EditableYearGroupDraft = {
  id: string;
  year: string;
  projectRows: EditableProjectRowDraft[];
};

type IntervenantContractLineItem = {
  id: string;
  contract: SynthesesGroupedContractNode;
  synthesesContractId: number | null;
  lineId: number | null;
  realFundingId: number | null;
  desiredFundingSourceIds: number[];
  projectId: number;
  projectName: string;
  year: string;
  amountEuro: number;
  financeurId: number | null;
  financeurName: string;
  contractDate: string | null;
  dateUsesFallback: boolean;
  isVerdeo: boolean;
  title: string;
  lineTitle: string;
  notes: string;
};

type ProjectYearGroup = {
  year: string;
  totalAmountEuro: number;
  contracts: IntervenantContractLineItem[];
};

type IntervenantProjectItem = {
  id: number;
  name: string;
  programName: string;
  totalAmountEuro: number;
  contractCount: number;
  funderCount: number;
  yearGroups: ProjectYearGroup[];
};

type EditorState = {
  mode: "create" | "edit";
  createSource: "new" | "existing";
  projectId: number;
  projectName: string;
  initialYear: string;
  targetLineId: number | null;
  targetRealFundingId: number | null;
  contract: SynthesesGroupedContractNode | null;
  financeurId: number | null;
  title: string;
  lineTitle: string;
  contractDate: string;
  year: string;
  amountEuro: string;
  notes: string;
  desiredFundingIds: number[];
  desiredFundingAllocations: DesiredFundingAllocationDraft[];
  initialDesiredFundingIds: number[];
  desiredFundingSelectionTouched: boolean;
};

type LinePaymentPreview = {
  id: string;
  at: string | null;
  amountEuro: number;
  synthesesContractId?: number | null;
  synthesesContractLineId?: number | null;
  lineId?: number | null;
  realFundingId?: number | null;
  lineTitle?: string | null;
  projectId?: number | null;
  projectName?: string | null;
  projectIndicatorId?: number | null;
  landPaymentId?: number | null;
  landPaymentStatus?: number | null;
  source?: string | null;
  status?: number | null;
  dateMeasure?: string | null;
  dateVerified?: string | null;
  pjVerified?: TerrainMediaRef | null;
  recipientSubcontractorId?: number | null;
  recipientSubcontractor?: unknown;
};

type TerrainMediaRef = {
  id: number | null;
  name: string | null;
  uri?: string | null;
  path?: string | null;
};

type ProgramsCatalogResponse = Array<{
  id?: number;
  name?: string;
  projects?: Array<{
    id?: number;
    name?: string;
    defaultImage?: {
      uri?: string | null;
    } | null;
    type?: {
      image?: {
        uri?: string | null;
      } | null;
    } | null;
  }>;
}>;

type ProjectVisualCatalogItem = {
  id: number;
  name: string;
  programName: string;
  pictureUri: string | null;
};

type ProjectDesiredFundingOption = {
  id: number;
  title: string;
  amount: number;
  convertedAmount: number | null;
  currencyCode: string | null;
  currencySymbol: string | null;
};

type ProjectDesiredFundingCatalogEntry = {
  projectId: number;
  desiredFundings: ProjectDesiredFundingOption[];
};

type DesiredFundingAllocationDraft = {
  desiredFundingId: number;
  amountEuro: string;
};

type TerrainReconcileState = {
  lineId: string;
  lineLabel: string;
  synthesesContractId: number | null;
  synthesesContractLineId: number | null;
  realFundingId: number | null;
  landPaymentId: number | null;
  landPaymentStatus: number | null;
  plannedRows: LinePaymentPreview[];
  realizedRows: LinePaymentPreview[];
  certifiedRows: LinePaymentPreview[];
  selectedEstimatedId: number | null;
  selectedMeasuredId: number | null;
  selectedVerifiedId: number | null;
};

type TerrainQuickLinkState = {
  projectKey: string;
  label: string;
  contractNodeId: string;
  synthesesContractId: number | null;
  realFundingId: number | null;
  targetLandPaymentId: number | null;
  targetLandPaymentStatus: number | null;
  targetPayment: LinePaymentPreview | null;
  targetLandPaymentRows: LinePaymentPreview[];
  selectedEstimatedId: number | null;
  selectedMeasuredId: number | null;
  selectedVerifiedId: number | null;
  selectedEstimatedPayment: LinePaymentPreview | null;
  selectedMeasuredPayment: LinePaymentPreview | null;
  selectedVerifiedPayment: LinePaymentPreview | null;
  error: string | null;
};

type TerrainMoveTargetOption = {
  lineId: number;
  label: string;
};

type TerrainMoveState = {
  lineId: string;
  paymentRowId: string;
  landPaymentId: number;
  selectedTargetLineId: number | null;
  targetOptions: TerrainMoveTargetOption[];
};

type TerrainPaymentEditorState = {
  lineId: string;
  lineLabel: string;
  synthesesContractId: number | null;
  synthesesContractLineId: number | null;
  realFundingId: number | null;
  projectId?: number | null;
  projectName?: string | null;
  lineTitle?: string | null;
  landPaymentId: number | null;
  status: number;
  amountEuro: string;
  expectedAt: string;
  dateMeasure: string;
  dateVerified: string;
  pjVerified: TerrainMediaRef | null;
  recipientSubcontractorId: number | null;
};

type TerrainCertifiedIndicatorOption = {
  id: number;
  label: string;
  disabled: boolean;
};

type TerrainCertifiedLineOption = {
  lineId: string;
  label: string;
  synthesesContractId: number | null;
  synthesesContractLineId: number | null;
  realFundingId: number | null;
  amountEuro: number;
};

type TerrainCertifiedEditorState = {
  lineId: string;
  lineLabel: string;
  contractNodeId: string;
  synthesesContractId: number | null;
  synthesesContractLineId: number | null;
  realFundingId: number | null;
  projectId: number | null;
  landPaymentId: number | null;
  year: number;
  monthIndex: number;
  amountEuro: string;
  selectedLineId: string;
  targetLineOptions: TerrainCertifiedLineOption[];
  pjVerified: TerrainMediaRef | null;
  estimatedIndicatorId: number | null;
  measuredIndicatorId: number | null;
  estimatedOptions: TerrainCertifiedIndicatorOption[];
  measuredOptions: TerrainCertifiedIndicatorOption[];
  recipientSubcontractorId: number | null;
  allRows: LinePaymentPreview[];
};

type FinanceurNavigationTarget = {
  financeurId: string | null;
  financeurName: string | null;
  synthesesContractId: number | null;
  contractNodeId: string | null;
  projectFilterKey: string | null;
  projectName: string | null;
  action: string | null;
  targetLineId: number | null;
  targetProjectId: number | null;
};

type IntervenantTerrainTimelineContractLink = {
  key: string;
  href: string;
  target: FinanceurNavigationTarget;
  financeurName: string;
  contractTitle: string;
  contractDate: string | null;
  lineTitle: string;
  at: string | null;
  amountEuro: number;
  status: number;
};

type IntervenantTerrainTimelineMonthlyBucket = {
  plannedEuro: number;
  realizedEuro: number;
  certifiedEuro: number;
  plannedLinks: IntervenantTerrainTimelineContractLink[];
  realizedLinks: IntervenantTerrainTimelineContractLink[];
  certifiedLinks: IntervenantTerrainTimelineContractLink[];
};

type IntervenantTerrainTimelineYearSummary = {
  year: string;
  plannedEuro: number;
  realizedEuro: number;
  certifiedEuro: number;
  months: Record<number, IntervenantTerrainTimelineMonthlyBucket>;
};

const SYNTHESES_SECONDARY_BUTTON_CLASS =
  "inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50";
const SYNTHESES_PRIMARY_BUTTON_CLASS =
  "inline-flex items-center rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700";
const SYNTHESES_DANGER_BUTTON_CLASS =
  "inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-2 py-1 text-[11px] text-rose-700 hover:bg-rose-50";
const SYNTHESES_INPUT_CLASS =
  "w-full rounded-md border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-900 outline-none ring-emerald-200 focus:ring-2";
const FINANCEUR_NAVIGATION_TARGET_STORAGE_KEY =
  "syntheses:financeurs:navigation-target";
const UNASSIGNED_SUBCONTRACTOR_ID = -999_999;
const UNASSIGNED_SUBCONTRACTOR_LABEL = "Projets non affectés";
const UNASSIGNED_SUBCONTRACTOR_ITEM: SubcontractorItem = {
  id: UNASSIGNED_SUBCONTRACTOR_ID,
  name: UNASSIGNED_SUBCONTRACTOR_LABEL,
  email: "",
  fullAccess: false,
  memberIds: [],
  memberOptions: [],
  memberCount: 0,
};
const INTERVENANT_TIMELINE_MONTH_COLUMNS: Array<{ index: number; label: string }> = [
  { index: 0, label: "Jan" },
  { index: 1, label: "Fev" },
  { index: 2, label: "Mar" },
  { index: 3, label: "Avr" },
  { index: 4, label: "Mai" },
  { index: 5, label: "Juin" },
  { index: 6, label: "Juil" },
  { index: 7, label: "Aou" },
  { index: 8, label: "Sep" },
  { index: 9, label: "Oct" },
  { index: 10, label: "Nov" },
  { index: 11, label: "Dec" },
];
const INTERVENANT_TIMELINE_LABEL_COLUMN_CLASS = "w-[156px] shrink-0";
const INTERVENANT_TIMELINE_CELLS_ROW_CLASS =
  "flex flex-nowrap gap-1 [&>div]:!w-[62px] [&>div]:!min-w-[62px] [&>div]:!max-w-[62px] [&>div]:!flex-none";

function toTrimmedText(value: unknown) {
  return String(value ?? "").trim();
}

function getYear(value: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.getFullYear();
}

function buildLinePaymentsForAllYears(
  rows: Array<{
    id: number | null;
    projectIndicatorId?: number | null;
    landPaymentId?: number | null;
    landPaymentStatus?: number | null;
    realFundingId?: number | null;
    synthesesContractId?: number | null;
    synthesesContractLineId?: number | null;
    lineId?: number | null;
    lineTitle?: string | null;
    projectId?: number | null;
    projectName?: string | null;
    at: string | null;
    amountEuro: number;
    source?: string | null;
    status?: number | null;
    dateMeasure?: string | null;
    dateVerified?: string | null;
    pjVerified?: TerrainMediaRef | null;
    recipientSubcontractorId?: number | null;
    recipientSubcontractor?: unknown;
  }>,
  realFundingId: number | null,
  keyPrefix: string,
  context: {
    synthesesContractId?: number | null;
    synthesesContractLineId?: number | null;
  } = {},
) {
  const targetRealFundingId = Number(realFundingId || 0);
  const targetLineId = Number(context.synthesesContractLineId || 0);
  const targetContractId = Number(context.synthesesContractId || 0);
  if (!targetRealFundingId && !targetLineId && !targetContractId) return [];

  return rows
    .filter((row) => {
      const rowLineId = Number(row.synthesesContractLineId ?? row.lineId ?? 0);
      if (targetLineId > 0 && rowLineId > 0) {
        return rowLineId === targetLineId;
      }

      const rowRealFundingId = Number(row.realFundingId || 0);
      if (targetRealFundingId > 0 && rowRealFundingId > 0) {
        return rowRealFundingId === targetRealFundingId;
      }

      if (targetLineId > 0) {
        return false;
      }

      const rowContractId = Number(row.synthesesContractId || 0);
      return targetContractId > 0 && rowContractId === targetContractId;
    })
    .map((row, index) => ({
      id: `${keyPrefix}-${row.id ?? index}`,
      at: row.at,
      amountEuro: Number(Number(row.amountEuro || 0).toFixed(2)),
      synthesesContractId: Number(row.synthesesContractId || 0) > 0
        ? Number(row.synthesesContractId)
        : null,
      synthesesContractLineId: Number(row.synthesesContractLineId ?? row.lineId ?? 0) > 0
        ? Number(row.synthesesContractLineId ?? row.lineId)
        : null,
      lineId: Number(row.synthesesContractLineId ?? row.lineId ?? 0) > 0
        ? Number(row.synthesesContractLineId ?? row.lineId)
        : null,
      realFundingId: Number(row.realFundingId || 0) > 0
        ? Number(row.realFundingId)
        : null,
      lineTitle: typeof row.lineTitle === "string" ? row.lineTitle : null,
      projectId: Number(row.projectId || 0) > 0 ? Number(row.projectId) : null,
      projectName: typeof row.projectName === "string" ? row.projectName : null,
      projectIndicatorId: Number(row.projectIndicatorId || 0) > 0
        ? Number(row.projectIndicatorId)
        : null,
      landPaymentId: Number(row.landPaymentId || 0) > 0
        ? Number(row.landPaymentId)
        : null,
      landPaymentStatus: Number.isFinite(Number(row.landPaymentStatus))
        ? Number(row.landPaymentStatus)
        : null,
      source: typeof row.source === "string" ? row.source : null,
      status: Number.isFinite(Number(row.status)) ? Number(row.status) : null,
      dateMeasure: typeof row.dateMeasure === "string" ? row.dateMeasure : null,
      dateVerified: typeof row.dateVerified === "string" ? row.dateVerified : null,
      pjVerified: row.pjVerified && Number(row.pjVerified.id || 0) > 0
        ? row.pjVerified
        : null,
      recipientSubcontractorId: getLandPaymentRecipientId(row),
      recipientSubcontractor: row.recipientSubcontractor ?? null,
    }))
    .filter((row) => row.amountEuro > 0) satisfies LinePaymentPreview[];
}

function buildLinePaymentsForYear(
  rows: Array<{
    id: number | null;
    projectIndicatorId?: number | null;
    landPaymentId?: number | null;
    landPaymentStatus?: number | null;
    realFundingId?: number | null;
    synthesesContractId?: number | null;
    synthesesContractLineId?: number | null;
    lineId?: number | null;
    lineTitle?: string | null;
    projectId?: number | null;
    projectName?: string | null;
    at: string | null;
    amountEuro: number;
    source?: string | null;
    status?: number | null;
  }>,
  realFundingId: number | null,
  year: string,
  keyPrefix: string,
  context: {
    synthesesContractId?: number | null;
    synthesesContractLineId?: number | null;
  } = {},
) {
  if (!/^\d{4}$/.test(year)) return [];

  return buildLinePaymentsForAllYears(rows, realFundingId, keyPrefix, context).filter(
    (row) => getYear(row.at) === Number(year),
  );
}

function sumPaymentPreviews(rows: LinePaymentPreview[]) {
  return Number(
    rows.reduce((sum, row) => sum + Number(row.amountEuro || 0), 0).toFixed(2),
  );
}

function normalizeTerrainDateInput(value: string | null | undefined) {
  return typeof value === "string" ? value.slice(0, 10) : "";
}

function formatTerrainAmountInput(value: number | null | undefined) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return "";
  return String(Number(parsed.toFixed(2)));
}

function buildOptimisticLandPaymentRows({
  landPaymentId,
  amountEuro,
  status,
  expectedAt,
  dateMeasure,
  dateVerified,
  realFundingId,
  synthesesContractId,
  synthesesContractLineId,
  projectId,
  projectName,
  lineTitle,
  pjVerified,
  recipientSubcontractorId,
}: {
  landPaymentId: number | null;
  amountEuro: number;
  status: number;
  expectedAt: string;
  dateMeasure?: string | null;
  dateVerified?: string | null;
  realFundingId?: number | null;
  synthesesContractId?: number | null;
  synthesesContractLineId?: number | null;
  projectId?: number | null;
  projectName?: string | null;
  lineTitle?: string | null;
  pjVerified?: TerrainMediaRef | null;
  recipientSubcontractorId?: number | null;
}): SynthesesGroupedContractNode["landPayments"] {
  const normalizedAmount = Number(amountEuro || 0);
  if (!Number.isFinite(normalizedAmount) || normalizedAmount <= 0) return [];

  const normalizedStatus = Math.max(1, Math.min(3, Number(status || 1)));
  const rawLandPaymentId = Number(landPaymentId || 0);
  const normalizedLandPaymentId =
    Number.isFinite(rawLandPaymentId) && rawLandPaymentId !== 0
      ? rawLandPaymentId
      : -Date.now();
  const rows: SynthesesGroupedContractNode["landPayments"] = [];
  const pushRow = (rowStatus: number, at: string | null | undefined) => {
    if (!at) return;
    rows.push({
      id: -Math.abs(normalizedLandPaymentId * 10 + rowStatus),
      projectIndicatorId: null,
      landPaymentId: normalizedLandPaymentId,
      landPaymentStatus: normalizedStatus,
      realFundingId: Number(realFundingId || 0) > 0 ? Number(realFundingId) : null,
      synthesesContractId:
        Number(synthesesContractId || 0) > 0 ? Number(synthesesContractId) : null,
      synthesesContractLineId:
        Number(synthesesContractLineId || 0) > 0
          ? Number(synthesesContractLineId)
          : null,
      lineId:
        Number(synthesesContractLineId || 0) > 0
          ? Number(synthesesContractLineId)
          : null,
      lineTitle: lineTitle || null,
      projectId: Number(projectId || 0) > 0 ? Number(projectId) : null,
      projectName: projectName || null,
      at,
      amountLocal: Number(normalizedAmount.toFixed(2)),
      amountEuro: Number(normalizedAmount.toFixed(2)),
      source: "land_payment",
      status: rowStatus,
      dateMeasure: rowStatus === 2 ? at : dateMeasure || null,
      dateVerified: rowStatus === 3 ? at : dateVerified || null,
      pjVerified: pjVerified || null,
      recipientSubcontractorId:
        Number(recipientSubcontractorId || 0) > 0
          ? Number(recipientSubcontractorId)
          : null,
      recipientSubcontractor: null,
    });
  };

  pushRow(1, expectedAt);
  if (normalizedStatus >= 2) {
    pushRow(2, dateMeasure || expectedAt);
  }
  if (normalizedStatus >= 3) {
    pushRow(3, dateVerified || dateMeasure || expectedAt);
  }

  return rows;
}

function getDefaultTerrainDateForYear(year: string | number | null | undefined) {
  const normalizedYear = String(year ?? "").trim();
  if (/^\d{4}$/.test(normalizedYear)) {
    return `${normalizedYear}-01-01`;
  }

  return new Date().toISOString().slice(0, 10);
}

function getTerrainMediaHref(media: TerrainMediaRef | null | undefined) {
  const mediaUri = typeof media?.uri === "string" ? media.uri.trim() : "";
  if (mediaUri) {
    if (/^https?:\/\//i.test(mediaUri)) return mediaUri;
    return `${import.meta.env.VITE_MEDIA_BASE}${mediaUri}`;
  }

  const mediaPath = typeof media?.path === "string" ? media.path.trim() : "";
  if (mediaPath) {
    return `${import.meta.env.VITE_IP_API}/media/${mediaPath}`;
  }

  return "";
}

function getTerrainMediaLabel(media: TerrainMediaRef | null | undefined) {
  if (typeof media?.name === "string" && media.name.trim()) {
    return media.name;
  }

  const mediaUri = typeof media?.uri === "string" ? media.uri.trim() : "";
  if (mediaUri) {
    return mediaUri.split("/").pop() || "justificatif";
  }

  const mediaPath = typeof media?.path === "string" ? media.path.trim() : "";
  if (mediaPath) {
    return mediaPath.split("/").pop() || "justificatif";
  }

  return "justificatif";
}

function canManageExistingLandPaymentRow(
  payment: LinePaymentPreview,
  allLandPaymentRows: LinePaymentPreview[],
) {
  if (payment.source !== "land_payment" || !isPositiveId(payment.landPaymentId)) {
    return false;
  }

  const sameLandPaymentRows = allLandPaymentRows.filter(
    (row) =>
      row.source === "land_payment"
      && Number(row.landPaymentId || 0) === Number(payment.landPaymentId || 0),
  );
  if (sameLandPaymentRows.length === 0) {
    return false;
  }

  const landPaymentStatus = Number(payment.landPaymentStatus || payment.status || 0);
  const primaryRow = sameLandPaymentRows.find(
    (row) => Number(row.status || 0) === landPaymentStatus,
  );

  return (primaryRow ?? sameLandPaymentRows[0])?.id === payment.id;
}

function buildTerrainPaymentEditorDraft(
  rows: LinePaymentPreview[],
  payment: LinePaymentPreview | null | undefined,
  fallbackYear: string,
) {
  const landPaymentId = isPositiveId(payment?.landPaymentId)
    ? Number(payment?.landPaymentId)
    : null;
  const relatedRows = null === landPaymentId
    ? []
    : rows.filter(
        (row) =>
          row.source === "land_payment"
          && Number(row.landPaymentId || 0) === landPaymentId,
      );
  const relevantRows = relatedRows.length > 0
    ? relatedRows
    : (payment ? [payment] : []);
  const status = Number(payment?.landPaymentStatus || payment?.status || 1) || 1;
  const primaryRow = relevantRows.find((row) => Number(row.status || 0) === status)
    ?? relevantRows[0]
    ?? payment
    ?? null;
  const plannedRow = relevantRows.find((row) => Number(row.status || 0) === 1)
    ?? primaryRow;
  const realizedRow = relevantRows.find((row) => Number(row.status || 0) === 2)
    ?? primaryRow;
  const certifiedRow = relevantRows.find((row) => Number(row.status || 0) === 3)
    ?? primaryRow;

  return {
    landPaymentId,
    status,
    amountEuro: formatTerrainAmountInput(primaryRow?.amountEuro ?? payment?.amountEuro ?? 0),
    expectedAt:
      normalizeTerrainDateInput(plannedRow?.at || primaryRow?.at) ||
      getDefaultTerrainDateForYear(fallbackYear),
    dateMeasure:
      normalizeTerrainDateInput(realizedRow?.dateMeasure || realizedRow?.at),
    dateVerified:
      normalizeTerrainDateInput(certifiedRow?.dateVerified || certifiedRow?.at),
    pjVerified: certifiedRow?.pjVerified ?? primaryRow?.pjVerified ?? payment?.pjVerified ?? null,
    recipientSubcontractorId:
      getLandPaymentRecipientId(primaryRow) ??
      getLandPaymentRecipientId(payment) ??
      null,
  };
}

function formatTerrainIndicatorOption(row: LinePaymentPreview) {
  const monthIndex = extractTimelineMonthIndex(row.at);
  const monthLabel = monthIndex === null
    ? "Mois non renseigné"
    : INTERVENANT_TIMELINE_MONTH_COLUMNS.find((month) => month.index === monthIndex)?.label || "Mois";
  const yearLabel = extractTimelineYear(row.at) || "Année non renseignée";

  return `${monthLabel} ${yearLabel} • ${formatEuro(row.amountEuro)}`;
}

function buildTerrainCertifiedLineOptions(
  line: IntervenantContractLineItem,
): TerrainCertifiedLineOption[] {
  if (!line.id || (!isPositiveId(line.realFundingId) && !isPositiveId(line.lineId))) {
    return [];
  }

  const label = [
    toTrimmedText(line.lineTitle) || toTrimmedText(line.title) || "Poste de dépenses",
    formatEuro(line.amountEuro),
  ].filter(Boolean).join(" • ");

  return [
    {
      lineId: line.id,
      label,
      synthesesContractId: line.synthesesContractId,
      synthesesContractLineId: line.lineId,
      realFundingId: line.realFundingId,
      amountEuro: Number(line.amountEuro || 0),
    },
  ];
}

function isTerrainIndicatorLinkedToAnotherCertified(
  row: LinePaymentPreview,
  rows: LinePaymentPreview[],
  currentLandPaymentId: number | null,
  currentCertifiedIndicatorId: number | null,
) {
  const landPaymentId = Number(row.landPaymentId || 0);
  if (!Number.isFinite(landPaymentId) || landPaymentId <= 0) {
    return false;
  }
  if (currentLandPaymentId && landPaymentId === currentLandPaymentId) {
    return false;
  }

  return rows.some(
    (candidate) =>
      Number(candidate.landPaymentId || 0) === landPaymentId &&
      Number(candidate.status || 0) === 3 &&
      Number(candidate.projectIndicatorId || 0) > 0 &&
      Number(candidate.projectIndicatorId || 0) !== Number(currentCertifiedIndicatorId || 0),
  );
}

function buildTerrainCertifiedIndicatorOptions(
  rows: LinePaymentPreview[],
  status: 1 | 2,
  currentLandPaymentId: number | null,
  currentCertifiedIndicatorId: number | null,
): TerrainCertifiedIndicatorOption[] {
  const seen = new Set<number>();

  return rows
    .filter(
      (row) =>
        Number(row.status || 0) === status &&
        Number(row.projectIndicatorId || 0) > 0,
    )
    .filter((row) => {
      const id = Number(row.projectIndicatorId || 0);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .sort((a, b) => String(a.at || "").localeCompare(String(b.at || "")))
    .map((row) => ({
      id: Number(row.projectIndicatorId || 0),
      label: formatTerrainIndicatorOption(row),
      disabled: isTerrainIndicatorLinkedToAnotherCertified(
        row,
        rows,
        currentLandPaymentId,
        currentCertifiedIndicatorId,
      ),
    }));
}

function findTerrainLinkedIndicatorId(
  rows: LinePaymentPreview[],
  status: 1 | 2,
  landPaymentId: number | null,
) {
  if (!landPaymentId) return null;

  const row = rows.find(
    (candidate) =>
      Number(candidate.landPaymentId || 0) === landPaymentId &&
      Number(candidate.status || 0) === status &&
      Number(candidate.projectIndicatorId || 0) > 0,
  );

  return row ? Number(row.projectIndicatorId || 0) : null;
}

function sumPaymentPreviewsByStatus(
  rows: LinePaymentPreview[],
  status: number,
) {
  return Number(
    rows
      .reduce((sum, row) => {
        return Number(row.status || 0) === status
          ? sum + Number(row.amountEuro || 0)
          : sum;
      }, 0)
      .toFixed(2),
  );
}

function filterPaymentPreviewsByStatus(
  rows: LinePaymentPreview[],
  status: number,
) {
  return rows.filter((row) => Number(row.status || 0) === status);
}

function dedupePaymentPreviewsByMonthYear(rows: LinePaymentPreview[]) {
  const seen = new Set<string>();

  return rows.filter((row) => {
    const date = toTrimmedText(row.at);
    if (!date) {
      return true;
    }

    const monthYearKey = date.slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(monthYearKey)) {
      return true;
    }

    if (seen.has(monthYearKey)) {
      return false;
    }

    seen.add(monthYearKey);
    return true;
  });
}

function extractTimelineYear(value: string | null | undefined) {
  const raw = toTrimmedText(value);
  if (!raw) return null;
  const date = new Date(raw);
  if (!Number.isNaN(date.getTime())) {
    return String(date.getFullYear());
  }
  const match = raw.match(/^(\d{4})/);
  return match?.[1] ?? null;
}

function extractTimelineMonthIndex(value: string | null | undefined) {
  const raw = toTrimmedText(value);
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return date.getMonth();
}

function createIntervenantTerrainTimelineMonthlyBucket(): IntervenantTerrainTimelineMonthlyBucket {
  return {
    plannedEuro: 0,
    realizedEuro: 0,
    certifiedEuro: 0,
    plannedLinks: [],
    realizedLinks: [],
    certifiedLinks: [],
  };
}

function createIntervenantTerrainTimelineYearSummary(
  year: string,
): IntervenantTerrainTimelineYearSummary {
  return {
    year,
    plannedEuro: 0,
    realizedEuro: 0,
    certifiedEuro: 0,
    months: {},
  };
}

function getCurrentTimelineYearString() {
  return String(new Date().getFullYear());
}

function buildIntervenantTerrainTimelineValues(
  yearSummary: IntervenantTerrainTimelineYearSummary,
  key: "plannedEuro" | "realizedEuro" | "certifiedEuro",
) {
  return INTERVENANT_TIMELINE_MONTH_COLUMNS.map((month) =>
    Number((Number(yearSummary.months[month.index]?.[key] || 0)).toFixed(2)),
  );
}

function getFinanceurNavigationProjectFilterKey(
  projectId: number | null | undefined,
  projectName: string | null | undefined,
) {
  const normalizedProjectId = Number(projectId || 0);
  if (Number.isFinite(normalizedProjectId) && normalizedProjectId > 0) {
    return `id:${normalizedProjectId}`;
  }

  const normalizedProjectName = toTrimmedText(projectName).toLocaleLowerCase();
  return normalizedProjectName ? `name:${normalizedProjectName}` : "";
}

function buildFinanceurContractNavigationTarget(
  line: IntervenantContractLineItem,
): FinanceurNavigationTarget {
  const financeurId = Number(line.financeurId || 0);
  const synthesesContractId =
    line.synthesesContractId ?? extractSynthesesContractId(line.contract);
  const projectFilterKey = getFinanceurNavigationProjectFilterKey(
    line.projectId,
    line.projectName,
  );

  return {
    financeurId:
      Number.isFinite(financeurId) && financeurId > 0
        ? String(financeurId)
        : null,
    financeurName: toTrimmedText(line.financeurName) || null,
    synthesesContractId:
      Number.isFinite(Number(synthesesContractId)) && Number(synthesesContractId) > 0
        ? Number(synthesesContractId)
        : null,
    contractNodeId: toTrimmedText(line.contract?.id) || null,
    projectFilterKey: projectFilterKey || null,
    projectName: toTrimmedText(line.projectName) || null,
    action: null,
    targetLineId:
      Number.isFinite(Number(line.lineId || 0)) && Number(line.lineId || 0) > 0
        ? Number(line.lineId)
        : null,
    targetProjectId:
      Number.isFinite(Number(line.projectId || 0)) && Number(line.projectId || 0) > 0
        ? Number(line.projectId)
        : null,
  };
}

function buildFinanceurContractHref(line: IntervenantContractLineItem) {
  const target = buildFinanceurContractNavigationTarget(line);
  const searchParams = new URLSearchParams();

  if (target.financeurId) searchParams.set("targetFinanceurId", target.financeurId);
  if (target.financeurName) searchParams.set("targetFinanceurName", target.financeurName);
  if (target.contractNodeId) searchParams.set("targetContractNodeId", target.contractNodeId);
  if (target.synthesesContractId) {
    searchParams.set("targetSynthesesContractId", String(target.synthesesContractId));
  }
  if (target.projectFilterKey) {
    searchParams.set("targetProjectFilterKey", target.projectFilterKey);
  }
  if (target.projectName) searchParams.set("targetProjectName", target.projectName);
  if (target.targetLineId) searchParams.set("targetLineId", String(target.targetLineId));
  if (target.targetProjectId) {
    searchParams.set("targetProjectId", String(target.targetProjectId));
  }

  const query = searchParams.toString();
  return `/dashboard/syntheses/financeurs${query ? `?${query}` : ""}`;
}

function storeFinanceurContractNavigationTarget(
  target: FinanceurNavigationTarget,
) {
  if (typeof window === "undefined") return;

  window.sessionStorage.setItem(
    FINANCEUR_NAVIGATION_TARGET_STORAGE_KEY,
    JSON.stringify(target),
  );
}

function getIntervenantTerrainTimelineAmountKey(status: number) {
  if (status === 1) return "plannedEuro" as const;
  if (status === 2) return "realizedEuro" as const;
  return "certifiedEuro" as const;
}

function getIntervenantTerrainTimelineLinksKey(status: number) {
  if (status === 1) return "plannedLinks" as const;
  if (status === 2) return "realizedLinks" as const;
  return "certifiedLinks" as const;
}

function buildIntervenantTerrainTimelineContractLink(
  line: IntervenantContractLineItem,
  payment: LinePaymentPreview,
  status: number,
  index: number,
): IntervenantTerrainTimelineContractLink {
  const target = buildFinanceurContractNavigationTarget(line);
  return {
    key: [
      line.id,
      status,
      payment.landPaymentId || payment.projectIndicatorId || payment.id,
      index,
    ].join("-"),
    href: buildFinanceurContractHref(line),
    target,
    financeurName: toTrimmedText(line.financeurName) || "Financeur",
    contractTitle: toTrimmedText(line.title) || "Contrat",
    contractDate: line.contractDate,
    lineTitle: toTrimmedText(line.lineTitle) || toTrimmedText(line.title) || "Poste",
    at: payment.at,
    amountEuro: Number(payment.amountEuro || 0),
    status,
  };
}

function dedupeIntervenantTerrainTimelineLinks(
  links: IntervenantTerrainTimelineContractLink[],
) {
  const seen = new Set<string>();
  return links.filter((link) => {
    const key =
      link.target.contractNodeId ||
      (link.target.synthesesContractId
        ? `syntheses:${link.target.synthesesContractId}`
        : link.key);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function collectIntervenantTerrainTimelineContractLinks(
  yearSummary: IntervenantTerrainTimelineYearSummary,
) {
  const links = Object.values(yearSummary.months).flatMap((bucket) => [
    ...bucket.plannedLinks,
    ...bucket.realizedLinks,
    ...bucket.certifiedLinks,
  ]);

  return dedupeIntervenantTerrainTimelineLinks(links);
}

function buildIntervenantTerrainTimelineYears(projects: IntervenantProjectItem[]) {
  const yearMap = new Map<string, IntervenantTerrainTimelineYearSummary>();

  projects.forEach((project) => {
    project.yearGroups.forEach((group) => {
      group.contracts.forEach((line) => {
        const lineFallbackYear =
          (/^\d{4}$/.test(line.year) ? line.year : null)
          || getYearFromIsoDate(line.contractDate);
        const allPayments = buildLinePaymentsForAllYears(
          line.contract.landPayments,
          line.realFundingId,
          `${line.id}-terrain-timeline`,
          {
            synthesesContractId: line.synthesesContractId,
            synthesesContractLineId: line.lineId,
          },
        );

        [1, 2, 3].forEach((status) => {
          const payments = dedupePaymentPreviewsByMonthYear(
            filterPaymentPreviewsByStatus(allPayments, status),
          );

          payments.forEach((payment, paymentIndex) => {
            const year = extractTimelineYear(payment.at) || lineFallbackYear;
            if (!year) return;

            if (!yearMap.has(year)) {
              yearMap.set(year, createIntervenantTerrainTimelineYearSummary(year));
            }

            const yearNode = yearMap.get(year)!;
            const amount = Number(payment.amountEuro || 0);
            const amountKey = getIntervenantTerrainTimelineAmountKey(status);
            yearNode[amountKey] += amount;

            const monthIndex = extractTimelineMonthIndex(payment.at);
            if (monthIndex === null) return;

            if (!yearNode.months[monthIndex]) {
              yearNode.months[monthIndex] = createIntervenantTerrainTimelineMonthlyBucket();
            }

            const monthBucket = yearNode.months[monthIndex];
            monthBucket[amountKey] += amount;
            monthBucket[getIntervenantTerrainTimelineLinksKey(status)].push(
              buildIntervenantTerrainTimelineContractLink(
                line,
                payment,
                status,
                paymentIndex,
              ),
            );
          });
        });
      });
    });
  });

  return Array.from(yearMap.values())
    .map((yearNode) => ({
      ...yearNode,
      plannedEuro: Number(yearNode.plannedEuro.toFixed(2)),
      realizedEuro: Number(yearNode.realizedEuro.toFixed(2)),
      certifiedEuro: Number(yearNode.certifiedEuro.toFixed(2)),
    }))
    .sort((a, b) => Number(a.year) - Number(b.year));
}

function buildIntervenantContractDateYears(projects: IntervenantProjectItem[]) {
  const years = new Set<number>();

  projects.forEach((project) => {
    project.yearGroups.forEach((group) => {
      group.contracts.forEach((line) => {
        const year = Number(getYearFromIsoDate(line.contractDate || null));
        if (Number.isFinite(year) && year > 0) {
          years.add(year);
        }
      });
    });
  });

  const sortedYears = Array.from(years).sort((a, b) => a - b);
  if (sortedYears.length > 0) {
    return sortedYears.map((year) => String(year));
  }

  return [getCurrentTimelineYearString()];
}

function filterIntervenantProjectsByContractYear(
  projects: IntervenantProjectItem[],
  selectedYear: string,
) {
  if (!/^\d{4}$/.test(selectedYear)) {
    return projects;
  }

  return projects
    .map((project) => {
      const filteredContracts = project.yearGroups
        .flatMap((group) => group.contracts)
        .filter((line) => getYearFromIsoDate(line.contractDate || null) === selectedYear);

      if (filteredContracts.length === 0) {
        return null;
      }

      const uniqueFunders = new Set(
        filteredContracts
          .map((line) => String(line.financeurId || line.financeurName || ""))
          .filter(Boolean),
      );
      const uniqueContracts = new Set(filteredContracts.map((line) => String(line.id || "")));

      return {
        ...project,
        totalAmountEuro: Number(
          filteredContracts
            .reduce((sum, line) => sum + Number(line.amountEuro || 0), 0)
            .toFixed(2),
        ),
        contractCount: uniqueContracts.size,
        funderCount: uniqueFunders.size,
        yearGroups: [
          {
            year: selectedYear,
            totalAmountEuro: Number(
              filteredContracts
                .reduce((sum, line) => sum + Number(line.amountEuro || 0), 0)
                .toFixed(2),
            ),
            contracts: filteredContracts,
          },
        ],
      } satisfies IntervenantProjectItem;
    })
    .filter(Boolean) as IntervenantProjectItem[];
}

function getIntervenantProjectLines(project: IntervenantProjectItem) {
  return project.yearGroups.flatMap((group) => group.contracts);
}

function findIntervenantLineForPayment(
  lines: IntervenantContractLineItem[],
  payment: LinePaymentPreview | null | undefined,
) {
  if (!payment) return null;

  const paymentLineId = Number(payment.synthesesContractLineId || payment.lineId || 0);
  if (Number.isFinite(paymentLineId) && paymentLineId > 0) {
    const rowByLine = lines.find((line) => Number(line.lineId || 0) === paymentLineId);
    if (rowByLine) return rowByLine;
  }

  const paymentRealFundingId = Number(payment.realFundingId || 0);
  if (Number.isFinite(paymentRealFundingId) && paymentRealFundingId > 0) {
    const rowByRealFunding = lines.find(
      (line) => Number(line.realFundingId || 0) === paymentRealFundingId,
    );
    if (rowByRealFunding) return rowByRealFunding;
  }

  return null;
}

function buildIntervenantTerrainMoveTargetOptions(
  project: IntervenantProjectItem,
  sourceLine: IntervenantContractLineItem | null | undefined,
): TerrainMoveTargetOption[] {
  if (!sourceLine) return [];

  const sourceLineId = Number(sourceLine.lineId || 0);
  const sourceProjectId = Number(sourceLine.projectId || 0);
  if (!Number.isFinite(sourceProjectId) || sourceProjectId <= 0) return [];

  return Array.from(
    new Map(
      getIntervenantProjectLines(project)
        .map((line) => {
          const targetLineId = Number(line.lineId || 0);
          const targetProjectId = Number(line.projectId || 0);

          return {
            key: targetLineId,
            projectId: targetProjectId,
            isVerdeo: line.isVerdeo === true,
            value: {
              lineId: targetLineId,
              label: [
                toTrimmedText(line.title) || "Contrat",
                formatEuro(Number(line.amountEuro || 0)),
              ]
                .filter(Boolean)
                .join(" • "),
            } satisfies TerrainMoveTargetOption,
          };
        })
        .filter(
          (option) =>
            option.key > 0
            && option.key !== sourceLineId
            && option.projectId > 0
            && option.projectId === sourceProjectId
            && option.isVerdeo !== true,
        )
        .map((option) => [option.key, option.value]),
    ).values(),
  );
}

function buildIntervenantTerrainTimelineCellKey(
  lineKey: string,
  recipientSubcontractorId: number | null | undefined,
  status: number,
  year: number | string,
  monthIndex: number,
) {
  const recipientId = Number(recipientSubcontractorId || 0);
  const recipientKey =
    Number.isFinite(recipientId) && recipientId > 0
      ? `recipient-${recipientId}`
      : "recipient-unassigned";

  return `${lineKey}-${recipientKey}-${status}-${toTrimmedText(year)}-${monthIndex}`;
}

function getLandStatusLabel(status: number) {
  if (status === 1) return "Prévu";
  if (status === 2) return "Réalisé";
  if (status === 3) return "Certifié";
  return "Autre";
}

function getLandPaymentSourceClasses(source: string | null | undefined) {
  if (source === "project_indicator") {
    return "border-sky-200 bg-sky-50/35";
  }

  return "border-emerald-200 bg-emerald-50/30";
}

function isPositiveId(value: unknown) {
  return Number.isFinite(Number(value)) && Number(value) > 0;
}

function canLinkExistingLandPaymentRow(
  payment: LinePaymentPreview,
  plannedRows: LinePaymentPreview[],
  realizedRows: LinePaymentPreview[],
  certifiedRows: LinePaymentPreview[],
  allLandPaymentRows: LinePaymentPreview[],
) {
  if (payment.source !== "land_payment" || !isPositiveId(payment.landPaymentId)) {
    return false;
  }

  const status = Number(payment.landPaymentStatus || payment.status || 0);
  if (Number(payment.status || 0) !== status) {
    return false;
  }

  const linkedStatuses = new Set(
    allLandPaymentRows
      .filter(
        (row) =>
          row.source === "land_payment"
          && Number(row.landPaymentId || 0) === Number(payment.landPaymentId || 0),
      )
      .map((row) => Number(row.status || 0))
      .filter((rowStatus) => rowStatus > 0),
  );

  if (status === 1) {
    return (
      (realizedRows.length > 0 && !linkedStatuses.has(2))
      || (certifiedRows.length > 0 && !linkedStatuses.has(3))
    );
  }
  if (status === 2) {
    return (
      (plannedRows.length > 0 && !linkedStatuses.has(1))
      || (certifiedRows.length > 0 && !linkedStatuses.has(3))
    );
  }
  if (status === 3) {
    return (
      (plannedRows.length > 0 && !linkedStatuses.has(1))
      || (realizedRows.length > 0 && !linkedStatuses.has(2))
    );
  }

  return false;
}

function canMoveLandPaymentRow(
  payment: LinePaymentPreview,
  targetOptions: TerrainMoveTargetOption[],
  allLandPaymentRows: LinePaymentPreview[],
) {
  if (payment.source !== "land_payment" || !isPositiveId(payment.landPaymentId)) {
    return false;
  }

  if (targetOptions.length === 0) {
    return false;
  }

  const sameLandPaymentRows = allLandPaymentRows.filter(
    (row) =>
      row.source === "land_payment"
      && Number(row.landPaymentId || 0) === Number(payment.landPaymentId || 0),
  );
  const landPaymentStatus = Number(payment.landPaymentStatus || payment.status || 0);
  const primaryRow = sameLandPaymentRows.find(
    (row) => Number(row.status || 0) === landPaymentStatus,
  );

  if (primaryRow) {
    return primaryRow.id === payment.id;
  }

  return sameLandPaymentRows[0]?.id === payment.id;
}

function isTerrainMoveTargetRow(
  payment: LinePaymentPreview,
  terrainMoveState: TerrainMoveState | null,
) {
  if (
    !terrainMoveState
    || !isPositiveId(terrainMoveState.landPaymentId)
    || !toTrimmedText(terrainMoveState.paymentRowId)
  ) {
    return false;
  }

  return payment.id === terrainMoveState.paymentRowId;
}

function isTerrainMoveIncludedRow(
  payment: LinePaymentPreview,
  terrainMoveState: TerrainMoveState | null,
) {
  if (!terrainMoveState || !isPositiveId(terrainMoveState.landPaymentId)) {
    return false;
  }

  return Number(payment.landPaymentId || 0) === Number(terrainMoveState.landPaymentId || 0);
}

function isTerrainReconcileTargetRow(
  payment: LinePaymentPreview,
  terrainReconcileState: TerrainReconcileState | null,
) {
  if (!terrainReconcileState || !isPositiveId(terrainReconcileState.landPaymentId)) {
    return false;
  }

  return (
    Number(payment.landPaymentId || 0) === Number(terrainReconcileState.landPaymentId || 0)
    && Number(payment.status || 0) === Number(terrainReconcileState.landPaymentStatus || 0)
  );
}

function canSelectTerrainPaymentInReconcile(
  status: number,
  payment: LinePaymentPreview,
  terrainReconcileState: TerrainReconcileState | null,
) {
  if (
    !terrainReconcileState
    || payment.source !== "project_indicator"
    || !isPositiveId(payment.projectIndicatorId)
  ) {
    return false;
  }

  const targetStatus = Number(terrainReconcileState.landPaymentStatus || 0);
  if (isPositiveId(terrainReconcileState.landPaymentId)) {
    if (targetStatus === 1) {
      return status === 2 || status === 3;
    }
    if (targetStatus === 2) {
      return status === 1 || status === 3;
    }
    if (targetStatus === 3) {
      return status === 1 || status === 2;
    }

    return false;
  }

  return status === 1 || status === 2 || status === 3;
}

function canSubmitTerrainReconcile(terrainReconcileState: TerrainReconcileState | null) {
  if (!terrainReconcileState) return false;

  const targetStatus = Number(terrainReconcileState.landPaymentStatus || 0);
  if (isPositiveId(terrainReconcileState.landPaymentId)) {
    if (targetStatus === 1) {
      return isPositiveId(terrainReconcileState.selectedMeasuredId);
    }
    if (targetStatus === 2) {
      return (
        isPositiveId(terrainReconcileState.selectedEstimatedId)
        || isPositiveId(terrainReconcileState.selectedVerifiedId)
      );
    }
    if (targetStatus === 3) {
      return (
        isPositiveId(terrainReconcileState.selectedEstimatedId)
        || isPositiveId(terrainReconcileState.selectedMeasuredId)
      );
    }

    return false;
  }

  return (
    isPositiveId(terrainReconcileState.selectedEstimatedId)
    && isPositiveId(terrainReconcileState.selectedMeasuredId)
  );
}

function getTerrainReconcileInstruction(terrainReconcileState: TerrainReconcileState | null) {
  const targetStatus = Number(terrainReconcileState?.landPaymentStatus || 0);
  if (isPositiveId(terrainReconcileState?.landPaymentId) && targetStatus === 1) {
    return "Selectionnez un réalisé, et éventuellement un certifié, pour compléter ce versement à l'intervenant prévu.";
  }
  if (isPositiveId(terrainReconcileState?.landPaymentId) && targetStatus === 2) {
    return "Selectionnez un prévu et/ou un certifié pour compléter ce versement à l'intervenant réalisé.";
  }
  if (isPositiveId(terrainReconcileState?.landPaymentId) && targetStatus === 3) {
    return "Selectionnez un prévu et/ou un réalisé pour compléter ce versement à l'intervenant certifié.";
  }

  return "Selectionnez directement dans les listes un prévu, un réalisé, et éventuellement un certifié.";
}

function getTerrainReconcileSubmitLabel(
  terrainReconcileState: TerrainReconcileState | null,
  savingTerrainReconcile: boolean,
) {
  const hasTargetLandPayment = isPositiveId(terrainReconcileState?.landPaymentId);
  if (savingTerrainReconcile) {
    return hasTargetLandPayment ? "Mise a jour..." : "Création...";
  }

  return hasTargetLandPayment
    ? "Mettre a jour le versement a l'intervenant"
    : "Créer le versement a l'intervenant";
}

function getTerrainQuickLinkSelectedId(
  state: TerrainQuickLinkState | null,
  status: 1 | 2 | 3,
) {
  if (!state) return null;
  if (status === 1) return state.selectedEstimatedId;
  if (status === 2) return state.selectedMeasuredId;
  return state.selectedVerifiedId;
}

function getTerrainQuickLinkContextRows(state: TerrainQuickLinkState) {
  const selectedRows = [
    state.selectedEstimatedPayment,
    state.selectedMeasuredPayment,
    state.selectedVerifiedPayment,
  ].filter((row): row is LinePaymentPreview => !!row);
  const selectedStatuses = new Set(
    selectedRows
      .map((row) => Number(row.status || 0))
      .filter((status) => [1, 2, 3].includes(status)),
  );
  const targetRows = (Array.isArray(state.targetLandPaymentRows)
    ? state.targetLandPaymentRows
    : []
  ).filter((row) => !selectedStatuses.has(Number(row.status || 0)));
  const targetPayment =
    state.targetPayment && !selectedStatuses.has(Number(state.targetPayment.status || 0))
      ? state.targetPayment
      : null;
  const rows = [
    ...targetRows,
    targetPayment,
    ...selectedRows,
  ].filter((row): row is LinePaymentPreview => !!row);

  const uniqueRows = new Map<string, LinePaymentPreview>();
  rows.forEach((row) => {
    const status = Number(row.status || 0);
    const projectIndicatorId = Number(row.projectIndicatorId || 0);
    const key =
      row.landPaymentId && Number(row.landPaymentId) > 0
        ? `land-${row.landPaymentId}-${status}-${projectIndicatorId || row.id}`
        : projectIndicatorId > 0
          ? `indicator-${projectIndicatorId}`
          : `${row.id}-${status}`;
    uniqueRows.set(key, row);
  });

  return Array.from(uniqueRows.values());
}

function getTerrainQuickLinkSelectedIndicatorIds(state: TerrainQuickLinkState) {
  return [
    state.selectedEstimatedId,
    state.selectedMeasuredId,
    state.selectedVerifiedId,
    ...getTerrainQuickLinkContextRows(state).map((row) => row.projectIndicatorId),
  ]
    .map((id) => Number(id || 0))
    .filter((id, index, items) =>
      Number.isFinite(id) && id > 0 && items.indexOf(id) === index,
    );
}

function getTerrainQuickLinkSelectedSourceLandPaymentIds(state: TerrainQuickLinkState) {
  const targetLandPaymentId = Number(state.targetLandPaymentId || 0);

  return [
    state.selectedEstimatedPayment?.landPaymentId,
    state.selectedMeasuredPayment?.landPaymentId,
    state.selectedVerifiedPayment?.landPaymentId,
  ]
    .map((id) => Number(id || 0))
    .filter((id, index, items) =>
      Number.isFinite(id) &&
      id > 0 &&
      id !== targetLandPaymentId &&
      items.indexOf(id) === index,
    );
}

function buildOptimisticReconciledLandPaymentRows({
  state,
  landPaymentId,
  realFundingId,
}: {
  state: TerrainQuickLinkState;
  landPaymentId: number;
  realFundingId: number;
}): SynthesesGroupedContractNode["landPayments"] {
  const contextRows = getTerrainQuickLinkContextRows(state);
  const finalStatus = Math.max(
    1,
    Number(state.targetLandPaymentStatus || 0),
    ...contextRows.map((row) => Number(row.status || 0)),
  );
  const recipientSource =
    contextRows.find((row) => isPositiveId(getLandPaymentRecipientId(row))) ||
    contextRows.find((row) => !!row.recipientSubcontractor) ||
    null;
  const recipientSubcontractorId = recipientSource
    ? getLandPaymentRecipientId(recipientSource)
    : null;
  const recipientSubcontractor = recipientSource?.recipientSubcontractor ?? null;
  const pjVerified =
    contextRows.find((row) => Number(row.status || 0) === 3 && row.pjVerified)?.pjVerified ||
    contextRows.find((row) => row.pjVerified)?.pjVerified ||
    null;

  const rowsByKey = new Map<string, SynthesesGroupedContractNode["landPayments"][number]>();

  contextRows.forEach((row) => {
    const rowStatus = Number(row.status || 0);
    if (![1, 2, 3].includes(rowStatus)) return;

    const projectIndicatorId = Number(row.projectIndicatorId || 0);
    const key = projectIndicatorId > 0
      ? `indicator-${projectIndicatorId}`
      : `row-${row.id}-${rowStatus}`;
    const amountEuro = Number(row.amountEuro || 0);
    if (!Number.isFinite(amountEuro) || amountEuro <= 0) return;

    rowsByKey.set(key, {
      id: projectIndicatorId > 0
        ? -projectIndicatorId
        : -Math.abs(Number(landPaymentId || 0) * 10 + rowStatus),
      projectIndicatorId: projectIndicatorId > 0 ? projectIndicatorId : null,
      landPaymentId,
      landPaymentStatus: finalStatus,
      realFundingId: Number(row.realFundingId || realFundingId || 0) > 0
        ? Number(row.realFundingId || realFundingId)
        : null,
      synthesesContractId:
        Number(row.synthesesContractId || 0) > 0 ? Number(row.synthesesContractId) : null,
      synthesesContractLineId:
        Number(row.synthesesContractLineId || row.lineId || 0) > 0
          ? Number(row.synthesesContractLineId || row.lineId)
          : null,
      lineId:
        Number(row.lineId || row.synthesesContractLineId || 0) > 0
          ? Number(row.lineId || row.synthesesContractLineId)
          : null,
      lineTitle: row.lineTitle || null,
      projectId: Number(row.projectId || 0) > 0 ? Number(row.projectId) : null,
      projectName: row.projectName || null,
      at: row.at,
      amountLocal: Number(amountEuro.toFixed(2)),
      amountEuro: Number(amountEuro.toFixed(2)),
      source: "land_payment",
      status: rowStatus,
      dateMeasure: rowStatus === 2 ? row.at : row.dateMeasure || null,
      dateVerified: rowStatus === 3 ? row.at : row.dateVerified || null,
      pjVerified,
      recipientSubcontractorId,
      recipientSubcontractor,
    });
  });

  return Array.from(rowsByKey.values()).sort((a, b) =>
    toTrimmedText(a.at).localeCompare(toTrimmedText(b.at)),
  );
}

function canSubmitTerrainQuickLink(state: TerrainQuickLinkState | null) {
  if (!state) return false;

  const targetStatus = Number(state.targetLandPaymentStatus || 0);
  if (isPositiveId(state.targetLandPaymentId)) {
    if (targetStatus === 1) {
      return isPositiveId(state.selectedMeasuredId);
    }
    if (targetStatus === 2) {
      return (
        isPositiveId(state.selectedEstimatedId) ||
        isPositiveId(state.selectedVerifiedId)
      );
    }
    if (targetStatus === 3) {
      return (
        isPositiveId(state.selectedEstimatedId) ||
        isPositiveId(state.selectedMeasuredId)
      );
    }
    return false;
  }

  return (
    isPositiveId(state.selectedEstimatedId) &&
    isPositiveId(state.selectedMeasuredId)
  );
}

function getTerrainQuickLinkSummary(state: TerrainQuickLinkState | null) {
  if (!state) return "";
  const parts = [
    isPositiveId(state.selectedEstimatedId) ||
    (state.targetLandPaymentStatus === 1 && isPositiveId(state.targetLandPaymentId))
      ? "Prévu sélectionné"
      : "Prévu manquant",
    isPositiveId(state.selectedMeasuredId) ||
    (state.targetLandPaymentStatus === 2 && isPositiveId(state.targetLandPaymentId))
      ? "Mesuré sélectionné"
      : "Mesuré manquant",
    isPositiveId(state.selectedVerifiedId) ||
    (state.targetLandPaymentStatus === 3 && isPositiveId(state.targetLandPaymentId))
      ? "Certifié sélectionné"
      : "Certifié optionnel",
  ];

  return parts.join(" • ");
}

function buildProjectYearTotals(yearGroups: ProjectYearGroup[]) {
  const lines = yearGroups.flatMap((group) => group.contracts);
  const uniqueFunders = new Set(
    lines
      .map((line) => String(line.financeurId || line.financeurName || ""))
      .filter(Boolean),
  );
  const uniqueContracts = new Set(lines.map((line) => String(line.id || "")));

  const expectedPaymentsEuro = yearGroups.reduce((sum, group) => {
    const activeYear = /^\d{4}$/.test(group.year) ? group.year : null;
    return (
      sum +
      group.contracts.reduce((groupSum, line) => {
        const targetYear = activeYear || line.year;
        return (
          groupSum +
          sumPaymentPreviews(
            buildLinePaymentsForYear(
              line.contract.expectedPayments,
              line.realFundingId,
              targetYear,
              `${line.id}-expected-total`,
              {
                synthesesContractId: line.synthesesContractId,
                synthesesContractLineId: line.lineId,
              },
            ),
          )
        );
      }, 0)
    );
  }, 0);

  const receivedPaymentsEuro = yearGroups.reduce((sum, group) => {
    const activeYear = /^\d{4}$/.test(group.year) ? group.year : null;
    return (
      sum +
      group.contracts.reduce((groupSum, line) => {
        const targetYear = activeYear || line.year;
        return (
          groupSum +
          sumPaymentPreviews(
            buildLinePaymentsForYear(
              line.contract.paidPayments,
              line.realFundingId,
              targetYear,
              `${line.id}-paid-total`,
              {
                synthesesContractId: line.synthesesContractId,
                synthesesContractLineId: line.lineId,
              },
            ),
          )
        );
      }, 0)
    );
  }, 0);

  const landPaymentsEuro = yearGroups.reduce((sum, group) => {
    const activeYear = /^\d{4}$/.test(group.year) ? group.year : null;
    return (
      sum +
      group.contracts.reduce((groupSum, line) => {
        const targetYear = activeYear || line.year;
        return (
          groupSum +
          sumPaymentPreviews(
            buildLinePaymentsForYear(
              line.contract.landPayments,
              line.realFundingId,
              targetYear,
              `${line.id}-land-total`,
              {
                synthesesContractId: line.synthesesContractId,
                synthesesContractLineId: line.lineId,
              },
            ),
          )
        );
      }, 0)
    );
  }, 0);

  return {
    totalAmountEuro: Number(
      yearGroups
        .reduce((sum, group) => sum + Number(group.totalAmountEuro || 0), 0)
        .toFixed(2),
    ),
    contractCount: uniqueContracts.size,
    funderCount: uniqueFunders.size,
    expectedPaymentsEuro: Number(expectedPaymentsEuro.toFixed(2)),
    receivedPaymentsEuro: Number(receivedPaymentsEuro.toFixed(2)),
    landPaymentsEuro: Number(landPaymentsEuro.toFixed(2)),
  };
}

function buildLineActivityYears(line: IntervenantContractLineItem) {
  const years = new Set<string>();

  if (/^\d{4}$/.test(line.year)) {
    years.add(line.year);
  }

  [
    ...buildLinePaymentsForAllYears(
      line.contract.expectedPayments,
      line.realFundingId,
      `${line.id}-activity-expected`,
      {
        synthesesContractId: line.synthesesContractId,
        synthesesContractLineId: line.lineId,
      },
    ),
    ...buildLinePaymentsForAllYears(
      line.contract.paidPayments,
      line.realFundingId,
      `${line.id}-activity-paid`,
      {
        synthesesContractId: line.synthesesContractId,
        synthesesContractLineId: line.lineId,
      },
    ),
    ...buildLinePaymentsForAllYears(
      line.contract.landPayments,
      line.realFundingId,
      `${line.id}-activity-land`,
      {
        synthesesContractId: line.synthesesContractId,
        synthesesContractLineId: line.lineId,
      },
    ),
  ].forEach((payment) => {
    const year = getYear(payment.at);
    if (year) {
      years.add(String(year));
    }
  });

  return Array.from(years).sort((a, b) => Number(a) - Number(b));
}

function lineHasActivityForYear(
  line: IntervenantContractLineItem,
  year: string,
) {
  if (!/^\d{4}$/.test(year)) {
    return line.year === year;
  }

  return buildLineActivityYears(line).includes(year);
}

function buildProjectAvailableYears(project: IntervenantProjectItem) {
  const years = new Set<string>();

  project.yearGroups.forEach((group) => {
    if (/^\d{4}$/.test(group.year)) {
      years.add(group.year);
    }

    group.contracts.forEach((line) => {
      buildLineActivityYears(line).forEach((year) => {
        if (/^\d{4}$/.test(year)) {
          years.add(year);
        }
      });
    });
  });

  return Array.from(years).sort((a, b) => Number(a) - Number(b));
}

function buildDisplayedYearGroups(
  project: IntervenantProjectItem,
  selectedYear: string,
) {
  if (!/^\d{4}$/.test(selectedYear)) return project.yearGroups;

  const contracts = project.yearGroups
    .flatMap((group) => group.contracts)
    .filter((line) => lineHasActivityForYear(line, selectedYear));

  if (!contracts.length) {
    return [];
  }

  return [
    {
      year: selectedYear,
      totalAmountEuro: Number(
        contracts
          .reduce((sum, line) => {
            return line.year === selectedYear
              ? sum + Number(line.amountEuro || 0)
              : sum;
          }, 0)
          .toFixed(2),
      ),
      contracts,
    } satisfies ProjectYearGroup,
  ];
}

function toFinitePositiveNumber(value: unknown) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : null;
}

function createDraftId(prefix: string) {
  const randomPart = Math.random().toString(36).slice(2, 8);
  return `${prefix}-${Date.now()}-${randomPart}`;
}

function normalizeAmountInput(value: string) {
  return String(value || "")
    .replace(/\s+/g, "")
    .replace(",", ".")
    .trim();
}

function parseAmountInput(value: string): number | null {
  const normalized = normalizeAmountInput(value);
  if (!normalized) return null;
  const amount = Number(normalized);
  if (!Number.isFinite(amount)) return null;
  return Number(amount.toFixed(2));
}

function getDesiredFundingOptionLabel(option: ProjectDesiredFundingOption) {
  return String(option.title || "").trim() || `Financement souhaité #${option.id}`;
}

function getDesiredFundingOptionAmountEuro(option: ProjectDesiredFundingOption) {
  const convertedAmount = Number(option.convertedAmount ?? 0);
  if (Number.isFinite(convertedAmount) && convertedAmount > 0) {
    return Number(convertedAmount.toFixed(2));
  }

  const amount = Number(option.amount || 0);
  return Number.isFinite(amount) ? Number(amount.toFixed(2)) : 0;
}

function getDesiredFundingOptionCurrencyLabel(
  option: ProjectDesiredFundingOption,
) {
  const symbol = toTrimmedText(option.currencySymbol);
  if (symbol) return symbol;

  const code = toTrimmedText(option.currencyCode).toUpperCase();
  if (code === "EUR") return "€";
  if (code) return code;

  return "€";
}

function buildAutomaticDesiredFundingAllocationMap(
  options: ProjectDesiredFundingOption[],
  targetAmountEuro: number,
) {
  const normalizedTargetAmountEuro = Number(targetAmountEuro || 0);
  if (
    !Number.isFinite(normalizedTargetAmountEuro) ||
    normalizedTargetAmountEuro <= 0
  ) {
    return new Map<number, number>();
  }

  const targetAmountCents = Math.round(normalizedTargetAmountEuro * 100);
  if (targetAmountCents <= 0) {
    return new Map<number, number>();
  }

  const entries = options
    .map((option) => ({
      desiredFundingId: Number(option.id || 0),
      amountEuro: getDesiredFundingOptionAmountEuro(option),
      amountCents: Math.round(getDesiredFundingOptionAmountEuro(option) * 100),
    }))
    .filter(
      (entry) =>
        Number.isFinite(entry.desiredFundingId) &&
        entry.desiredFundingId > 0 &&
        Number.isFinite(entry.amountEuro) &&
        entry.amountEuro > 0 &&
        entry.amountCents > 0,
    );

  if (entries.length === 0) {
    return new Map<number, number>();
  }

  const selectedTotalCents = entries.reduce(
    (sum, entry) => sum + entry.amountCents,
    0,
  );
  if (selectedTotalCents <= 0) {
    return new Map<number, number>();
  }

  if (selectedTotalCents <= targetAmountCents) {
    return new Map(
      entries.map((entry) => [
        entry.desiredFundingId,
        Number((entry.amountCents / 100).toFixed(2)),
      ]),
    );
  }

  let remainingCents = Math.min(targetAmountCents, selectedTotalCents);
  const lastIndex = entries.length - 1;
  const allocationMap = new Map<number, number>();

  entries.forEach((entry, index) => {
    if (remainingCents <= 0) {
      return;
    }

    let allocatedCents = entry.amountCents;
    if (index === lastIndex) {
      allocatedCents = Math.min(entry.amountCents, remainingCents);
    } else {
      allocatedCents = Math.round(
        (entry.amountCents / selectedTotalCents) * targetAmountCents,
      );
      allocatedCents = Math.min(entry.amountCents, remainingCents, allocatedCents);
    }

    allocatedCents = Math.max(0, allocatedCents);
    if (allocatedCents <= 0) {
      return;
    }

    allocationMap.set(
      entry.desiredFundingId,
      Number((allocatedCents / 100).toFixed(2)),
    );
    remainingCents = Math.max(0, remainingCents - allocatedCents);
  });

  return allocationMap;
}

function getYearFromIsoDate(value: string | null) {
  if (!value) return String(new Date().getFullYear());
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(new Date().getFullYear());
  return String(date.getFullYear());
}

function normalizeContractDateInput(value: string | null | undefined): string {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch?.[1]) return isoMatch[1];
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function getScrollParent(node: HTMLElement | null): HTMLElement | Window {
  if (!node || typeof window === "undefined") return window;

  let parent: HTMLElement | null = node.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    if (overflowY === "auto" || overflowY === "scroll") {
      return parent;
    }
    parent = parent.parentElement;
  }

  return window;
}

function buildSaveContractEndpoint() {
  const apiBase = String(import.meta.env.VITE_IP_API || "")
    .trim()
    .replace(/\/+$/, "");
  if (!apiBase) return "";

  return `${apiBase}/api/syntheses/contracts/save`;
}

function buildDeleteContractEndpoint() {
  const apiBase = String(import.meta.env.VITE_IP_API || "")
    .trim()
    .replace(/\/+$/, "");
  if (!apiBase) return "";

  return `${apiBase}/api/syntheses/contracts/delete`;
}

function buildReconcileTerrainEndpoint() {
  const apiBase = String(import.meta.env.VITE_IP_API || "")
    .trim()
    .replace(/\/+$/, "");
  if (!apiBase) return "";

  return `${apiBase}/api/syntheses/land-payments/reconcile`;
}

function buildMoveTerrainEndpoint() {
  const apiBase = String(import.meta.env.VITE_IP_API || "")
    .trim()
    .replace(/\/+$/, "");
  if (!apiBase) return "";

  return `${apiBase}/api/syntheses/land-payments/move`;
}

function buildSaveLandPaymentEndpoint() {
  const apiBase = String(import.meta.env.VITE_IP_API || "")
    .trim()
    .replace(/\/+$/, "");
  if (!apiBase) return "";

  return `${apiBase}/api/land-payment/save`;
}

function buildDeleteLandPaymentEndpoint() {
  const apiBase = String(import.meta.env.VITE_IP_API || "")
    .trim()
    .replace(/\/+$/, "");
  if (!apiBase) return "";

  return `${apiBase}/api/land-payment/delete`;
}

function buildProjectIndicatorEndpoint(verified = false) {
  const apiBase = String(import.meta.env.VITE_IP_API || "")
    .trim()
    .replace(/\/+$/, "");
  if (!apiBase) return "";

  return `${apiBase}/api/project/indicator${verified ? "/verify" : ""}?responseType=status`;
}

function extractSynthesesContractId(contract: SynthesesGroupedContractNode): number | null {
  const directId = Number(contract.synthesesContractId);
  if (Number.isFinite(directId) && directId > 0) return directId;

  const match = String(contract.id || "").match(/^synth-contract-(\d+)$/);
  if (!match?.[1]) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function formatEuro(value: number) {
  return `${Number(value || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

function formatDate(value: string | null) {
  if (!value) return "Date du contrat non renseignée";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date invalide";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function SectionHint({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 hover:text-slate-700",
            className,
          )}
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-[300px] rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left text-xs text-slate-600 shadow-xl"
      >
        {children}
      </TooltipContent>
    </Tooltip>
  );
}

function getSubcontractorName(item: any) {
  const firstname = toTrimmedText(item?.firstname);
  const lastname = toTrimmedText(item?.lastname);
  const fullName = toTrimmedText(`${firstname} ${lastname}`);
  if (fullName) return fullName;
  return toTrimmedText(item?.email || item?.name || "");
}

function getSubcontractorOrganisationName(item: any) {
  return toTrimmedText(item?.Organisation?.name || item?.organisation?.name || "");
}

function getFinanceurName(item: any) {
  return toTrimmedText(
    item?.structureName || item?.fullname || item?.name || item?.title || "",
  );
}

function getProjectName(item: any) {
  return toTrimmedText(item?.name || item?.title || "");
}

function getCurrentUserStructureName() {
  if (typeof window === "undefined") return "";

  try {
    const rawUser =
      localStorage.getItem("user") || sessionStorage.getItem("user");
    if (!rawUser) return "";

    const parsedUser = JSON.parse(rawUser);
    return toTrimmedText(
      parsedUser?.structureName ||
        parsedUser?.Organisation?.name ||
        parsedUser?.fullname ||
        "",
    );
  } catch {
    return "";
  }
}

function getLandPaymentRecipientId(source: any): number | null {
  const recipientId = Number(
    source?.recipientSubcontractorId ??
      source?.recipientSubcontractor?.id ??
      source?.landPayment?.recipientSubcontractorId ??
      source?.landPayment?.recipientSubcontractor?.id ??
      0,
  );

  return Number.isFinite(recipientId) && recipientId > 0 ? recipientId : null;
}

function buildSubcontractorRecipientMemberIds(
  subcontractor: SubcontractorItem | null,
) {
  if (!subcontractor) return [];

  return Array.from(
    new Set(
      (Array.isArray(subcontractor.memberIds) ? subcontractor.memberIds : [])
        .map((memberId) => Number(memberId || 0))
        .filter((memberId) => Number.isFinite(memberId) && memberId > 0),
    ),
  );
}

function filterLinePaymentsForSelectedSubcontractor(
  payments: LinePaymentPreview[],
  recipientMemberIds: number[],
  recipientOptions: IntervenantRecipientOption[],
) {
  const recipientIds = new Set(
    recipientMemberIds
      .map((memberId) => Number(memberId || 0))
      .filter((memberId) => Number.isFinite(memberId) && memberId > 0),
  );
  if (recipientIds.size === 0) return [];

  const optionsById = new Map(
    recipientOptions
      .map((option) => [Number(option.id || 0), option] as const)
      .filter(([id]) => Number.isFinite(id) && id > 0),
  );

  return payments
    .filter((payment) => {
      const recipientId = getLandPaymentRecipientId(payment);
      return recipientId !== null && recipientIds.has(recipientId);
    })
    .map((payment) => {
      const recipientId = getLandPaymentRecipientId(payment);
      const recipientOption =
        recipientId !== null ? optionsById.get(recipientId) : null;

      return recipientOption
        ? {
            ...payment,
            recipientSubcontractorId: recipientOption.id,
            recipientSubcontractor:
              payment.recipientSubcontractor || {
                id: recipientOption.id,
                name: recipientOption.label,
              },
          }
        : payment;
    });
}

function getProjectDashboardHref(projectId: number | null | undefined) {
  const normalizedId = Number(projectId || 0);
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) return null;
  return `/dashboard/project/${normalizedId}`;
}

function CustomHorizontalScroller({
  children,
  compact = false,
}: {
  children: ReactNode;
  compact?: boolean;
}) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{
    pointerId: number;
    startClientX: number;
    startScrollLeft: number;
  } | null>(null);
  const [metrics, setMetrics] = useState({
    viewportWidth: 0,
    contentWidth: 0,
    maxScrollLeft: 0,
  });
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    const viewportNode = viewportRef.current;
    const contentNode = contentRef.current;
    if (!viewportNode || !contentNode) return;

    const updateMetrics = () => {
      const viewportWidth = viewportNode.clientWidth;
      const contentWidth = contentNode.scrollWidth;
      const maxScrollLeft = Math.max(0, contentWidth - viewportWidth);

      setMetrics({
        viewportWidth,
        contentWidth,
        maxScrollLeft,
      });
      setScrollLeft((current) => Math.min(current, maxScrollLeft));
    };

    updateMetrics();

    const observer = new ResizeObserver(() => {
      updateMetrics();
    });
    observer.observe(viewportNode);
    observer.observe(contentNode);

    return () => {
      observer.disconnect();
    };
  }, [children]);

  const syncScrollLeft = (nextScrollLeft: number) => {
    const viewportNode = viewportRef.current;
    const maxScrollLeft = metrics.maxScrollLeft;
    const clampedValue = Math.max(0, Math.min(nextScrollLeft, maxScrollLeft));

    if (viewportNode) {
      viewportNode.scrollLeft = clampedValue;
    }
    setScrollLeft(clampedValue);
  };

  const handleViewportScroll = () => {
    const viewportNode = viewportRef.current;
    if (!viewportNode) return;
    setScrollLeft(viewportNode.scrollLeft);
  };

  const handleTrackPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    const trackNode = trackRef.current;
    if (!trackNode || metrics.maxScrollLeft <= 0) return;

    const bounds = trackNode.getBoundingClientRect();
    const ratio = Math.max(
      0,
      Math.min(1, (event.clientX - bounds.left) / bounds.width),
    );
    syncScrollLeft(ratio * metrics.maxScrollLeft);
  };

  const handleThumbPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (metrics.maxScrollLeft <= 0) return;
    dragStateRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startScrollLeft: scrollLeft,
    };

    const releaseDrag = () => {
      dragStateRef.current = null;
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", releaseDrag);
      window.removeEventListener("pointercancel", releaseDrag);
    };

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const state = dragStateRef.current;
      const trackNode = trackRef.current;
      if (!state || !trackNode) return;
      if (moveEvent.pointerId !== state.pointerId) return;

      const deltaX = moveEvent.clientX - state.startClientX;
      const trackWidth = trackNode.clientWidth;
      if (trackWidth <= 0) return;

      const nextScrollLeft =
        state.startScrollLeft + (deltaX / trackWidth) * metrics.maxScrollLeft;
      syncScrollLeft(nextScrollLeft);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", releaseDrag);
    window.addEventListener("pointercancel", releaseDrag);
  };

  const thumbWidthPercent =
    metrics.contentWidth > 0
      ? Math.max(
          12,
          Math.min(100, (metrics.viewportWidth / metrics.contentWidth) * 100),
        )
      : 100;
  const thumbTravelPercent = Math.max(0, 100 - thumbWidthPercent);
  const thumbLeftPercent =
    metrics.maxScrollLeft > 0
      ? (scrollLeft / metrics.maxScrollLeft) * thumbTravelPercent
      : 0;

  return (
    <div className="min-w-0 w-full max-w-full">
      <div
        ref={viewportRef}
        className={cn(
          "hide-native-scrollbar min-w-0 w-full max-w-full overflow-x-auto",
          compact ? "pb-1.5" : "pb-2",
        )}
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        onScroll={handleViewportScroll}
      >
        <div ref={contentRef} className="w-max">
          {children}
        </div>
      </div>

      {metrics.maxScrollLeft > 0 ? (
        <div className={cn(compact ? "px-0.5" : "px-1")}>
          <div
            ref={trackRef}
            className={cn(
              "relative w-full cursor-pointer rounded-full bg-slate-200/90",
              compact ? "h-2.5" : "h-3",
            )}
            onPointerDown={handleTrackPointerDown}
          >
            <div
              role="slider"
              aria-label="Défilement horizontal"
              aria-valuemin={0}
              aria-valuemax={Math.round(metrics.maxScrollLeft)}
              aria-valuenow={Math.round(scrollLeft)}
              tabIndex={0}
              onPointerDown={handleThumbPointerDown}
              className={cn(
                "absolute rounded-full bg-slate-400 shadow-sm transition-colors hover:bg-slate-500",
                compact ? "top-[2px] h-[6px]" : "top-[2px] h-[8px]",
              )}
              style={{
                left: `${thumbLeftPercent}%`,
                width: `${thumbWidthPercent}%`,
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function IntervenantTerrainMonthlyTimeline({
  yearSummary,
}: {
  yearSummary: IntervenantTerrainTimelineYearSummary | null;
}) {
  const safeYearSummary =
    yearSummary || createIntervenantTerrainTimelineYearSummary(getCurrentTimelineYearString());

  const plannedValues = buildIntervenantTerrainTimelineValues(
    safeYearSummary,
    "plannedEuro",
  );
  const realizedValues = buildIntervenantTerrainTimelineValues(
    safeYearSummary,
    "realizedEuro",
  );
  const certifiedValues = buildIntervenantTerrainTimelineValues(
    safeYearSummary,
    "certifiedEuro",
  );
  const renderCell = (
    value: number,
    monthIndex: number,
    status: 1 | 2 | 3,
  ) => {
    return (
      <FieldInput
        key={`intervenant-timeline-${status}-${safeYearSummary.year}-${monthIndex}`}
        type={status}
        defaultValue={value || 0}
        valueMultiple={String(value || 0)}
        data={{
          idFinanceur: 0,
          year: Number(safeYearSummary.year) || 0,
          month: monthIndex,
          idDevise: 0,
        }}
        compact
      />
    );
  };

  return (
    <CustomHorizontalScroller compact>
      <div className="min-w-[980px] rounded-3xl border border-[#dbead7] bg-[#F7FFF5] px-4 py-4">
        <div className="mb-2 flex items-center gap-2 w-full">
          <div
            className={cn(
              INTERVENANT_TIMELINE_LABEL_COLUMN_CLASS,
              "text-[8px] font-semibold uppercase tracking-wide text-slate-500",
            )}
          >
            Mois
          </div>
          <div className={INTERVENANT_TIMELINE_CELLS_ROW_CLASS}>
            {INTERVENANT_TIMELINE_MONTH_COLUMNS.map((month) => (
              <div
                key={`intervenant-timeline-month-${month.index}`}
                className="rounded-md border border-slate-200 bg-white px-1 py-0.5 text-center text-[8px] font-semibold uppercase tracking-wide text-slate-600"
              >
                {month.label}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-1 rounded-2xl bg-white/75 px-2 py-1.5">
          <div className="flex items-center gap-1.5 w-full">
          <div
            className={cn(
              INTERVENANT_TIMELINE_LABEL_COLUMN_CLASS,
              "text-[11px] font-medium text-[#586071]",
            )}
          >
            Versements prévus
          </div>
          <div className={INTERVENANT_TIMELINE_CELLS_ROW_CLASS}>
            {plannedValues.map((value, index) =>
              renderCell(
                value,
                index,
                1,
              ),
            )}
          </div>
        </div>
        </div>

        <div className="mb-1 rounded-2xl bg-white/75 px-2 py-1.5">
          <div className="flex items-center gap-1.5 w-full">
          <div
            className={cn(
              INTERVENANT_TIMELINE_LABEL_COLUMN_CLASS,
              "text-[11px] font-medium text-[#586071]",
            )}
          >
            Versements réalisés
          </div>
          <div className={INTERVENANT_TIMELINE_CELLS_ROW_CLASS}>
            {realizedValues.map((value, index) =>
              renderCell(
                value,
                index,
                2,
              ),
            )}
          </div>
        </div>
        </div>

        <div className="rounded-2xl bg-white/75 px-2 py-1.5">
          <div className="flex items-center gap-1.5 w-full">
          <div
            className={cn(
              INTERVENANT_TIMELINE_LABEL_COLUMN_CLASS,
              "text-[11px] font-medium text-[#586071]",
            )}
          >
            Versements certifiés
          </div>
          <div className={INTERVENANT_TIMELINE_CELLS_ROW_CLASS}>
            {certifiedValues.map((value, index) =>
              renderCell(
                value,
                index,
                3,
              ),
            )}
          </div>
        </div>
        </div>
      </div>
    </CustomHorizontalScroller>
  );
}

export function buildSubcontractors(response: any): SubcontractorItem[] {
  const rawItems = Array.isArray(response?.subcontractors)
    ? response.subcontractors
    : [];

  const grouped = new Map<string, SubcontractorItem>();

  rawItems.forEach((item: any) => {
    const userId = Number(item?.id);
    if (!Number.isFinite(userId) || userId <= 0) return;

    const organisationId = Number(item?.Organisation?.id || item?.organisation?.id || 0);
    const organisationName = getSubcontractorOrganisationName(item);
    const normalizedOrganisationName = organisationName.toLocaleLowerCase("fr").trim();
    const groupKey = normalizedOrganisationName
      ? `organisation:${normalizedOrganisationName}`
      : `user:${userId}`;
    const groupId = Number.isFinite(organisationId) && organisationId > 0
      ? organisationId
      : -userId;
    const displayName = organisationName || getSubcontractorName(item) || `Intervenant #${userId}`;
    const memberLabel = getSubcontractorName(item) || `Intervenant #${userId}`;
    const email = toTrimmedText(item?.email);
    const current = grouped.get(groupKey);

    if (!current) {
      grouped.set(groupKey, {
        id: groupId,
        name: displayName,
        email,
        fullAccess: item?.fullAccess === true,
        memberIds: [userId],
        memberOptions: [{ id: userId, label: memberLabel }],
        memberCount: 1,
      });
      return;
    }

    if (!current.memberIds.includes(userId)) {
      current.memberIds.push(userId);
      current.memberOptions.push({ id: userId, label: memberLabel });
      current.memberCount += 1;
    }
    current.fullAccess = current.fullAccess || item?.fullAccess === true;
    if (!current.email && email) {
      current.email = email;
    }
  });

  return Array.from(grouped.values()).sort((a, b) =>
    String(a?.name || "").localeCompare(String(b?.name || ""), "fr"),
  ).map((item) => ({
    ...item,
    memberOptions: item.memberOptions.sort((a, b) =>
      a.label.localeCompare(b.label, "fr"),
    ),
    memberIds: item.memberIds.sort((a, b) => a - b),
  }));
}

function mergeAccessProgramResponses(
  responses: Array<AccessProgramResponse | null | undefined>,
): AccessProgramResponse {
  const programsMap = new Map<number, {
    id: number;
    name: string;
    access: boolean;
    projectsMap: Map<number, { id: number; name: string; access: boolean }>;
  }>();

  responses.forEach((response) => {
    const programs = Array.isArray(response) ? response : [];

    programs.forEach((program) => {
      const programId = Number(program?.id || 0);
      if (!Number.isFinite(programId) || programId <= 0) return;

      const existingProgram = programsMap.get(programId) ?? {
        id: programId,
        name: toTrimmedText(program?.name) || `Programme #${programId}`,
        access: false,
        projectsMap: new Map<number, { id: number; name: string; access: boolean }>(),
      };

      existingProgram.access = existingProgram.access || program?.access === true;

      const projects = Array.isArray(program?.projects) ? program.projects : [];
      projects.forEach((project) => {
        const projectId = Number(project?.id || 0);
        if (!Number.isFinite(projectId) || projectId <= 0) return;

        const existingProject = existingProgram.projectsMap.get(projectId) ?? {
          id: projectId,
          name: toTrimmedText(project?.name) || `Projet #${projectId}`,
          access: false,
        };

        existingProject.access = existingProject.access || project?.access === true;
        existingProgram.projectsMap.set(projectId, existingProject);
      });

      programsMap.set(programId, existingProgram);
    });
  });

  return Array.from(programsMap.values())
    .map((program) => ({
      id: program.id,
      name: program.name,
      access: program.access,
      projects: Array.from(program.projectsMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name, "fr"),
      ),
    }))
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "fr"));
}

function buildFinanceurOptions(financeursResponse: any, groupedFinanceurs: SynthesesGroupedFinanceurItem[]) {
  const byId = new Map<number, FinanceurOption>();
  const rawFinanceurs = Array.isArray(financeursResponse) ? financeursResponse : [];

  rawFinanceurs.forEach((item: any) => {
    const id = Number(item?.id);
    const name = getFinanceurName(item);
    if (!Number.isFinite(id) || id <= 0 || !name) return;
    byId.set(id, { id, name });
  });

  groupedFinanceurs.forEach((item) => {
    const id = Number(item?.id);
    const name = toTrimmedText(item?.name);
    if (!Number.isFinite(id) || id <= 0 || !name || byId.has(id)) return;
    byId.set(id, { id, name });
  });

  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name, "fr"));
}

function buildAccessibleProjects(response: AccessProgramResponse | null | undefined) {
  const projectMap = new Map<number, AccessProjectItem>();
  const programs = Array.isArray(response) ? response : [];

  programs.forEach((program) => {
    const programName = toTrimmedText(program?.name);
    const projects = Array.isArray(program?.projects) ? program.projects : [];

    projects.forEach((project) => {
      const projectId = Number(project?.id);
      const hasAccess = project?.access === true;
      if (!hasAccess || !Number.isFinite(projectId) || projectId <= 0) return;
      if (projectMap.has(projectId)) return;
      projectMap.set(projectId, {
        id: projectId,
        name: getProjectName(project) || `Projet #${projectId}`,
        programName,
      });
    });
  });

  return Array.from(projectMap.values()).sort((a, b) => a.name.localeCompare(b.name, "fr"));
}

function buildProjectVisualCatalog(
  programsResponse: ProgramsCatalogResponse | null | undefined,
) {
  const catalog = new Map<number, ProjectVisualCatalogItem>();
  const programs = Array.isArray(programsResponse) ? programsResponse : [];

  programs.forEach((program) => {
    const programName = toTrimmedText(program?.name);
    const projects = Array.isArray(program?.projects) ? program.projects : [];

    projects.forEach((project) => {
      const projectId = Number(project?.id || 0);
      if (!Number.isFinite(projectId) || projectId <= 0 || catalog.has(projectId)) {
        return;
      }

      const pictureUri =
        toTrimmedText(project?.defaultImage?.uri) ||
        toTrimmedText(project?.type?.image?.uri) ||
        null;

      catalog.set(projectId, {
        id: projectId,
        name: toTrimmedText(project?.name) || `Projet ${projectId}`,
        programName,
        pictureUri,
      });
    });
  });

  return catalog;
}

function buildAccessProjectsFromVisualCatalog(
  catalog: Map<number, ProjectVisualCatalogItem>,
) {
  return Array.from(catalog.values())
    .map((project) => ({
      id: project.id,
      name: project.name,
      programName: project.programName,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, "fr"));
}

function resolveProjectPictureHref(pictureUri: string | null | undefined) {
  const mediaBase = String(import.meta.env.VITE_MEDIA_BASE || "")
    .trim()
    .replace(/\/+$/, "");
  const cleanedUri = toTrimmedText(pictureUri);
  if (mediaBase && cleanedUri) {
    return `${mediaBase}${cleanedUri}`;
  }

  return "/pexels-pixabay-235925.jpg";
}

function getContractYears(contract: SynthesesGroupedContractNode) {
  const years = new Set<string>();

  const lineRows = Array.isArray(contract.lineRows) ? contract.lineRows : [];
  lineRows.forEach((lineRow) => {
    const year = String(Number(lineRow?.year || 0) || "").trim();
    if (/^\d{4}$/.test(year)) years.add(year);
  });

  const rawYears = Array.isArray(contract.years) ? contract.years : [];
  rawYears.forEach((yearValue) => {
    const year = String(Number(yearValue || 0) || "").trim();
    if (/^\d{4}$/.test(year)) years.add(year);
  });

  if (years.size === 0) {
    years.add(getYearFromIsoDate(contract.contractDate));
  }

  return Array.from(years.values()).sort((a, b) => Number(a) - Number(b));
}

function mergeUniqueNumbers(values: Array<number | null | undefined>) {
  const unique = new Set<number>();
  values.forEach((value) => {
    const numericValue = Number(value || 0);
    if (Number.isFinite(numericValue) && numericValue > 0) {
      unique.add(numericValue);
    }
  });
  return Array.from(unique.values()).sort((a, b) => a - b);
}

function mergeContractLineRows(
  baseRows: SynthesesGroupedContractNode["lineRows"],
  nextRows: SynthesesGroupedContractNode["lineRows"],
): SynthesesGroupedContractNode["lineRows"] {
  const merged: NonNullable<SynthesesGroupedContractNode["lineRows"]> = [];
  const seen = new Set<string>();

  [...(Array.isArray(baseRows) ? baseRows : []), ...(Array.isArray(nextRows) ? nextRows : [])].forEach(
    (row) => {
      const key = [
        Number(row?.lineId || 0) || "no-line",
        Number(row?.realFundingId || 0) || "no-rf",
        Number(row?.projectId || 0) || "no-project",
        Number(row?.year || 0) || "no-year",
        Number(row?.amountEuro || 0) || "no-amount",
      ].join("|");
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(row);
    },
  );

  return merged;
}

function mergeLegacyContractsForOption(
  base: SynthesesGroupedContractNode,
  next: SynthesesGroupedContractNode,
): SynthesesGroupedContractNode {
  const mergedRealFundingIds = mergeUniqueNumbers([
    ...(Array.isArray(base.realFundingIds) ? base.realFundingIds : []),
    ...(Array.isArray(next.realFundingIds) ? next.realFundingIds : []),
    base.realFundingId,
    next.realFundingId,
  ]);
  const mergedYears = mergeUniqueNumbers([
    ...(Array.isArray(base.years) ? base.years : []),
    ...(Array.isArray(next.years) ? next.years : []),
  ]);
  const mergedLineRows = mergeContractLineRows(base.lineRows, next.lineRows);

  return {
    ...base,
    id: `legacy-option-${toTrimmedText(base.financier?.id)}-${toTrimmedText(base.contractDate || next.contractDate || "")}`,
    sourceType: "legacy_real_funding_group",
    realFundingId: mergedRealFundingIds[0] ?? base.realFundingId ?? next.realFundingId ?? null,
    realFundingIds: mergedRealFundingIds,
    years: mergedYears,
    amountEuro: Number((Number(base.amountEuro || 0) + Number(next.amountEuro || 0)).toFixed(2)),
    amountLocal: Number((Number(base.amountLocal || 0) + Number(next.amountLocal || 0)).toFixed(2)),
    legacyContractsCount:
      Number(base.legacyContractsCount || 1) + Number(next.legacyContractsCount || 1),
    lineRows: mergedLineRows,
    projectTotals: [
      ...(Array.isArray(base.projectTotals) ? base.projectTotals : []),
      ...(Array.isArray(next.projectTotals) ? next.projectTotals : []),
    ],
  };
}

function buildExistingContractOptions(
  groupedFinanceurs: SynthesesGroupedFinanceurItem[],
): ExistingContractOption[] {
  const optionsByKey = new Map<string, ExistingContractOption>();

  groupedFinanceurs.forEach((financeur, financeurIndex) => {
    financeur.contracts.forEach((contract) => {
      if (contract.isVerdeo === true) {
        return;
      }

      const sourceType = String(contract.sourceType || "").trim();
      const isLegacy =
        sourceType === "legacy_real_funding" || sourceType === "legacy_real_funding_group";
      const contractKey = isLegacy
        ? [
            "legacy",
            Number(contract.financier?.id || financeur?.id || 0) || "no-financeur",
            toTrimmedText(contract.contractDate || contract.contractDateRaw || ""),
          ].join("|")
        : Number.isFinite(Number(contract.synthesesContractId || 0)) &&
            Number(contract.synthesesContractId) > 0
          ? `synth-${Number(contract.synthesesContractId)}`
          : toTrimmedText(contract.id);
      if (!contractKey) return;

      const financeurName =
        toTrimmedText(contract.financier?.name) ||
        toTrimmedText(financeur?.name) ||
        `Financeur ${financeurIndex + 1}`;
      const existingOption = optionsByKey.get(contractKey);
      const mergedContract =
        existingOption && isLegacy
          ? mergeLegacyContractsForOption(existingOption.contract, contract)
          : contract;
      const years = getContractYears(mergedContract);
      const title = isLegacy
        ? "Contrat"
        : toTrimmedText(mergedContract.title) || "Contrat";

      optionsByKey.set(contractKey, {
        id: contractKey,
        contract: mergedContract,
        years,
        label: `${title} • ${financeurName} • ${formatDate(mergedContract.contractDate)}`,
      });
    });
  });

  return Array.from(optionsByKey.values()).sort((a, b) => {
    const dateSort = String(b.contract.contractDate || "").localeCompare(
      String(a.contract.contractDate || ""),
    );
    if (dateSort !== 0) return dateSort;
    return a.label.localeCompare(b.label, "fr");
  });
}

function buildEditableYearGroupsFromContract(contract: SynthesesGroupedContractNode): EditableYearGroupDraft[] {
  const grouped = new Map<string, EditableProjectRowDraft[]>();
  const lineRows = Array.isArray(contract.lineRows) ? contract.lineRows : [];

  lineRows.forEach((lineRow, index) => {
    const year = String(Number(lineRow?.year || 0) || "").trim() || getYearFromIsoDate(contract.contractDate);
    const projectId = Number(lineRow?.projectId);
    const projectName = String(lineRow?.projectName || "").trim();
    if (!projectName && (!Number.isFinite(projectId) || projectId <= 0)) return;
    if (!grouped.has(year)) grouped.set(year, []);

    grouped.get(year)?.push({
      id: createDraftId(`row-${index}`),
      lineId:
        Number.isFinite(Number(lineRow?.lineId)) && Number(lineRow?.lineId) > 0
          ? Number(lineRow?.lineId)
          : null,
      realFundingId:
        Number.isFinite(Number(lineRow?.realFundingId)) &&
        Number(lineRow?.realFundingId) > 0
          ? Number(lineRow?.realFundingId)
          : null,
      projectId:
        Number.isFinite(projectId) && projectId > 0 ? projectId : null,
      projectName,
      title: String(lineRow?.title || contract.title || "").trim(),
      amountEuro: Number(lineRow?.amountEuro || 0).toFixed(2),
      desiredFundingIds: [],
      desiredFundingAllocations: [],
      initialDesiredFundingIds: Array.isArray(lineRow?.desiredFundingSourceIds)
        ? lineRow.desiredFundingSourceIds
            .map((desiredFundingId: any) => Number(desiredFundingId))
            .filter(
              (desiredFundingId: number) =>
                Number.isFinite(desiredFundingId) && desiredFundingId > 0,
            )
        : [],
      desiredFundingSelectionTouched: false,
    });
  });

  if (
    grouped.size === 0 &&
    Number.isFinite(Number(contract.realFundingId || 0)) &&
    Number(contract.realFundingId) > 0 &&
    (Number.isFinite(Number(contract.project?.id || 0)) || toTrimmedText(contract.project?.name))
  ) {
    const fallbackYear = getYearFromIsoDate(contract.contractDate);
    grouped.set(fallbackYear, [
      {
        id: createDraftId("row-fallback"),
        lineId: null,
        realFundingId: Number(contract.realFundingId),
        projectId:
          Number.isFinite(Number(contract.project?.id || 0)) && Number(contract.project?.id) > 0
            ? Number(contract.project?.id)
            : null,
        projectName: toTrimmedText(contract.project?.name),
        title: toTrimmedText(contract.title) || "Contrat",
        amountEuro: Number(contract.amountEuro || 0).toFixed(2),
        desiredFundingIds: [],
        desiredFundingAllocations: [],
        initialDesiredFundingIds: [],
        desiredFundingSelectionTouched: false,
      },
    ]);
  }

  if (grouped.size === 0) {
    return [
      {
        id: createDraftId("year-group"),
        year: getYearFromIsoDate(contract.contractDate),
        projectRows: [],
      },
    ];
  }

  return Array.from(grouped.entries())
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([year, projectRows]) => ({
      id: createDraftId(`group-${year}`),
      year,
      projectRows,
    }));
}

function regroupEditableRows(rows: EditableProjectRowDraft[]) {
  const grouped = new Map<string, EditableProjectRowDraft[]>();

  rows.forEach((row) => {
    const year = String(row.id ? row.id : "");
    void year;
  });
}

function buildIntervenantProjects(
  accessibleProjects: AccessProjectItem[],
  financeurs: SynthesesGroupedFinanceurItem[],
): IntervenantProjectItem[] {
  return accessibleProjects.map((project) => {
    const lines: IntervenantContractLineItem[] = [];

    financeurs.forEach((financeur, financeurIndex) => {
      financeur.contracts.forEach((contract, contractIndex) => {
        const lineRows = Array.isArray(contract.lineRows) ? contract.lineRows : [];
        const matchingRows = lineRows.filter(
          (lineRow) => Number(lineRow?.projectId) === project.id,
        );

        if (matchingRows.length > 0) {
          matchingRows.forEach((lineRow, lineIndex) => {
            const year = String(Number(lineRow?.year || 0) || "").trim();
            const amountEuro = Number(lineRow?.amountEuro || 0);
            lines.push({
              id: `${contract.id}-line-${lineRow?.lineId ?? lineRow?.realFundingId ?? lineIndex}`,
              contract,
              synthesesContractId:
                Number.isFinite(Number(contract.synthesesContractId || 0)) &&
                Number(contract.synthesesContractId) > 0
                  ? Number(contract.synthesesContractId)
                  : null,
              lineId:
                Number.isFinite(Number(lineRow?.lineId)) && Number(lineRow?.lineId) > 0
                  ? Number(lineRow?.lineId)
                  : null,
              realFundingId:
                Number.isFinite(Number(lineRow?.realFundingId)) &&
                Number(lineRow?.realFundingId) > 0
                  ? Number(lineRow?.realFundingId)
                  : null,
              desiredFundingSourceIds: Array.isArray(lineRow?.desiredFundingSourceIds)
                ? lineRow.desiredFundingSourceIds
                    .map((desiredFundingId: any) => Number(desiredFundingId))
                    .filter(
                      (desiredFundingId: number) =>
                        Number.isFinite(desiredFundingId) && desiredFundingId > 0,
                    )
                : [],
              projectId: project.id,
              projectName: project.name,
              year: year || getYearFromIsoDate(contract.contractDate),
              amountEuro: Number.isFinite(amountEuro) ? amountEuro : 0,
              financeurId:
                Number.isFinite(Number(contract.financier?.id)) &&
                Number(contract.financier?.id) > 0
                  ? Number(contract.financier?.id)
                  : null,
              financeurName:
                toTrimmedText(contract.financier?.name) ||
                toTrimmedText(financeur?.name) ||
                `Financeur ${financeurIndex + 1}`,
              contractDate: contract.contractDate,
              dateUsesFallback: contract.dateAmountFallback === true,
              isVerdeo: contract.isVerdeo === true,
              title: toTrimmedText(contract.title) || `Contrat ${contractIndex + 1}`,
              lineTitle:
                toTrimmedText(lineRow?.title) ||
                toTrimmedText(contract.title) ||
                `Contrat ${contractIndex + 1}`,
              notes: toTrimmedText(contract.notes),
            });
          });
          return;
        }

        if (Number(contract.project?.id) !== project.id) return;

        lines.push({
          id: `${contract.id}-fallback-row`,
          contract,
          synthesesContractId:
            Number.isFinite(Number(contract.synthesesContractId || 0)) &&
            Number(contract.synthesesContractId) > 0
              ? Number(contract.synthesesContractId)
              : null,
          lineId: null,
          realFundingId:
            Number.isFinite(Number(contract.realFundingId)) && Number(contract.realFundingId) > 0
              ? Number(contract.realFundingId)
              : null,
          desiredFundingSourceIds: [],
          projectId: project.id,
          projectName: project.name,
          year: getYearFromIsoDate(contract.contractDate),
          amountEuro: Number(contract.amountEuro || 0),
          financeurId:
            Number.isFinite(Number(contract.financier?.id)) && Number(contract.financier?.id) > 0
              ? Number(contract.financier?.id)
              : null,
          financeurName:
            toTrimmedText(contract.financier?.name) ||
            toTrimmedText(financeur?.name) ||
            `Financeur ${financeurIndex + 1}`,
          contractDate: contract.contractDate,
          dateUsesFallback: contract.dateAmountFallback === true,
          isVerdeo: contract.isVerdeo === true,
          title: toTrimmedText(contract.title) || `Contrat ${contractIndex + 1}`,
          lineTitle: toTrimmedText(contract.title) || `Contrat ${contractIndex + 1}`,
          notes: toTrimmedText(contract.notes),
        });
      });
    });

    lines.sort((a, b) => {
      const yearSort = Number(a.year || 0) - Number(b.year || 0);
      if (yearSort !== 0) return yearSort;
      const dateSort = String(a.contractDate || "").localeCompare(String(b.contractDate || ""));
      if (dateSort !== 0) return dateSort;
      return a.financeurName.localeCompare(b.financeurName, "fr");
    });

    const yearGroupsMap = new Map<string, IntervenantContractLineItem[]>();
    lines.forEach((line) => {
      const year = /^\d{4}$/.test(line.year) ? line.year : "Sans date";
      if (!yearGroupsMap.has(year)) yearGroupsMap.set(year, []);
      yearGroupsMap.get(year)?.push(line);
    });

    const yearGroups = Array.from(yearGroupsMap.entries())
      .map(([year, contracts]) => ({
        year,
        totalAmountEuro: Number(
          contracts.reduce((sum, contract) => sum + Number(contract.amountEuro || 0), 0).toFixed(2),
        ),
        contracts,
      }))
      .sort((a, b) => {
        if (a.year === "Sans date") return 1;
        if (b.year === "Sans date") return -1;
        return Number(a.year) - Number(b.year);
      });

    const uniqueFunders = new Set(
      lines
        .map((line) => String(line.financeurId || line.financeurName || ""))
        .filter(Boolean),
    );
    const uniqueContracts = new Set(lines.map((line) => String(line.id || "")));

    return {
      id: project.id,
      name: project.name,
      programName: project.programName,
      totalAmountEuro: Number(
        lines.reduce((sum, line) => sum + Number(line.amountEuro || 0), 0).toFixed(2),
      ),
      contractCount: uniqueContracts.size,
      funderCount: uniqueFunders.size,
      yearGroups,
    } satisfies IntervenantProjectItem;
  });
}

function mergeIntervenantProjectsById(
  projects: IntervenantProjectItem[],
): IntervenantProjectItem[] {
  const projectMap = new Map<number, IntervenantProjectItem>();

  projects.forEach((project) => {
    const projectId = Number(project.id || 0);
    if (!Number.isFinite(projectId) || projectId <= 0) {
      return;
    }

    const existing = projectMap.get(projectId);
    if (!existing) {
      projectMap.set(projectId, {
        ...project,
        yearGroups: project.yearGroups.map((group) => ({
          ...group,
          contracts: [...group.contracts],
        })),
      });
      return;
    }

    const mergedContracts = new Map<string, IntervenantContractLineItem>();
    existing.yearGroups.forEach((group) => {
      group.contracts.forEach((contract) => {
        mergedContracts.set(String(contract.id), contract);
      });
    });
    project.yearGroups.forEach((group) => {
      group.contracts.forEach((contract) => {
        mergedContracts.set(String(contract.id), contract);
      });
    });

    const yearGroupMap = new Map<string, IntervenantContractLineItem[]>();
    Array.from(mergedContracts.values()).forEach((contract) => {
      const year = /^\d{4}$/.test(contract.year) ? contract.year : "Sans date";
      if (!yearGroupMap.has(year)) yearGroupMap.set(year, []);
      yearGroupMap.get(year)?.push(contract);
    });

    const yearGroups = Array.from(yearGroupMap.entries())
      .map(([year, contracts]) => ({
        year,
        totalAmountEuro: Number(
          contracts.reduce((sum, contract) => sum + Number(contract.amountEuro || 0), 0).toFixed(2),
        ),
        contracts: contracts.sort((a, b) => {
          const dateSort = String(a.contractDate || "").localeCompare(String(b.contractDate || ""));
          if (dateSort !== 0) return dateSort;
          return a.financeurName.localeCompare(b.financeurName, "fr");
        }),
      }))
      .sort((a, b) => {
        if (a.year === "Sans date") return 1;
        if (b.year === "Sans date") return -1;
        return Number(a.year) - Number(b.year);
      });

    const uniqueFunders = new Set(
      Array.from(mergedContracts.values())
        .map((line) => String(line.financeurId || line.financeurName || ""))
        .filter(Boolean),
    );
    const uniqueContracts = new Set(
      Array.from(mergedContracts.values()).map((line) => String(line.id || "")),
    );

    projectMap.set(projectId, {
      ...existing,
      totalAmountEuro: Number(
        Array.from(mergedContracts.values())
          .reduce((sum, line) => sum + Number(line.amountEuro || 0), 0)
          .toFixed(2),
      ),
      contractCount: uniqueContracts.size,
      funderCount: uniqueFunders.size,
      yearGroups,
    });
  });

  return Array.from(projectMap.values()).sort((a, b) => a.name.localeCompare(b.name, "fr"));
}

function buildReminders(
  projects: IntervenantProjectItem[],
  contractsReady: boolean,
): SynthesesReminderItem[] {
  const reminders: SynthesesReminderItem[] = [];

  if (!projects.length) {
    reminders.push({
      id: "no-project",
      level: "medium",
      title: "Aucun projet affecté",
      description: "Cet intervenant n'a pas encore de projet affecté.",
      targetNodeId: null,
    });
    return reminders;
  }

  if (!contractsReady) {
    reminders.push({
      id: "contracts-loading",
      level: "low",
      title: "Chargement des contrats projet",
      description: "Les montants seront affichés dès que les contrats auront été chargés.",
      targetNodeId: null,
    });
    return reminders;
  }

  projects.forEach((project) => {
    const targetNodeId = `project-${project.id}`;
    const allLines = project.yearGroups.flatMap((group) => group.contracts);

    if (!allLines.length) {
      reminders.push({
        id: `project-${project.id}-no-contract`,
        level: "medium",
        title: `${project.name}: aucun contrat`,
        description: "Aucun contrat projet n'a encore été trouvé pour ce projet.",
        targetNodeId,
      });
      return;
    }

    const missingDateCount = allLines.filter(
      (line) => !line.contractDate && !line.isVerdeo,
    ).length;
    if (missingDateCount > 0) {
      reminders.push({
        id: `project-${project.id}-missing-date`,
        level: "medium",
        title: `${project.name}: date du contrat à renseigner`,
        description: `${missingDateCount} contrat(s) sans vraie date de contrat.`,
        targetNodeId,
      });
    }

    const fallbackCount = allLines.filter((line) => line.dateUsesFallback).length;
    if (fallbackCount > 0) {
      reminders.push({
        id: `project-${project.id}-fallback-date`,
        level: "low",
        title: `${project.name}: date de référence utilisée`,
        description:
          "Une date de référence est utilisée pour figer le taux de change sur certains contrats.",
        targetNodeId,
      });
    }
  });

  return reminders.slice(0, 20);
}

function buildCalendarEvents(projects: IntervenantProjectItem[]): SynthesesCalendarEvent[] {
  const events: SynthesesCalendarEvent[] = [];

  projects.forEach((project) => {
    project.yearGroups.forEach((group) => {
      group.contracts.forEach((contract, index) => {
        if (!contract.contractDate) return;
        events.push({
          id: `${contract.id}-${index}`,
          date: contract.contractDate,
          label: `${project.name} • ${contract.financeurName}`,
          count: 1,
        });
      });
    });
  });

  return events;
}

function scrollToTargetNode(targetNodeId: string) {
  const target = Array.from(
    document.querySelectorAll<HTMLElement>("[data-contract-node-id]"),
  ).find((node) => node.dataset.contractNodeId === targetNodeId);

  if (!target) return;

  const stickyHeader = document.querySelector<HTMLElement>(
    "[data-syntheses-sticky-header='true']",
  );
  const stickyHeaderHeight = stickyHeader
    ? stickyHeader.getBoundingClientRect().height
    : 0;
  target.style.scrollMarginTop = `${stickyHeaderHeight + (stickyHeaderHeight > 0 ? 24 : 16)}px`;
  target.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

function sumYearGroupsAmount(yearGroups: EditableYearGroupDraft[]) {
  const total = yearGroups.reduce((sum, group) => {
    return (
      sum +
      group.projectRows.reduce((groupSum, row) => {
        return groupSum + Number(parseAmountInput(row.amountEuro) || 0);
      }, 0)
    );
  }, 0);

  return Number(total.toFixed(2)).toFixed(2);
}

function cloneYearGroups(yearGroups: EditableYearGroupDraft[]) {
  return yearGroups.map((group) => ({
    ...group,
    projectRows: group.projectRows.map((row) => ({ ...row })),
  }));
}

function updateOrMoveLineInYearGroups(
  yearGroups: EditableYearGroupDraft[],
  editor: EditorState,
  amountEuro: string,
  year: string,
): EditableYearGroupDraft[] {
  const nextRows = yearGroups.flatMap((group) =>
    group.projectRows.map((row) => ({ ...row, __year: group.year } as EditableProjectRowDraft & { __year: string })),
  );

  let found = false;
  const updatedRows = nextRows.map((row) => {
    const isTargetByLineId =
      editor.targetLineId !== null && row.lineId === editor.targetLineId;
    const isTargetByRealFundingId =
      editor.targetLineId === null &&
      editor.targetRealFundingId !== null &&
      row.realFundingId === editor.targetRealFundingId;
    if (!isTargetByLineId && !isTargetByRealFundingId) return row;
    found = true;
    return {
      ...row,
      __year: year,
      amountEuro,
      projectId: editor.projectId,
      projectName: editor.projectName,
      title: editor.lineTitle,
      desiredFundingIds: [...editor.desiredFundingIds],
      desiredFundingAllocations: editor.desiredFundingAllocations.map(
        (allocation) => ({ ...allocation }),
      ),
      initialDesiredFundingIds: [...editor.initialDesiredFundingIds],
      desiredFundingSelectionTouched: editor.desiredFundingSelectionTouched,
    };
  });

  if (!found) {
    throw new Error("La ligne à modifier est introuvable dans ce contrat.");
  }

  const regroupedMap = new Map<string, EditableProjectRowDraft[]>();
  updatedRows.forEach((row) => {
    const rowYear = row.__year;
    if (!regroupedMap.has(rowYear)) regroupedMap.set(rowYear, []);
    const { __year, ...cleanRow } = row;
    void __year;
    regroupedMap.get(rowYear)?.push(cleanRow);
  });

  return Array.from(regroupedMap.entries())
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([groupYear, projectRows]) => ({
      id: createDraftId(`group-${groupYear}`),
      year: groupYear,
      projectRows,
    }));
}

function removeLineFromYearGroups(
  yearGroups: EditableYearGroupDraft[],
  targetLineId: number | null,
  targetRealFundingId: number | null,
) {
  const nextGroups = yearGroups
    .map((group) => ({
      ...group,
      projectRows: group.projectRows.filter((row) => {
        if (targetLineId !== null) {
          return row.lineId !== targetLineId;
        }
        if (targetRealFundingId !== null) {
          return row.realFundingId !== targetRealFundingId;
        }
        return true;
      }),
    }))
    .filter((group) => group.projectRows.length > 0);

  return nextGroups;
}

function appendLineToYearGroups(
  yearGroups: EditableYearGroupDraft[],
  editor: EditorState,
  amountEuro: string,
  year: string,
) {
  const nextGroups = cloneYearGroups(yearGroups);
  const targetYear = /^\d{4}$/.test(year) ? year : editor.initialYear;
  const nextRow: EditableProjectRowDraft = {
    id: createDraftId("row"),
    lineId: null,
    realFundingId: null,
    projectId: editor.projectId,
    projectName: editor.projectName,
    title: editor.lineTitle,
    amountEuro,
    desiredFundingIds: [...editor.desiredFundingIds],
    desiredFundingAllocations: editor.desiredFundingAllocations.map(
      (allocation) => ({ ...allocation }),
    ),
    initialDesiredFundingIds: [...editor.initialDesiredFundingIds],
    desiredFundingSelectionTouched: editor.desiredFundingSelectionTouched,
  };

  const existingGroup = nextGroups.find((group) => group.year === targetYear);
  if (existingGroup) {
    existingGroup.projectRows.push(nextRow);
  } else {
    nextGroups.push({
      id: createDraftId(`group-${targetYear}`),
      year: targetYear,
      projectRows: [nextRow],
    });
  }

  return nextGroups.sort((a, b) => Number(a.year) - Number(b.year));
}

function mapYearGroupsToPayload(yearGroups: EditableYearGroupDraft[]) {
  return yearGroups.map((group) => ({
    year: group.year,
    projectRows: group.projectRows.map((row) => ({
      lineId: row.lineId,
      realFundingId: row.realFundingId,
      projectId: row.projectId,
      projectName: row.projectName,
      title: row.title,
      amountEuro: row.amountEuro,
      ...(row.initialDesiredFundingIds.length === 0 &&
      row.desiredFundingSelectionTouched &&
      row.desiredFundingIds.length > 0
        ? {
            desiredFundingIds: Array.from(
              new Set(
                row.desiredFundingIds.filter(
                  (desiredFundingId) =>
                    Number.isFinite(Number(desiredFundingId)) &&
                    Number(desiredFundingId) > 0,
                ),
              ),
            ),
            ...(row.desiredFundingAllocations.length > 0
              ? {
                  desiredFundingAllocations: row.desiredFundingAllocations
                    .map((allocation) => {
                      const desiredFundingId = Number(allocation?.desiredFundingId);
                      const amountEuro = parseAmountInput(
                        String(allocation?.amountEuro || ""),
                      );
                      if (
                        !Number.isFinite(desiredFundingId) ||
                        desiredFundingId <= 0 ||
                        amountEuro === null ||
                        amountEuro <= 0
                      ) {
                        return null;
                      }

                      return {
                        desiredFundingId,
                        amountEuro,
                      };
                    })
                    .filter((allocation) => !!allocation),
                }
              : {}),
          }
        : {}),
    })),
  }));
}

function extractLegacyRealFundingIds(
  yearGroups: EditableYearGroupDraft[],
  contract: SynthesesGroupedContractNode,
) {
  const ids = new Set<number>();

  yearGroups.forEach((group) => {
    group.projectRows.forEach((row) => {
      const realFundingId = Number(row.realFundingId || 0);
      if (Number.isFinite(realFundingId) && realFundingId > 0) {
        ids.add(realFundingId);
      }
    });
  });

  const contractLevelRealFundingIds = Array.isArray(contract.realFundingIds)
    ? contract.realFundingIds
    : [];
  contractLevelRealFundingIds.forEach((value) => {
    const realFundingId = Number(value || 0);
    if (Number.isFinite(realFundingId) && realFundingId > 0) {
      ids.add(realFundingId);
    }
  });

  const directRealFundingId = Number(contract.realFundingId || 0);
  if (Number.isFinite(directRealFundingId) && directRealFundingId > 0) {
    ids.add(directRealFundingId);
  }

  return Array.from(ids.values()).sort((a, b) => a - b);
}

function buildCreateEditor(project: IntervenantProjectItem, initialYear: string): EditorState {
  return {
    mode: "create",
    createSource: "new",
    projectId: project.id,
    projectName: project.name,
    initialYear,
    targetLineId: null,
    targetRealFundingId: null,
    contract: null,
    financeurId: null,
    title: "Contrat",
    lineTitle: "",
    contractDate: "",
    year: initialYear,
    amountEuro: "",
    notes: "",
    desiredFundingIds: [],
    desiredFundingAllocations: [],
    initialDesiredFundingIds: [],
    desiredFundingSelectionTouched: false,
  };
}

function buildEditEditor(line: IntervenantContractLineItem): EditorState {
  return {
    mode: "edit",
    createSource: "existing",
    projectId: line.projectId,
    projectName: line.projectName,
    initialYear: line.year,
    targetLineId: line.lineId,
    targetRealFundingId: line.realFundingId,
    contract: line.contract,
    financeurId: line.financeurId,
    title: line.title,
    lineTitle: line.lineTitle || line.title,
    contractDate: normalizeContractDateInput(line.contractDate),
    year: /^\d{4}$/.test(line.year) ? line.year : getYearFromIsoDate(line.contractDate),
    amountEuro: Number(line.amountEuro || 0).toFixed(2),
    notes: line.notes,
    desiredFundingIds: [],
    desiredFundingAllocations: [],
    initialDesiredFundingIds: Array.isArray(line.desiredFundingSourceIds)
      ? line.desiredFundingSourceIds
      : [],
    desiredFundingSelectionTouched: false,
  };
}

function applySelectedContractToCreateEditor(
  editor: EditorState,
  contract: SynthesesGroupedContractNode | null,
) {
  if (!contract) {
    return {
      ...editor,
      createSource: "new" as const,
      contract: null,
      financeurId: null,
      title: "Contrat",
      lineTitle: editor.lineTitle,
      contractDate: "",
      notes: "",
    };
  }

  const existingYears = getContractYears(contract);
  const nextYear = existingYears.includes(editor.year)
    ? editor.year
    : existingYears[0] || editor.initialYear;

  return {
    ...editor,
    createSource: "existing" as const,
    contract,
    financeurId:
      Number.isFinite(Number(contract.financier?.id || 0)) && Number(contract.financier?.id) > 0
        ? Number(contract.financier?.id)
        : null,
    title: toTrimmedText(contract.title) || "Contrat",
    lineTitle: editor.lineTitle,
    contractDate: normalizeContractDateInput(contract.contractDate),
    notes: toTrimmedText(contract.notes),
    year: nextYear,
  };
}

function StatCard({
  icon,
  label,
  value,
  loading = false,
  tone = "slate",
  subtitle,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  loading?: boolean;
  tone?: SynthesesMetricTone;
  subtitle?: string;
}) {
  const toneClasses = getSynthesesMetricToneClasses(tone);
  const safeSubtitle = toTrimmedText(subtitle);
  return (
    <div
      className={`rounded-xl border p-3 shadow-[0_1px_0_rgba(15,23,42,0.03)] ${toneClasses.card}`}
    >
      <div className="mb-2 flex items-center gap-2">
        <span
          className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ring-1 ring-black/5 ${toneClasses.iconWrap}`}
        >
          {icon}
        </span>
        <span className={`text-xs uppercase tracking-wide ${toneClasses.label}`}>
          {label}
        </span>
      </div>
      {loading ? (
        <Skeleton className="h-7 w-24" />
      ) : (
        <div className={`text-xl font-semibold ${toneClasses.value}`}>
          {value}
        </div>
      )}
      {safeSubtitle ? (
        <div className={`mt-1 text-[11px] ${toneClasses.meta}`}>
          {safeSubtitle}
        </div>
      ) : null}
    </div>
  );
}

function IntervenantListSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="rounded-xl border border-slate-200 p-3">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="mt-2 h-3 w-36" />
        </div>
      ))}
    </div>
  );
}

function ContractEditorCard({
  editorState,
  existingContractOptions,
  financeurOptions,
  desiredFundingCatalog,
  desiredFundingLoading,
  desiredFundingError,
  saving,
  error,
  onChange,
  onChangeCreateSource,
  onSelectExistingContract,
  onCancel,
  onSave,
}: {
  editorState: EditorState;
  existingContractOptions: ExistingContractOption[];
  financeurOptions: FinanceurOption[];
  desiredFundingCatalog: ProjectDesiredFundingOption[];
  desiredFundingLoading: boolean;
  desiredFundingError: string;
  saving: boolean;
  error: string | null;
  onChange: (patch: Partial<EditorState>) => void;
  onChangeCreateSource: (source: "new" | "existing") => void;
  onSelectExistingContract: (contractId: string) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const affectsWholeContract =
    editorState.mode === "edit" &&
    (editorState.contract?.lineRows?.length || 0) > 1;
  const isCreateMode = editorState.mode === "create";
  const isEditMode = editorState.mode === "edit";
  const isExistingContractCreate =
    isCreateMode && editorState.createSource === "existing";
  const existingContractYears =
    isExistingContractCreate && editorState.contract
      ? getContractYears(editorState.contract)
      : [];
  const isDesiredFundingSelectionLocked =
    editorState.initialDesiredFundingIds.length > 0;
  const selectedDesiredFundingIds = (
    editorState.desiredFundingSelectionTouched
      ? editorState.desiredFundingIds
      : editorState.initialDesiredFundingIds
  ).filter(
    (desiredFundingId) =>
      Number.isFinite(Number(desiredFundingId)) && Number(desiredFundingId) > 0,
  );
  const desiredFundingCatalogById = new Map(
    desiredFundingCatalog.map((option) => [option.id, option] as const),
  );
  const selectedDesiredFundingOptions = selectedDesiredFundingIds
    .map((desiredFundingId) => desiredFundingCatalogById.get(desiredFundingId))
    .filter((option): option is ProjectDesiredFundingOption => !!option);
  const rowAmountEuro = parseAmountInput(editorState.amountEuro) ?? 0;
  const selectedDesiredFundingTotalEuro = selectedDesiredFundingOptions.reduce(
    (sum, option) => sum + getDesiredFundingOptionAmountEuro(option),
    0,
  );
  const desiredFundingAllocationById = new Map(
    Array.isArray(editorState.desiredFundingAllocations)
      ? editorState.desiredFundingAllocations
          .map((allocation) => {
            const desiredFundingId = Number(allocation?.desiredFundingId ?? 0);
            if (
              !Number.isFinite(desiredFundingId) ||
              desiredFundingId <= 0
            ) {
              return null;
            }

            return [desiredFundingId, String(allocation?.amountEuro ?? "")] as const;
          })
          .filter(
            (entry): entry is readonly [number, string] => Array.isArray(entry),
          )
      : [],
  );
  const requiresManualDesiredFundingAllocation =
    selectedDesiredFundingTotalEuro > rowAmountEuro + 0.0001;
  const automaticDesiredFundingAllocationById =
    buildAutomaticDesiredFundingAllocationMap(
      selectedDesiredFundingOptions,
      rowAmountEuro,
    );
  const manualDesiredFundingTotalEuro = selectedDesiredFundingIds.reduce(
    (sum, desiredFundingId) => {
      const amountEuro = desiredFundingAllocationById.has(desiredFundingId)
        ? parseAmountInput(
            desiredFundingAllocationById.get(desiredFundingId) || "",
          )
        : automaticDesiredFundingAllocationById.get(desiredFundingId) ?? null;
      return sum + (amountEuro ?? 0);
    },
    0,
  );
  const manualDesiredFundingRemainingEuro = Number(
    (rowAmountEuro - manualDesiredFundingTotalEuro).toFixed(2),
  );

  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5">
            <div className="text-sm font-semibold text-slate-900">
              {editorState.mode === "create" ? "Ajouter un contrat" : "Modifier le montant"}
            </div>
            <SectionHint>
              <div className="space-y-1.5">
                <p>Ce formulaire ajoute une part de contrat sur le projet sélectionné.</p>
                <ul className="list-disc space-y-1 pl-4">
                  <li>vous pouvez créer un nouveau contrat ou ajouter ce projet dans un contrat existant ;</li>
                  <li>le montant saisi correspond uniquement à la part de ce projet ;</li>
                  <li>l'année sert à classer cette ligne dans l'affichage de suivi.</li>
                </ul>
              </div>
            </SectionHint>
          </div>
          <div className="text-xs text-slate-600">
            {editorState.projectName} • {/^\d{4}$/.test(editorState.year) ? editorState.year : editorState.initialYear}
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className={SYNTHESES_SECONDARY_BUTTON_CLASS}
        >
          <X className="h-3.5 w-3.5" />
          Annuler
        </button>
      </div>

      {affectsWholeContract && !isEditMode ? (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          Ce contrat contient plusieurs lignes. Le financeur, la date et les notes s'appliqueront a tout le contrat.
        </div>
      ) : null}

      {isEditMode ? (
        <div className="mb-4 grid gap-3 md:grid-cols-3">
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Financeur
            </div>
            <div className="mt-1 text-sm font-medium text-slate-900">
              {toTrimmedText(editorState.contract?.financier?.name) || "Non renseigné"}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Date du contrat
            </div>
            <div className="mt-1 text-sm font-medium text-slate-900">
              {formatDate(editorState.contract?.contractDate || editorState.contractDate || null)}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
              Année
            </div>
            <div className="mt-1 text-sm font-medium text-slate-900">
              {/^\d{4}$/.test(editorState.initialYear) ? editorState.initialYear : editorState.year}
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2">
        {isCreateMode ? (
          <>
            <label className="space-y-1.5 text-sm">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Ajout
              </span>
              <select
                value={editorState.createSource}
                onChange={(event) =>
                  onChangeCreateSource(
                    event.target.value === "existing" ? "existing" : "new",
                  )
                }
                className={SYNTHESES_INPUT_CLASS}
              >
                <option value="new">Nouveau contrat</option>
                <option value="existing">Contrat existant</option>
              </select>
            </label>

            {isExistingContractCreate ? (
              <label className="space-y-1.5 text-sm">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  Contrat existant
                </span>
                <select
                  value={editorState.contract?.id || ""}
                  onChange={(event) => onSelectExistingContract(event.target.value)}
                  className={SYNTHESES_INPUT_CLASS}
                >
                  <option value="">Choisir un contrat</option>
                  {existingContractOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </>
        ) : null}

        {!isEditMode ? (
          <>
            <label className="space-y-1.5 text-sm">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Financeur
              </span>
              <select
                value={editorState.financeurId ?? ""}
                onChange={(event) => {
                  const value = Number(event.target.value);
                  onChange({ financeurId: Number.isFinite(value) && value > 0 ? value : null });
                }}
                disabled={isExistingContractCreate}
                className={SYNTHESES_INPUT_CLASS}
              >
                <option value="">Choisir un financeur</option>
                {financeurOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-1.5 text-sm">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Date du contrat
              </span>
              <input
                type="date"
                value={editorState.contractDate}
                onChange={(event) => onChange({ contractDate: event.target.value })}
                disabled={isExistingContractCreate}
                className={SYNTHESES_INPUT_CLASS}
              />
            </label>

            <label className="space-y-1.5 text-sm">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Intitule
              </span>
              <input
                type="text"
                value={editorState.title}
                onChange={(event) => onChange({ title: event.target.value })}
                disabled={isExistingContractCreate}
                className={SYNTHESES_INPUT_CLASS}
                placeholder="Contrat"
              />
            </label>

            <label className="space-y-1.5 text-sm">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Annee
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={editorState.year}
                onChange={(event) => onChange({ year: event.target.value })}
                className={SYNTHESES_INPUT_CLASS}
                placeholder="2026"
              />
              {existingContractYears.length > 0 ? (
                <div className="flex flex-wrap gap-2 pt-2">
                  {existingContractYears.map((yearValue) => (
                    <button
                      key={yearValue}
                      type="button"
                      onClick={() => onChange({ year: yearValue })}
                      className={cn(
                        "rounded-full border px-2.5 py-1 text-[11px] font-medium",
                        editorState.year === yearValue
                          ? "border-emerald-300 bg-emerald-100 text-emerald-800"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
                      )}
                    >
                      {yearValue}
                    </button>
                  ))}
                </div>
              ) : null}
            </label>
          </>
        ) : null}

        <label className="space-y-1.5 text-sm md:col-span-2">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Intitulé du financement obtenu
          </span>
          <input
            type="text"
            value={editorState.lineTitle}
            onChange={(event) => onChange({ lineTitle: event.target.value })}
            className={SYNTHESES_INPUT_CLASS}
            placeholder={editorState.title || "Contrat"}
          />
        </label>

        <label className="space-y-1.5 text-sm md:col-span-2">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Montant pour ce projet (€)
          </span>
          <input
            type="text"
            value={editorState.amountEuro}
            onChange={(event) => onChange({ amountEuro: event.target.value })}
            className={SYNTHESES_INPUT_CLASS}
            placeholder="25000"
          />
        </label>

        <div className="rounded-xl border border-violet-200 bg-violet-50/70 p-3 md:col-span-2">
          <div className="flex items-start justify-between gap-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-violet-700">
              Associer aux financements souhaités
            </div>
            <SectionHint className="shrink-0">
              <div className="space-y-1.5">
                <p>Ce bloc relie la part du contrat aux financements souhaités du projet.</p>
                <ul className="list-disc space-y-1 pl-4">
                  <li>si tout tient dans le montant saisi, la répartition proposée est appliquée automatiquement ;</li>
                  <li>si le montant est insuffisant, ajustez la part à affecter à chaque financement souhaité ;</li>
                  <li>une ligne déjà reliée reste verrouillée pour conserver les associations existantes.</li>
                </ul>
              </div>
            </SectionHint>
          </div>
          <div className="mt-1 text-xs text-slate-600">
            Rattachez cette part du contrat aux financements souhaités du projet. Si le total sélectionné dépasse le montant saisi, ajustez la répartition proposée.
          </div>

          {isDesiredFundingSelectionLocked ? (
            <div className="mt-3 rounded-lg border border-violet-200 bg-white/90 px-3 py-2">
              <div className="text-xs text-slate-700">
                {selectedDesiredFundingIds.length > 0
                  ? `${selectedDesiredFundingIds.length} financement${selectedDesiredFundingIds.length > 1 ? "s" : ""} souhaité${selectedDesiredFundingIds.length > 1 ? "s sont déjà associés" : " est déjà associé"} à cette ligne de contrat.`
                  : "Des financements souhaités sont déjà associés à cette ligne de contrat."}
              </div>
              {selectedDesiredFundingOptions.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedDesiredFundingOptions.map((option) => (
                    <div
                      key={option.id}
                      className="rounded-xl border border-violet-200 bg-violet-100/80 px-3 py-2 text-left text-xs text-violet-900"
                    >
                      <div className="font-medium">
                        {getDesiredFundingOptionLabel(option)}
                      </div>
                      <div className="mt-1 text-violet-800/80">
                        {getDesiredFundingOptionAmountEuro(option).toLocaleString(
                          "fr-FR",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}{" "}
                        €
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : desiredFundingLoading ? (
            <div className="mt-3 rounded-lg border border-dashed border-violet-200 bg-white/80 px-3 py-2 text-xs text-slate-500">
              Chargement des financements souhaités...
            </div>
          ) : desiredFundingError ? (
            <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {desiredFundingError}
            </div>
          ) : desiredFundingCatalog.length === 0 ? (
            <div className="mt-3 rounded-lg border border-dashed border-violet-200 bg-white/80 px-3 py-2 text-xs text-slate-500">
              Aucun financement souhaité disponible sur ce projet.
            </div>
          ) : (
            <>
              <div className="mt-3 flex flex-wrap gap-2">
                {desiredFundingCatalog.map((option) => {
                  const isSelected = selectedDesiredFundingIds.includes(option.id);

                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={cn(
                        "min-w-[220px] rounded-xl border px-3 py-2 text-left transition",
                        isSelected
                          ? "border-violet-400 bg-violet-100 text-violet-950 shadow-sm"
                          : "border-violet-200 bg-white text-slate-700 hover:border-violet-300 hover:bg-violet-50",
                      )}
                      onClick={() => {
                        const nextIds = isSelected
                          ? editorState.desiredFundingIds.filter(
                              (desiredFundingId) => desiredFundingId !== option.id,
                            )
                          : [...editorState.desiredFundingIds, option.id];
                        const nextAllocations = isSelected
                          ? editorState.desiredFundingAllocations.filter(
                              (allocation) =>
                                allocation.desiredFundingId !== option.id,
                            )
                          : editorState.desiredFundingAllocations;

                        onChange({
                          desiredFundingIds: Array.from(new Set(nextIds)),
                          desiredFundingAllocations: nextAllocations,
                          desiredFundingSelectionTouched: true,
                        });
                      }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium">
                            {getDesiredFundingOptionLabel(option)}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            {Number(option.amount || 0).toLocaleString("fr-FR", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{" "}
                            {getDesiredFundingOptionCurrencyLabel(option)}
                          </div>
                        </div>
                        <div
                          className={cn(
                            "shrink-0 rounded-full px-2 py-1 text-[11px] font-medium",
                            isSelected
                              ? "bg-violet-600 text-white"
                              : "bg-violet-100 text-violet-800",
                          )}
                        >
                          {getDesiredFundingOptionAmountEuro(option).toLocaleString(
                            "fr-FR",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}{" "}
                          €
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {selectedDesiredFundingIds.length > 0 ? (
                <div className="mt-3 rounded-lg border border-violet-200 bg-white/90 px-3 py-2 text-xs text-slate-700">
                  {selectedDesiredFundingIds.length} financement
                  {selectedDesiredFundingIds.length > 1 ? "s" : ""} souhaité
                  {selectedDesiredFundingIds.length > 1 ? "s" : ""} sélectionné
                  {selectedDesiredFundingIds.length > 1 ? "s" : ""} •{" "}
                  {selectedDesiredFundingTotalEuro.toLocaleString("fr-FR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  €
                </div>
              ) : null}

              {requiresManualDesiredFundingAllocation ? (
                <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50/90 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-700">
                    Répartition manuelle requise
                  </div>
                  <div className="mt-1 text-xs text-slate-700">
                    Le total des financements souhaités sélectionnés ({selectedDesiredFundingTotalEuro.toLocaleString(
                      "fr-FR",
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}{" "}
                    €) dépasse le montant de cette ligne ({rowAmountEuro.toLocaleString(
                      "fr-FR",
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}{" "}
                    €). Indiquez la part à affecter à chacun.
                  </div>

                  <div className="mt-3 space-y-2">
                    {selectedDesiredFundingOptions.map((option) => {
                      const currentAllocationValue = desiredFundingAllocationById.has(
                        option.id,
                      )
                        ? desiredFundingAllocationById.get(option.id) || ""
                        : (() => {
                            const automaticAmountEuro =
                              automaticDesiredFundingAllocationById.get(option.id);
                            return Number.isFinite(automaticAmountEuro)
                              ? String(automaticAmountEuro)
                              : "";
                          })();

                      return (
                        <label
                          key={option.id}
                          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-white px-3 py-2"
                        >
                          <div className="min-w-0 text-xs text-slate-700">
                            <div className="font-medium text-slate-900">
                              {getDesiredFundingOptionLabel(option)}
                            </div>
                            <div className="mt-0.5 text-slate-500">
                              {getDesiredFundingOptionAmountEuro(option).toLocaleString(
                                "fr-FR",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                },
                              )}{" "}
                              €
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">
                              Montant à affecter
                            </span>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={currentAllocationValue}
                              onChange={(event) => {
                                const value = event.target.value;
                                onChange({
                                  desiredFundingAllocations: [
                                    ...editorState.desiredFundingAllocations.filter(
                                      (allocation) =>
                                        allocation.desiredFundingId !== option.id,
                                    ),
                                    {
                                      desiredFundingId: option.id,
                                      amountEuro: value,
                                    },
                                  ],
                                });
                              }}
                              className="w-28 rounded-md border border-amber-200 bg-white px-2.5 py-1.5 text-sm text-slate-900 outline-none ring-amber-200 focus:ring-2"
                              placeholder="0,00"
                            />
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  <div className="mt-3 text-xs text-slate-700">
                    Reste à répartir :{" "}
                    <span
                      className={
                        manualDesiredFundingRemainingEuro === 0
                          ? "font-medium text-emerald-700"
                          : "font-medium text-amber-700"
                      }
                    >
                      {manualDesiredFundingRemainingEuro.toLocaleString("fr-FR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      €
                    </span>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>

        {!isEditMode ? (
          <label className="space-y-1.5 text-sm md:col-span-2">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Notes
            </span>
            <textarea
              value={editorState.notes}
              onChange={(event) => onChange({ notes: event.target.value })}
              disabled={isExistingContractCreate}
              className="min-h-[78px] w-full resize-y rounded-md border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-900 outline-none ring-emerald-200 focus:ring-2"
              placeholder="Notes sur ce contrat"
            />
          </label>
        ) : null}
      </div>

      {isExistingContractCreate ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
          Le financeur, l'intitule, la date et les notes viennent du contrat selectionne.
          Vous ajoutez ici seulement ce projet sur une annee existante ou nouvelle.
        </div>
      ) : null}

      {error ? (
        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className={SYNTHESES_SECONDARY_BUTTON_CLASS}
        >
          Annuler
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className={cn(
            SYNTHESES_PRIMARY_BUTTON_CLASS,
            saving ? "cursor-not-allowed opacity-70" : "",
          )}
        >
          {saving ? "Enregistrement..." : "Enregistrer le contrat"}
        </button>
      </div>
    </div>
  );
}

function IntervenantTerrainMovePanel({
  terrainMoveState,
  terrainMoveError,
  movingTerrainPayment,
  activeTerrainMoveSummary,
  onCancel,
  onChangeTarget,
  onConfirm,
}: {
  terrainMoveState: TerrainMoveState | null;
  terrainMoveError: string | null;
  movingTerrainPayment: boolean;
  activeTerrainMoveSummary: Array<{
    status: number;
    label: string;
    total: number;
  }>;
  onCancel: () => void;
  onChangeTarget: (targetLineId: number | null) => void;
  onConfirm: () => void;
}) {
  if (!terrainMoveState) return null;

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="text-sm font-medium text-slate-900">
            Déplacement du versement à l&apos;intervenant
          </div>
          <p className="max-w-2xl text-xs leading-relaxed text-slate-600">
            Choisissez une autre ligne du même projet. Les statuts déjà liés se déplacent ensemble.
          </p>
        </div>
        <button
          type="button"
          className={SYNTHESES_SECONDARY_BUTTON_CLASS}
          onClick={onCancel}
          disabled={movingTerrainPayment}
        >
          Annuler
        </button>
      </div>

      {activeTerrainMoveSummary.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {activeTerrainMoveSummary.map((item) => (
            <span
              key={`intervenant-timeline-move-summary-${item.status}`}
              className="rounded-full border border-amber-200 bg-white px-2.5 py-1 text-[11px] font-medium text-amber-800"
            >
              {item.label} {formatEuro(item.total)}
            </span>
          ))}
        </div>
      ) : null}

      {terrainMoveError ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {terrainMoveError}
        </div>
      ) : null}

      <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
        <label className="space-y-1.5 text-sm">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Contrat cible
          </span>
          <select
            value={terrainMoveState.selectedTargetLineId ?? ""}
            onChange={(event) => {
              const value = Number(event.target.value);
              onChangeTarget(Number.isFinite(value) && value > 0 ? value : null);
            }}
            className={SYNTHESES_INPUT_CLASS}
          >
            <option value="">Choisir un contrat</option>
            {(terrainMoveState.targetOptions ?? []).map((option) => (
              <option key={option.lineId} value={option.lineId}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className={SYNTHESES_PRIMARY_BUTTON_CLASS}
          onClick={onConfirm}
          disabled={
            movingTerrainPayment
            || !isPositiveId(terrainMoveState.selectedTargetLineId)
          }
        >
          {movingTerrainPayment ? "Déplacement..." : "Déplacer le versement"}
        </button>
      </div>
    </div>
  );
}

function ProjectSection({
  project,
  selectedYear,
  contractsReady,
  recipientMemberIds,
  recipientOptions,
  savingTerrainTimelineCellKeys,
  terrainMoveState,
  terrainMoveError,
  movingTerrainPayment,
  terrainQuickLinkState,
  savingTerrainQuickLink,
  onCommitTerrainTimelineCell,
  onOpenTerrainCertifiedTimelineEditor,
  onStartTerrainQuickLink,
  onCancelTerrainQuickLink,
  onPickTerrainQuickLinkCell,
  onConfirmTerrainQuickLink,
  onOpenTerrainMove,
  onCancelTerrainMove,
  onChangeTerrainMoveTarget,
  onConfirmTerrainMove,
}: {
  project: IntervenantProjectItem;
  selectedYear: string;
  contractsReady: boolean;
  recipientMemberIds: number[];
  recipientOptions: IntervenantRecipientOption[];
  savingTerrainTimelineCellKeys: string[];
  terrainMoveState: TerrainMoveState | null;
  terrainMoveError: string | null;
  movingTerrainPayment: boolean;
  terrainQuickLinkState: TerrainQuickLinkState | null;
  savingTerrainQuickLink: boolean;
  onCommitTerrainTimelineCell: (
    line: IntervenantContractLineItem,
    allRows: LinePaymentPreview[],
    payload: {
      status: 1 | 2 | 3;
      year: number;
      monthIndex: number;
      amountEuro: number | null;
      payment: LinePaymentPreview | null;
      recipientSubcontractorId?: number | null;
    },
  ) => Promise<void> | void;
  onOpenTerrainCertifiedTimelineEditor: (
    line: IntervenantContractLineItem,
    allRows: LinePaymentPreview[],
    payload: {
      status: 1 | 2 | 3;
      year: number;
      monthIndex: number;
      amountEuro: number | null;
      payment: LinePaymentPreview | null;
      recipientSubcontractorId?: number | null;
    },
  ) => void;
  onStartTerrainQuickLink: (line: IntervenantContractLineItem) => void;
  onCancelTerrainQuickLink: () => void;
  onPickTerrainQuickLinkCell: (
    projectKey: string,
    payload: {
      status: 1 | 2 | 3;
      payment: LinePaymentPreview | null;
    },
    allRows: LinePaymentPreview[],
  ) => void;
  onConfirmTerrainQuickLink: () => void;
  onOpenTerrainMove: (
    line: IntervenantContractLineItem,
    payment: LinePaymentPreview,
    targetOptions: TerrainMoveTargetOption[],
  ) => void;
  onCancelTerrainMove: () => void;
  onChangeTerrainMoveTarget: (targetLineId: number | null) => void;
  onConfirmTerrainMove: () => void;
}) {
  const projectLines = useMemo(() => getIntervenantProjectLines(project), [project]);

  return (
    <div
      data-contract-node-id={`project-${project.id}`}
      className="scroll-mt-6 overflow-hidden rounded-2xl border border-emerald-100 bg-white"
    >
      <div className="border-b border-emerald-100 bg-gradient-to-r from-emerald-50/90 via-white to-white px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700/70">
              Détail du projet sélectionné
            </div>
            <div className="mt-1 text-base font-semibold text-slate-900">
              Versements à l&apos;intervenant
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Timeline des versements terrain rattachés à ce projet. Les liens ouvrent le contrat concerné dans l&apos;onglet Financeurs.
            </p>
          </div>
          <div className="rounded-full border border-emerald-100 bg-white px-2.5 py-1 text-[11px] font-medium text-emerald-800">
            {projectLines.length} contrat
            {projectLines.length > 1 ? "s" : ""} concerné
            {projectLines.length > 1 ? "s" : ""}
          </div>
        </div>
      </div>

      <div className="p-4">
        {!contractsReady ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
            <div className="space-y-2">
              <Skeleton className="h-4 w-40 rounded-full" />
              <Skeleton className="h-24 w-full rounded-xl" />
            </div>
          </div>
        ) : projectLines.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">
            Aucun contrat rattaché à ce projet pour cet intervenant.
          </div>
        ) : (
          <div className="space-y-3">
            {projectLines.map((line) => {
              const allLineLandPayments = buildLinePaymentsForAllYears(
                line.contract.landPayments,
                line.realFundingId,
                `${line.id}-terrain-editable`,
                {
                  synthesesContractId: line.synthesesContractId,
                  synthesesContractLineId: line.lineId,
                },
              ).sort((a, b) => String(a.at || "").localeCompare(String(b.at || "")));
              const lineLandPayments = filterLinePaymentsForSelectedSubcontractor(
                allLineLandPayments,
                recipientMemberIds,
                recipientOptions,
              );
              const moveTargetOptions = buildIntervenantTerrainMoveTargetOptions(project, line);
              const isTerrainMoveActive = terrainMoveState?.lineId === line.id;
              const activeTerrainMoveRows = isTerrainMoveActive
                ? lineLandPayments.filter((payment) =>
                    isTerrainMoveIncludedRow(payment, terrainMoveState),
                  )
                : [];
              const activeTerrainMoveSummary = [1, 2, 3]
                .map((status) => {
                  const rows = dedupePaymentPreviewsByMonthYear(
                    filterPaymentPreviewsByStatus(activeTerrainMoveRows, status),
                  );

                  return {
                    status,
                    label: getLandStatusLabel(status),
                    total: sumPaymentPreviews(rows),
                  };
                })
                .filter((item) => item.total > 0);
              const contractHref = buildFinanceurContractHref(line);
              const isTerrainQuickLinkActive = terrainQuickLinkState?.projectKey === line.id;
              const canMoveTimelinePayment = (payment: LinePaymentPreview) =>
                !isTerrainQuickLinkActive &&
                canMoveLandPaymentRow(payment, moveTargetOptions, lineLandPayments);

              return (
                <div
                  key={line.id}
                  className="rounded-2xl border border-emerald-100 bg-emerald-50/25 p-3"
                >
                  <ProjectLandPaymentsTimelinePanel
                    title="Versements à l'intervenant"
                    landPayments={lineLandPayments}
                    projectId={line.projectId}
                    recipientOptions={recipientOptions}
                    fallbackYear={/^\d{4}$/.test(selectedYear) ? selectedYear : line.year}
                    editable
                    disabled={recipientOptions.length === 0}
                    savingCellKeys={savingTerrainTimelineCellKeys}
                    cellKeyPrefix={line.id}
                    quickLinkMode={isTerrainQuickLinkActive}
                    quickLinkState={terrainQuickLinkState}
                    canMovePayment={canMoveTimelinePayment}
                    isMovePaymentActive={(payment) =>
                      isTerrainMoveTargetRow(payment, terrainMoveState)
                    }
                    onCommitCell={(payload) =>
                      onCommitTerrainTimelineCell(
                        line,
                        lineLandPayments,
                        payload as {
                          status: 1 | 2 | 3;
                          year: number;
                          monthIndex: number;
                          amountEuro: number | null;
                          payment: LinePaymentPreview | null;
                          recipientSubcontractorId?: number | null;
                        },
                      )
                    }
                    onOpenCertifiedCell={(payload) =>
                      onOpenTerrainCertifiedTimelineEditor(
                        line,
                        lineLandPayments,
                        payload as {
                          status: 1 | 2 | 3;
                          year: number;
                          monthIndex: number;
                          amountEuro: number | null;
                          payment: LinePaymentPreview | null;
                          recipientSubcontractorId?: number | null;
                        },
                      )
                    }
                    onPickQuickLinkCell={(payload) =>
                      onPickTerrainQuickLinkCell(
                        line.id,
                        payload as {
                          status: 1 | 2 | 3;
                          payment: LinePaymentPreview | null;
                        },
                        lineLandPayments,
                      )
                    }
                    onMovePayment={(payment) => {
                      if (isTerrainMoveTargetRow(payment, terrainMoveState)) {
                        onCancelTerrainMove();
                        return;
                      }

                      onOpenTerrainMove(line, payment, moveTargetOptions);
                    }}
                  >
                    <div className="rounded-2xl border border-slate-200 bg-white/85 px-3 py-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Contrat concerné
                          </div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                            <span className="font-medium text-slate-900">
                              {line.title || "Contrat"}
                            </span>
                            <span>·</span>
                            <span>{line.financeurName}</span>
                            <span>·</span>
                            <span>{formatEuro(line.amountEuro)}</span>
                          </div>
                        </div>
                        <a
                          href={contractHref}
                          onClick={() =>
                            storeFinanceurContractNavigationTarget(
                              buildFinanceurContractNavigationTarget(line),
                            )
                          }
                          className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-xs font-medium text-emerald-800 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-50"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          Ouvrir dans Financeurs
                        </a>
                      </div>
                    </div>

                    <div className="mt-2 rounded-2xl border border-slate-200 bg-white/85 px-3 py-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Liaison rapide
                          </div>
                          <div className="mt-0.5 text-xs text-slate-600">
                            Sélectionnez un prévu, un mesuré et si besoin un certifié directement sur la timeline.
                          </div>
                        </div>
                        <button
                          type="button"
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition",
                            isTerrainQuickLinkActive
                              ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                              : "border-verdeoblue/25 bg-verdeoblue/10 text-verdeoblue hover:bg-verdeoblue/15",
                          )}
                          onClick={() =>
                            isTerrainQuickLinkActive
                              ? onCancelTerrainQuickLink()
                              : onStartTerrainQuickLink(line)
                          }
                          disabled={lineLandPayments.length === 0}
                        >
                          {isTerrainQuickLinkActive ? (
                            <>
                              <X className="h-3.5 w-3.5" />
                              Annuler la liaison
                            </>
                          ) : (
                            <>
                              <Link2 className="h-3.5 w-3.5" />
                              Lier rapidement
                            </>
                          )}
                        </button>
                      </div>

                      {isTerrainQuickLinkActive ? (
                        <div className="mt-3 rounded-xl border border-verdeoblue/20 bg-verdeoblue/5 px-3 py-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="text-xs text-slate-700">
                              {getTerrainQuickLinkSummary(terrainQuickLinkState)}
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                className={SYNTHESES_SECONDARY_BUTTON_CLASS}
                                onClick={onCancelTerrainQuickLink}
                                disabled={savingTerrainQuickLink}
                              >
                                Annuler
                              </button>
                              <button
                                type="button"
                                className={SYNTHESES_PRIMARY_BUTTON_CLASS}
                                onClick={onConfirmTerrainQuickLink}
                                disabled={
                                  savingTerrainQuickLink ||
                                  !canSubmitTerrainQuickLink(terrainQuickLinkState)
                                }
                              >
                                {savingTerrainQuickLink ? "Liaison..." : "Valider la liaison"}
                              </button>
                            </div>
                          </div>
                          {terrainQuickLinkState?.error ? (
                            <div className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                              {terrainQuickLinkState.error}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    {isTerrainMoveActive ? (
                      <IntervenantTerrainMovePanel
                        terrainMoveState={terrainMoveState}
                        terrainMoveError={terrainMoveError}
                        movingTerrainPayment={movingTerrainPayment}
                        activeTerrainMoveSummary={activeTerrainMoveSummary}
                        onCancel={onCancelTerrainMove}
                        onChangeTarget={onChangeTerrainMoveTarget}
                        onConfirm={onConfirmTerrainMove}
                      />
                    ) : null}
                  </ProjectLandPaymentsTimelinePanel>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

}

function SynthesesIntervenantsBoard() {
  const { postMultipart } = usePostMultipart<FormData>();
  const subcontractorsQuery = useFetchDataV2<any>(
    `${import.meta.env.VITE_API_ENDPOINT}/users/subcontractors`,
    { browserCache: true, browserCacheNamespace: "syntheses" },
  );
  const financeursQuery = useFetchDataV2<any[]>(
    `${import.meta.env.VITE_API_ENDPOINT}/financers`,
    { browserCache: true, browserCacheNamespace: "syntheses" },
  );
  const groupedContractsQuery = useSynthesesGroupedContractsRead("financeurs");
  const programsCatalogEndpoint = useMemo(() => {
    const apiBase = String(import.meta.env.VITE_API_ENDPOINT || "")
      .trim()
      .replace(/\/+$/, "");
    return apiBase ? `${apiBase}/info/programs` : "";
  }, []);
  const programsCatalogQuery = useFetchDataV2<ProgramsCatalogResponse>(
    programsCatalogEndpoint,
    { browserCache: true, browserCacheNamespace: "syntheses" },
  );
  const { fetchData: fetchDesiredFundingProjectData } = useFetchDataV2<any>(
    "",
    {
      browserCache: true,
      browserCacheNamespace: "syntheses-project-catalog",
    },
  );

  const subcontractors = useMemo(
    () => buildSubcontractors(subcontractorsQuery.data),
    [subcontractorsQuery.data],
  );
  const projectVisualCatalog = useMemo(
    () => buildProjectVisualCatalog(programsCatalogQuery.data),
    [programsCatalogQuery.data],
  );
  const allCatalogProjects = useMemo(
    () => buildAccessProjectsFromVisualCatalog(projectVisualCatalog),
    [projectVisualCatalog],
  );
  const groupedFinanceurs = useMemo(
    () => (Array.isArray(groupedContractsQuery.data?.items) ? groupedContractsQuery.data.items : []),
    [groupedContractsQuery.data?.items],
  );
  const financeurOptions = useMemo(
    () => buildFinanceurOptions(financeursQuery.data, groupedFinanceurs),
    [financeursQuery.data, groupedFinanceurs],
  );
  const existingContractOptions = useMemo(
    () => buildExistingContractOptions(groupedFinanceurs),
    [groupedFinanceurs],
  );

  const [selectedSubcontractorId, setSelectedSubcontractorId] = useState<number | null>(null);
  const [editorState, setEditorState] = useState<EditorState | null>(null);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [desiredFundingCatalogByProjectId, setDesiredFundingCatalogByProjectId] =
    useState<Record<number, ProjectDesiredFundingCatalogEntry>>({});
  const [loadingDesiredFundingProjectIds, setLoadingDesiredFundingProjectIds] =
    useState<Record<number, boolean>>({});
  const [desiredFundingErrorsByProjectId, setDesiredFundingErrorsByProjectId] =
    useState<Record<number, string>>({});
  const [savingEditor, setSavingEditor] = useState(false);
  const [deletingLineId, setDeletingLineId] = useState<string | null>(null);
  const [terrainReconcileState, setTerrainReconcileState] =
    useState<TerrainReconcileState | null>(null);
  const [terrainReconcileError, setTerrainReconcileError] = useState<string | null>(null);
  const [savingTerrainReconcile, setSavingTerrainReconcile] = useState(false);
  const [terrainQuickLinkState, setTerrainQuickLinkState] =
    useState<TerrainQuickLinkState | null>(null);
  const [savingTerrainQuickLink, setSavingTerrainQuickLink] = useState(false);
  const [terrainPaymentEditorState, setTerrainPaymentEditorState] =
    useState<TerrainPaymentEditorState | null>(null);
  const [terrainPaymentEditorError, setTerrainPaymentEditorError] = useState<string | null>(null);
  const [savingTerrainPayment, setSavingTerrainPayment] = useState(false);
  const [terrainCertifiedEditorState, setTerrainCertifiedEditorState] =
    useState<TerrainCertifiedEditorState | null>(null);
  const [terrainCertifiedEditorError, setTerrainCertifiedEditorError] =
    useState<string | null>(null);
  const [savingTerrainCertified, setSavingTerrainCertified] = useState(false);
  const [deletingTerrainPaymentId, setDeletingTerrainPaymentId] = useState<number | null>(null);
  const [terrainMoveState, setTerrainMoveState] = useState<TerrainMoveState | null>(null);
  const [terrainMoveError, setTerrainMoveError] = useState<string | null>(null);
  const [movingTerrainPayment, setMovingTerrainPayment] = useState(false);
  const [savingTerrainTimelineCellKeys, setSavingTerrainTimelineCellKeys] =
    useState<string[]>([]);
  const [selectedYearBySubcontractor, setSelectedYearBySubcontractor] =
    useState<Record<string, string>>({});
  const [selectedTerrainTimelineYearByScope, setSelectedTerrainTimelineYearByScope] =
    useState<Record<string, string>>({});
  const [selectedProjectCardIdBySubcontractor, setSelectedProjectCardIdBySubcontractor] =
    useState<Record<string, number>>({});
  const [allAssignedProjectIds, setAllAssignedProjectIds] = useState<number[]>([]);
  const [unassignedProjectsLoading, setUnassignedProjectsLoading] = useState(false);
  const [unassignedProjectsError, setUnassignedProjectsError] = useState<string | null>(null);
  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);
  const [isStickyHeaderActive, setIsStickyHeaderActive] = useState(false);

  const saveContractEndpoint = useMemo(() => buildSaveContractEndpoint(), []);
  const deleteContractEndpoint = useMemo(() => buildDeleteContractEndpoint(), []);
  const reconcileTerrainEndpoint = useMemo(() => buildReconcileTerrainEndpoint(), []);
  const moveTerrainEndpoint = useMemo(() => buildMoveTerrainEndpoint(), []);
  const saveLandPaymentEndpoint = useMemo(() => buildSaveLandPaymentEndpoint(), []);
  const deleteLandPaymentEndpoint = useMemo(() => buildDeleteLandPaymentEndpoint(), []);
  const saveProjectIndicatorEndpoint = useMemo(
    () => buildProjectIndicatorEndpoint(false),
    [],
  );
  const saveProjectIndicatorVerifyEndpoint = useMemo(
    () => buildProjectIndicatorEndpoint(true),
    [],
  );
  const markTerrainTimelineCellSaving = useCallback((cellKey: string) => {
    setSavingTerrainTimelineCellKeys((current) => [...current, cellKey]);
  }, []);
  const unmarkTerrainTimelineCellSaving = useCallback((cellKey: string) => {
    setSavingTerrainTimelineCellKeys((current) => {
      const index = current.indexOf(cellKey);
      if (index < 0) return current;

      return [
        ...current.slice(0, index),
        ...current.slice(index + 1),
      ];
    });
  }, []);

  const loadDesiredFundingCatalogForProject = useCallback(
    async (projectId: number | null | undefined) => {
      const normalizedProjectId = Number(projectId || 0);
      if (!Number.isFinite(normalizedProjectId) || normalizedProjectId <= 0) {
        return;
      }

      if (
        desiredFundingCatalogByProjectId[normalizedProjectId] ||
        loadingDesiredFundingProjectIds[normalizedProjectId]
      ) {
        return;
      }

      const apiBase = String(import.meta.env.VITE_API_ENDPOINT || "")
        .trim()
        .replace(/\/+$/, "");
      if (!apiBase) return;

      setLoadingDesiredFundingProjectIds((current) => ({
        ...current,
        [normalizedProjectId]: true,
      }));
      setDesiredFundingErrorsByProjectId((current) => ({
        ...current,
        [normalizedProjectId]: "",
      }));

      try {
        const payload = await fetchDesiredFundingProjectData(
          `${apiBase}/project/${normalizedProjectId}?excludes=indicator,reporting`,
        );
        if (!payload || typeof payload !== "object") {
          throw new Error(
            "Erreur de chargement des financements souhaités",
          );
        }

        const desiredFundings = Array.isArray(payload?.desiredFunding)
          ? payload.desiredFunding
          : [];

        const normalizedCatalog: ProjectDesiredFundingCatalogEntry = {
          projectId: normalizedProjectId,
          desiredFundings: desiredFundings
            .map((item: any) => {
              const desiredFundingId = Number(item?.id || 0);
              if (!Number.isFinite(desiredFundingId) || desiredFundingId <= 0) {
                return null;
              }

              const convertedAmount = Number(item?.convertedAmount ?? 0);
              const amount = Number(item?.amount ?? 0);
              const devise = item?.devise && typeof item.devise === "object"
                ? item.devise
                : null;

              return {
                id: desiredFundingId,
                title:
                  toTrimmedText(item?.title) ||
                  `Financement souhaité #${desiredFundingId}`,
                amount: Number.isFinite(amount) ? amount : 0,
                convertedAmount:
                  Number.isFinite(convertedAmount) && convertedAmount > 0
                    ? convertedAmount
                    : null,
                currencyCode:
                  toTrimmedText(devise?.code ?? item?.currencyCode ?? "") ||
                  null,
                currencySymbol:
                  toTrimmedText(devise?.symbol ?? item?.currencySymbol ?? "") ||
                  null,
              } satisfies ProjectDesiredFundingOption;
            })
            .filter(
              (
                item,
              ): item is ProjectDesiredFundingOption => !!item && item.id > 0,
            ),
        };

        setDesiredFundingCatalogByProjectId((current) => ({
          ...current,
          [normalizedProjectId]: normalizedCatalog,
        }));
      } catch (error: unknown) {
        setDesiredFundingErrorsByProjectId((current) => ({
          ...current,
          [normalizedProjectId]:
            error instanceof Error
              ? error.message
              : "Impossible de charger les financements souhaités.",
        }));
      } finally {
        setLoadingDesiredFundingProjectIds((current) => ({
          ...current,
          [normalizedProjectId]: false,
        }));
      }
    },
    [
      desiredFundingCatalogByProjectId,
      fetchDesiredFundingProjectData,
      loadingDesiredFundingProjectIds,
    ],
  );

  useEffect(() => {
    if (!editorState?.projectId || editorState.projectId <= 0) return;
    void loadDesiredFundingCatalogForProject(editorState.projectId);
  }, [editorState?.projectId, loadDesiredFundingCatalogForProject]);

  useEffect(() => {
    if (!subcontractors.length) {
      if (subcontractorsQuery.loading || subcontractorsQuery.isFetching) {
        return;
      }
      setSelectedSubcontractorId(UNASSIGNED_SUBCONTRACTOR_ID);
      return;
    }

    setSelectedSubcontractorId((current) => {
      if (current === UNASSIGNED_SUBCONTRACTOR_ID) {
        return current;
      }
      if (current && subcontractors.some((subcontractor) => subcontractor.id === current)) {
        return current;
      }
      return subcontractors[0]?.id ?? UNASSIGNED_SUBCONTRACTOR_ID;
    });
  }, [subcontractors, subcontractorsQuery.isFetching, subcontractorsQuery.loading]);

  const isUnassignedProjectsSelected = selectedSubcontractorId === UNASSIGNED_SUBCONTRACTOR_ID;
  const selectedRealSubcontractor =
    subcontractors.find((item) => item.id === selectedSubcontractorId) ?? null;
  const selectedSubcontractor = isUnassignedProjectsSelected
    ? UNASSIGNED_SUBCONTRACTOR_ITEM
    : selectedRealSubcontractor;
  const selectedRecipientSubcontractorId = useMemo(() => {
    if (isUnassignedProjectsSelected || !selectedRealSubcontractor) return null;
    const firstMemberId = Number(selectedRealSubcontractor.memberIds[0] || 0);
    return Number.isFinite(firstMemberId) && firstMemberId > 0 ? firstMemberId : null;
  }, [isUnassignedProjectsSelected, selectedRealSubcontractor]);
  const selectedRecipientMemberIds = useMemo(
    () => buildSubcontractorRecipientMemberIds(selectedRealSubcontractor),
    [selectedRealSubcontractor],
  );
  const accessQuery = useFetchDataV2<Record<string, AccessProgramResponse>>("", {
    browserCache: true,
    browserCacheNamespace: "syntheses",
  });
  const allAccessQuery = useFetchDataV2<AccessProgramResponse>("", {
    browserCache: true,
    browserCacheNamespace: "syntheses",
  });
  const [accessData, setAccessData] = useState<AccessProgramResponse>([]);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [accessLoading, setAccessLoading] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const loadAllAssignedProjects = async () => {
      const memberIds = Array.from(
        new Set(
          subcontractors.flatMap((subcontractor) =>
            Array.isArray(subcontractor.memberIds) ? subcontractor.memberIds : [],
          ),
        ),
      ).filter((memberId) => Number.isFinite(memberId) && memberId > 0);

      if (memberIds.length === 0) {
        setAllAssignedProjectIds([]);
        setUnassignedProjectsError(null);
        setUnassignedProjectsLoading(false);
        return;
      }

      setUnassignedProjectsLoading(true);
      setUnassignedProjectsError(null);

      try {
        const responses = await Promise.all(
          memberIds.map((memberId) =>
            allAccessQuery.fetchData(
              `${import.meta.env.VITE_API_ENDPOINT}/syntheses/funded-local-subcontractor-projects/${memberId}`,
            ),
          ),
        );

        if (isCancelled) return;

        const hasError = responses.some((response) => !Array.isArray(response));
        if (hasError) {
          setUnassignedProjectsError("Impossible de charger toutes les affectations des intervenants.");
        }

        const assignedIds = buildAccessibleProjects(mergeAccessProgramResponses(responses))
          .map((project) => project.id);
        setAllAssignedProjectIds(Array.from(new Set(assignedIds)));
      } catch {
        if (!isCancelled) {
          setAllAssignedProjectIds([]);
          setUnassignedProjectsError("Impossible de charger toutes les affectations des intervenants.");
        }
      } finally {
        if (!isCancelled) {
          setUnassignedProjectsLoading(false);
        }
      }
    };

    void loadAllAssignedProjects();

    return () => {
      isCancelled = true;
    };
  }, [subcontractors]);

  useEffect(() => {
    let isCancelled = false;

    const loadAccessData = async () => {
      if (!selectedSubcontractor || selectedSubcontractor.memberIds.length === 0) {
        setAccessData([]);
        setAccessError(null);
        setAccessLoading(false);
        return;
      }

      setAccessLoading(true);
      setAccessError(null);

      const endpoints = buildUserAccessBatchEndpoints(
        import.meta.env.VITE_API_ENDPOINT,
        selectedSubcontractor.memberIds,
      );
      const payloads = await Promise.all(
        endpoints.map((endpoint) => accessQuery.fetchData(endpoint)),
      );
      const payload = mergeBatchAccessPayloads(payloads);
      const responses = readBatchAccessResponses(
        payload,
        selectedSubcontractor.memberIds,
      );

      if (isCancelled) {
        return;
      }

      const hasError = responses.some((response) => !Array.isArray(response));
      if (hasError) {
        setAccessError("Impossible de charger les projets affectés à cette organisation.");
      }

      setAccessData(mergeAccessProgramResponses(responses));
      setAccessLoading(false);
    };

    void loadAccessData();

    return () => {
      isCancelled = true;
    };
  }, [selectedSubcontractor]);

  const accessibleProjects = useMemo(
    () => buildAccessibleProjects(accessData),
    [accessData],
  );

  const assignedProjects = useMemo(
    () => mergeIntervenantProjectsById(buildIntervenantProjects(accessibleProjects, groupedFinanceurs)),
    [accessibleProjects, groupedFinanceurs],
  );
  const allAssignedProjectIdSet = useMemo(
    () => new Set(allAssignedProjectIds),
    [allAssignedProjectIds],
  );
  const unassignedAccessProjects = useMemo(
    () =>
      allCatalogProjects.filter((project) => !allAssignedProjectIdSet.has(project.id)),
    [allAssignedProjectIdSet, allCatalogProjects],
  );
  const unassignedProjects = useMemo(
    () =>
      mergeIntervenantProjectsById(buildIntervenantProjects(unassignedAccessProjects, groupedFinanceurs)),
    [groupedFinanceurs, unassignedAccessProjects],
  );
  const activeIntervenantProjects = isUnassignedProjectsSelected
    ? unassignedProjects
    : assignedProjects;
  const availableIntervenantYears = useMemo(
    () => buildIntervenantContractDateYears(activeIntervenantProjects),
    [activeIntervenantProjects],
  );
  const intervenantYearFilterOptions = useMemo(
    () => ["all", ...availableIntervenantYears.filter((year) => year !== "all")],
    [availableIntervenantYears],
  );
  const selectedIntervenantYear = useMemo(() => {
    const storageKey = String(selectedSubcontractorId || "");
    const storedYear = selectedYearBySubcontractor[storageKey];
    if (storedYear && intervenantYearFilterOptions.includes(storedYear)) {
      return storedYear;
    }

    if (isUnassignedProjectsSelected) {
      return "all";
    }

    return availableIntervenantYears[availableIntervenantYears.length - 1]
      || getCurrentTimelineYearString();
  }, [
    availableIntervenantYears,
    intervenantYearFilterOptions,
    isUnassignedProjectsSelected,
    selectedSubcontractorId,
    selectedYearBySubcontractor,
  ]);
  const visibleAssignedProjects = useMemo(
    () => filterIntervenantProjectsByContractYear(activeIntervenantProjects, selectedIntervenantYear),
    [activeIntervenantProjects, selectedIntervenantYear],
  );

  const contractsReady = !groupedContractsQuery.loading;
  const projectsCatalogReady = !programsCatalogQuery.loading && !programsCatalogQuery.isFetching;
  const accessReady = isUnassignedProjectsSelected
    ? !unassignedProjectsLoading && projectsCatalogReady
    : !accessLoading;
  const activeAccessError = isUnassignedProjectsSelected
    ? unassignedProjectsError
      || (programsCatalogQuery.error ? "Impossible de charger la liste complète des projets." : null)
    : accessError;

  const summaryTotals = useMemo(() => {
    const seenContractIds = new Set<string>();

    const reduced = visibleAssignedProjects.reduce(
      (acc, project) => {
        acc.projects += 1;

        project.yearGroups.forEach((yearGroup) => {
          yearGroup.contracts.forEach((line) => {
            const contractKey = String(line.contract?.id || "");
            if (contractKey && !seenContractIds.has(contractKey)) {
              seenContractIds.add(contractKey);
              acc.contracts += 1;
              acc.totalContractsEuro += Number(line.contract?.amountEuro || 0);
            }

            acc.terrainPaymentsEuro += sumPaymentPreviews(
              filterPaymentPreviewsByStatus(
                buildLinePaymentsForAllYears(
                  line.contract.landPayments,
                  line.realFundingId,
                  `${line.id}-summary-land`,
                  {
                    synthesesContractId: line.synthesesContractId,
                    synthesesContractLineId: line.lineId,
                  },
                ),
                2,
              ),
            );
          });
        });

        return acc;
      },
      { projects: 0, contracts: 0, totalContractsEuro: 0, terrainPaymentsEuro: 0 },
    );

    return {
      ...reduced,
      totalContractsEuro: Number(reduced.totalContractsEuro.toFixed(2)),
      terrainPaymentsEuro: Number(reduced.terrainPaymentsEuro.toFixed(2)),
      structureShareEuro: Number(
        (reduced.totalContractsEuro - reduced.terrainPaymentsEuro).toFixed(2),
      ),
    };
  }, [visibleAssignedProjects]);
  const currentUserStructureName = useMemo(() => getCurrentUserStructureName(), []);
  const structureShareLabel = currentUserStructureName
    ? `Part ${currentUserStructureName}`
    : "Part de votre structure";
  const selectedProjectCardId = useMemo(() => {
    const storageKey = String(selectedSubcontractorId || "");
    const storedId = Number(selectedProjectCardIdBySubcontractor[storageKey] || 0);
    if (storedId > 0 && visibleAssignedProjects.some((project) => project.id === storedId)) {
      return storedId;
    }

    return visibleAssignedProjects[0]?.id ?? null;
  }, [selectedProjectCardIdBySubcontractor, selectedSubcontractorId, visibleAssignedProjects]);
  const selectedAssignedProject = useMemo(
    () =>
      visibleAssignedProjects.find((project) => Number(project.id || 0) === Number(selectedProjectCardId || 0))
      ?? visibleAssignedProjects[0]
      ?? null,
    [selectedProjectCardId, visibleAssignedProjects],
  );
  const terrainTimelineYears = useMemo(
    () => buildIntervenantTerrainTimelineYears(visibleAssignedProjects),
    [visibleAssignedProjects],
  );
  const terrainTimelineAvailableYears = useMemo(() => {
    if (terrainTimelineYears.length > 0) {
      return terrainTimelineYears.map((item) => item.year);
    }

    if (/^\d{4}$/.test(selectedIntervenantYear)) {
      return [selectedIntervenantYear];
    }

    return [getCurrentTimelineYearString()];
  }, [selectedIntervenantYear, terrainTimelineYears]);
  const selectedTerrainTimelineYear = useMemo(() => {
    const scopeKey = `${String(selectedSubcontractorId || "")}:${selectedIntervenantYear}`;
    const storedYear = selectedTerrainTimelineYearByScope[scopeKey];
    if (storedYear && terrainTimelineAvailableYears.includes(storedYear)) {
      return storedYear;
    }

    return terrainTimelineAvailableYears[terrainTimelineAvailableYears.length - 1]
      || selectedIntervenantYear;
  }, [
    selectedIntervenantYear,
    selectedSubcontractorId,
    selectedTerrainTimelineYearByScope,
    terrainTimelineAvailableYears,
  ]);
  const selectedTerrainTimelineSummary = useMemo(
    () =>
      terrainTimelineYears.find((item) => item.year === selectedTerrainTimelineYear)
      ?? createIntervenantTerrainTimelineYearSummary(selectedTerrainTimelineYear),
    [selectedTerrainTimelineYear, terrainTimelineYears],
  );

  useEffect(() => {
    setEditorState(null);
    setEditorError(null);
    setTerrainReconcileState(null);
    setTerrainReconcileError(null);
    setTerrainQuickLinkState(null);
    setTerrainMoveState(null);
    setTerrainMoveError(null);
  }, [selectedSubcontractorId]);

  useEffect(() => {
    const storageKey = String(selectedSubcontractorId || "");
    const firstProjectId = visibleAssignedProjects[0]?.id ?? null;
    if (!storageKey || !firstProjectId) return;

    setSelectedProjectCardIdBySubcontractor((current) => {
      const currentValue = Number(current[storageKey] || 0);
      if (
        currentValue > 0
        && visibleAssignedProjects.some((project) => project.id === currentValue)
      ) {
        return current;
      }

      return {
        ...current,
        [storageKey]: firstProjectId,
      };
    });
  }, [selectedSubcontractorId, visibleAssignedProjects]);

  useEffect(() => {
    const headerNode = stickyHeaderRef.current;
    if (!headerNode || typeof window === "undefined") return;

    const desktopMedia = window.matchMedia("(min-width: 1280px)");
    const scrollParent = getScrollParent(headerNode);

    const updateStickyState = () => {
      if (!desktopMedia.matches) {
        setIsStickyHeaderActive(false);
        return;
      }

      const headerRect = headerNode.getBoundingClientRect();
      if (scrollParent === window) {
        setIsStickyHeaderActive(headerRect.top <= 0.5 && window.scrollY > 0);
        return;
      }

      const parentRect = scrollParent.getBoundingClientRect();
      setIsStickyHeaderActive(
        headerRect.top <= parentRect.top + 0.5 && scrollParent.scrollTop > 0,
      );
    };

    updateStickyState();
    const scrollTarget: HTMLElement | Window = scrollParent;
    scrollTarget.addEventListener("scroll", updateStickyState, {
      passive: true,
    });
    window.addEventListener("resize", updateStickyState);

    const handleMediaChange = () => updateStickyState();
    if (typeof desktopMedia.addEventListener === "function") {
      desktopMedia.addEventListener("change", handleMediaChange);
    } else if (typeof desktopMedia.addListener === "function") {
      desktopMedia.addListener(handleMediaChange);
    }

    return () => {
      scrollTarget.removeEventListener("scroll", updateStickyState);
      window.removeEventListener("resize", updateStickyState);
      if (typeof desktopMedia.removeEventListener === "function") {
        desktopMedia.removeEventListener("change", handleMediaChange);
      } else if (typeof desktopMedia.removeListener === "function") {
        desktopMedia.removeListener(handleMediaChange);
      }
    };
  }, [selectedSubcontractorId, accessReady, visibleAssignedProjects.length]);

  const refreshContracts = useCallback(async () => {
    await Promise.resolve(groupedContractsQuery.refetch());
  }, [groupedContractsQuery]);

  const handleStartCreate = useCallback((project: IntervenantProjectItem, year: string) => {
    setEditorError(null);
    setEditorState(buildCreateEditor(project, /^\d{4}$/.test(year) ? year : getYearFromIsoDate(null)));
  }, []);

  const handleStartEdit = useCallback((line: IntervenantContractLineItem) => {
    setEditorError(null);
    setEditorState(buildEditEditor(line));
  }, []);

  const handleEditorChange = useCallback((patch: Partial<EditorState>) => {
    setEditorState((current) => {
      if (!current) return current;
      return { ...current, ...patch };
    });
  }, []);

  const handleEditorCreateSourceChange = useCallback(
    (source: "new" | "existing") => {
      setEditorState((current) => {
        if (!current || current.mode !== "create") return current;

        if (source === "new") {
          return applySelectedContractToCreateEditor(current, null);
        }

        return applySelectedContractToCreateEditor(
          current,
          existingContractOptions[0]?.contract ?? null,
        );
      });
    },
    [existingContractOptions],
  );

  const handleEditorSelectExistingContract = useCallback(
    (contractId: string) => {
      setEditorState((current) => {
        if (!current || current.mode !== "create") return current;
        const selectedOption = existingContractOptions.find((option) => option.id === contractId);
        return applySelectedContractToCreateEditor(current, selectedOption?.contract ?? null);
      });
    },
    [existingContractOptions],
  );

  const handleEditorCancel = useCallback(() => {
    setEditorState(null);
    setEditorError(null);
  }, []);

  const savePayloadToApi = useCallback(
    async (payload: any) => {
      if (!saveContractEndpoint) {
        throw new Error("La sauvegarde n'est pas configurée.");
      }

      const normalizedPayload = {
        ...payload,
        yearGroups: Array.isArray(payload?.yearGroups)
          ? payload.yearGroups.map((group: any) => ({
              ...group,
              projectRows: Array.isArray(group?.projectRows)
                ? group.projectRows.map((row: any) => {
                    const desiredFundingIds = Array.isArray(row?.desiredFundingIds)
                      ? row.desiredFundingIds
                          .map((desiredFundingId: any) => Number(desiredFundingId))
                          .filter(
                            (desiredFundingId: number) =>
                              Number.isFinite(desiredFundingId) && desiredFundingId > 0,
                          )
                      : [];
                    if (desiredFundingIds.length === 0) {
                      return row;
                    }

                    const projectId = Number(row?.projectId || 0);
                    const rowAmountEuro = Number(parseAmountInput(String(row?.amountEuro || "")) || 0);
                    const desiredFundingCatalog =
                      projectId > 0
                        ? desiredFundingCatalogByProjectId[projectId]?.desiredFundings || []
                        : [];
                    const desiredFundingCatalogById = new Map(
                      desiredFundingCatalog.map((option) => [option.id, option] as const),
                    );
                    const selectedDesiredFundingOptions = desiredFundingIds
                      .map((desiredFundingId) => desiredFundingCatalogById.get(desiredFundingId))
                      .filter(
                        (
                          option,
                        ): option is ProjectDesiredFundingOption => !!option,
                      );
                    const selectedDesiredFundingTotalEuro =
                      selectedDesiredFundingOptions.reduce(
                        (sum, option) => sum + getDesiredFundingOptionAmountEuro(option),
                        0,
                      );
                    const requiresManualDesiredFundingAllocation =
                      selectedDesiredFundingTotalEuro > rowAmountEuro + 0.0001;
                    const automaticDesiredFundingAllocationById =
                      buildAutomaticDesiredFundingAllocationMap(
                        selectedDesiredFundingOptions,
                        rowAmountEuro,
                      );
                    const desiredFundingAllocationInputById = new Map(
                      Array.isArray(row?.desiredFundingAllocations)
                        ? row.desiredFundingAllocations
                            .map((allocation: any) => {
                              const desiredFundingId = Number(
                                allocation?.desiredFundingId,
                              );
                              if (
                                !Number.isFinite(desiredFundingId) ||
                                desiredFundingId <= 0
                              ) {
                                return null;
                              }

                              return [
                                desiredFundingId,
                                String(allocation?.amountEuro || ""),
                              ] as const;
                            })
                            .filter(
                              (
                                allocation,
                              ): allocation is readonly [number, string] =>
                                Array.isArray(allocation),
                            )
                        : [],
                    );
                    const desiredFundingAllocations =
                      requiresManualDesiredFundingAllocation
                        ? desiredFundingIds
                            .map((desiredFundingId) => {
                              const amountEuro = desiredFundingAllocationInputById.has(
                                desiredFundingId,
                              )
                                ? parseAmountInput(
                                    desiredFundingAllocationInputById.get(
                                      desiredFundingId,
                                    ) || "",
                                  )
                                : automaticDesiredFundingAllocationById.get(
                                    desiredFundingId,
                                  ) ?? null;

                              if (
                                !Number.isFinite(amountEuro) ||
                                amountEuro === null ||
                                amountEuro <= 0
                              ) {
                                return null;
                              }

                              return {
                                desiredFundingId,
                                amountEuro: Number(amountEuro.toFixed(2)),
                              };
                            })
                            .filter((allocation) => !!allocation)
                        : [];

                    if (requiresManualDesiredFundingAllocation) {
                      const allocatedTotalEuro = desiredFundingAllocations.reduce(
                        (sum, allocation) => sum + Number(allocation.amountEuro || 0),
                        0,
                      );
                      if (Math.abs(allocatedTotalEuro - rowAmountEuro) > 0.01) {
                        throw new Error(
                          `Le montant de la ligne "${toTrimmedText(row?.projectName) || "projet"}" est insuffisant. Ajustez la répartition manuelle des financements souhaités.`,
                        );
                      }
                    }

                    return {
                      ...row,
                      desiredFundingIds,
                      ...(desiredFundingAllocations.length > 0
                        ? { desiredFundingAllocations }
                        : {}),
                    };
                  })
                : [],
            }))
          : [],
      };

      const token = sessionManager.getToken();
      const response = await fetch(saveContractEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(normalizedPayload),
      });

      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(String(body?.message || body?.error || `HTTP ${response.status}`));
      }
      return body;
    },
    [desiredFundingCatalogByProjectId, saveContractEndpoint],
  );

  const deletePayloadToApi = useCallback(
    async (payload: any) => {
      if (!deleteContractEndpoint) {
        throw new Error("La suppression n'est pas configurée.");
      }

      const token = sessionManager.getToken();
      const response = await fetch(deleteContractEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(String(body?.message || body?.error || `HTTP ${response.status}`));
      }
      return body;
    },
    [deleteContractEndpoint],
  );

  const reconcileTerrainPayloadToApi = useCallback(
    async (payload: {
      realFundingId: number;
      landPaymentId: number | null;
      estimatedIndicatorId: number | null;
      measuredIndicatorId: number | null;
      verifiedIndicatorId: number | null;
    }) => {
      if (!reconcileTerrainEndpoint) {
        throw new Error("Le rapprochement terrain n'est pas configuré.");
      }

      const token = sessionManager.getToken();
      const response = await fetch(reconcileTerrainEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(String(body?.message || body?.error || `HTTP ${response.status}`));
      }
      return body;
    },
    [reconcileTerrainEndpoint],
  );

  const moveTerrainPayloadToApi = useCallback(
    async (payload: {
      landPaymentId: number;
      targetLineId: number;
    }) => {
      if (!moveTerrainEndpoint) {
        throw new Error("Le déplacement terrain n'est pas configuré.");
      }

      const token = sessionManager.getToken();
      const response = await fetch(moveTerrainEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(String(body?.message || body?.error || `HTTP ${response.status}`));
      }
      return body;
    },
    [moveTerrainEndpoint],
  );

  const saveTerrainPaymentToApi = useCallback(
    async (payload: {
      realfunding?: number;
      synthesesContractId?: number;
      synthesesContractLineId?: number;
      amount: number;
      expectedAt: string;
      status: number;
      dateMeasure?: string;
      dateVerified?: string;
      toEdit?: number;
      recipientSubcontractorId?: number;
    }) => {
      if (!saveLandPaymentEndpoint) {
        throw new Error("La sauvegarde du versement à l'intervenant n'est pas configurée.");
      }

      const token = sessionManager.getToken();
      const response = await fetch(saveLandPaymentEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(String(body?.message || body?.error || `HTTP ${response.status}`));
      }
      return body;
    },
    [saveLandPaymentEndpoint],
  );

  const saveTerrainIndicatorToApi = useCallback(
    async (payload: Record<string, unknown>, verified = false) => {
      const endpoint = verified
        ? saveProjectIndicatorVerifyEndpoint
        : saveProjectIndicatorEndpoint;

      if (!endpoint) {
        throw new Error("La sauvegarde via le suivi de performance ESG n'est pas configurée.");
      }

      const token = sessionManager.getToken();
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const body = await response.json().catch(() => null);
      if (!response.ok || body?.status === false) {
        throw new Error(String(body?.message || body?.error || `HTTP ${response.status}`));
      }

      return body;
    },
    [saveProjectIndicatorEndpoint, saveProjectIndicatorVerifyEndpoint],
  );

  const deleteTerrainPaymentToApi = useCallback(
    async (landPaymentId: number) => {
      if (!deleteLandPaymentEndpoint) {
        throw new Error("La suppression du versement à l'intervenant n'est pas configurée.");
      }

      const token = sessionManager.getToken();
      const response = await fetch(`${deleteLandPaymentEndpoint}/${landPaymentId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({}),
      });

      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(String(body?.message || body?.error || `HTTP ${response.status}`));
      }
      return body;
    },
    [deleteLandPaymentEndpoint],
  );

  const handleOpenTerrainPaymentCreate = useCallback(
    (line: IntervenantContractLineItem, status: number, fallbackYear: string) => {
      if (!line.realFundingId && !line.lineId) {
        setTerrainPaymentEditorError("Impossible de retrouver la ligne à l'intervenant.");
        return;
      }

      const defaultDate = getDefaultTerrainDateForYear(fallbackYear);

      setTerrainReconcileState(null);
      setTerrainReconcileError(null);
      setTerrainQuickLinkState(null);
      setTerrainMoveState(null);
      setTerrainMoveError(null);
      setTerrainCertifiedEditorState(null);
      setTerrainCertifiedEditorError(null);
      setTerrainPaymentEditorError(null);
      setTerrainPaymentEditorState({
        lineId: line.id,
        lineLabel: `${line.projectName} • ${line.financeurName}`,
        synthesesContractId: line.synthesesContractId,
        synthesesContractLineId: line.lineId,
        realFundingId: line.realFundingId,
        projectId: line.projectId,
        projectName: line.projectName,
        lineTitle: line.lineTitle,
        landPaymentId: null,
        status,
        amountEuro: "",
        expectedAt: defaultDate,
        dateMeasure: status >= 2 ? defaultDate : "",
        dateVerified: status >= 3 ? defaultDate : "",
        pjVerified: null,
        recipientSubcontractorId: selectedRecipientSubcontractorId,
      });
    },
    [selectedRecipientSubcontractorId],
  );

  const handleOpenTerrainPaymentEdit = useCallback(
    (
      line: IntervenantContractLineItem,
      payment: LinePaymentPreview,
      allRows: LinePaymentPreview[],
      fallbackYear: string,
    ) => {
      if (!line.realFundingId && !line.lineId) {
        setTerrainPaymentEditorError("Impossible de retrouver la ligne à l'intervenant.");
        return;
      }

      const draft = buildTerrainPaymentEditorDraft(allRows, payment, fallbackYear);

      setTerrainReconcileState(null);
      setTerrainReconcileError(null);
      setTerrainQuickLinkState(null);
      setTerrainMoveState(null);
      setTerrainMoveError(null);
      setTerrainCertifiedEditorState(null);
      setTerrainCertifiedEditorError(null);
      setTerrainPaymentEditorError(null);
      setTerrainPaymentEditorState({
        lineId: line.id,
        lineLabel: `${line.projectName} • ${line.financeurName}`,
        synthesesContractId: line.synthesesContractId,
        synthesesContractLineId: line.lineId,
        realFundingId: line.realFundingId,
        projectId: line.projectId,
        projectName: line.projectName,
        lineTitle: line.lineTitle,
        landPaymentId: draft.landPaymentId,
        status: draft.status,
        amountEuro: draft.amountEuro,
        expectedAt: draft.expectedAt,
        dateMeasure: draft.dateMeasure,
        dateVerified: draft.dateVerified,
        pjVerified: draft.pjVerified,
        recipientSubcontractorId:
          draft.recipientSubcontractorId ?? selectedRecipientSubcontractorId,
      });
    },
    [selectedRecipientSubcontractorId],
  );

  const handleChangeTerrainPaymentEditor = useCallback(
    (patch: Partial<TerrainPaymentEditorState>) => {
      setTerrainPaymentEditorError(null);
      setTerrainPaymentEditorState((current) => {
        if (!current) return current;
        return { ...current, ...patch };
      });
    },
    [],
  );

  const handleUploadTerrainPaymentProof = useCallback(
    async (file: File) => {
      const formData = new FormData();
      formData.append("type", "12");
      formData.append("subType", "20");
      formData.append("files[]", file);

      const medias = await postMultipart({
        url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
        data: formData,
      });

      if (!Array.isArray(medias) || !medias[0]?.id) {
        throw new Error(String(medias?.message || "Erreur lors de l'upload du justificatif."));
      }

      setTerrainPaymentEditorState((current) => {
        if (!current) return current;
        return {
          ...current,
          pjVerified: medias[0],
        };
      });
    },
    [postMultipart],
  );

  const handleCancelTerrainPaymentEditor = useCallback(() => {
    setTerrainPaymentEditorState(null);
    setTerrainPaymentEditorError(null);
  }, []);

  const handleOpenTerrainCertifiedTimelineEditor = useCallback(
    (
      line: IntervenantContractLineItem,
      allRows: LinePaymentPreview[],
      payload: {
        status: 1 | 2 | 3;
        year: number;
        monthIndex: number;
        amountEuro: number | null;
        payment: LinePaymentPreview | null;
        recipientSubcontractorId?: number | null;
      },
    ) => {
      const realFundingId = Number(line.realFundingId || 0);
      const synthesesContractId = Number(line.synthesesContractId || 0);
      const synthesesContractLineId = Number(line.lineId || 0);
      const projectId = Number(line.projectId || payload.payment?.projectId || 0);
      const landPaymentId = Number(payload.payment?.landPaymentId || 0) > 0
        ? Number(payload.payment?.landPaymentId)
        : null;
      const currentCertifiedIndicatorId = Number(payload.payment?.projectIndicatorId || 0) > 0
        ? Number(payload.payment?.projectIndicatorId)
        : null;
      const relatedRows = landPaymentId
        ? allRows.filter(
            (paymentRow) => Number(paymentRow.landPaymentId || 0) === landPaymentId,
          )
        : [];
      const certifiedRow = relatedRows.find((paymentRow) => Number(paymentRow.status || 0) === 3)
        ?? payload.payment
        ?? null;
      const payloadRecipientSubcontractorId = isPositiveId(payload.recipientSubcontractorId)
        ? Number(payload.recipientSubcontractorId)
        : null;
      const initialRecipientSubcontractorId =
        getLandPaymentRecipientId(certifiedRow) ??
        getLandPaymentRecipientId(payload.payment) ??
        payloadRecipientSubcontractorId ??
        selectedRecipientSubcontractorId;
      const scopedRows = initialRecipientSubcontractorId
        ? allRows.filter(
            (paymentRow) =>
              getLandPaymentRecipientId(paymentRow) === initialRecipientSubcontractorId,
          )
        : allRows;

      if (
        (!Number.isFinite(realFundingId) || realFundingId <= 0) &&
        (!Number.isFinite(synthesesContractLineId) || synthesesContractLineId <= 0)
      ) {
        setTerrainPaymentEditorError("La ligne à l'intervenant est introuvable.");
        return;
      }
      if (!Number.isFinite(projectId) || projectId <= 0) {
        setTerrainPaymentEditorError("Le projet du versement à l'intervenant est introuvable.");
        return;
      }

      setTerrainPaymentEditorState(null);
      setTerrainPaymentEditorError(null);
      setTerrainReconcileState(null);
      setTerrainReconcileError(null);
      setTerrainQuickLinkState(null);
      setTerrainMoveState(null);
      setTerrainMoveError(null);
      setTerrainCertifiedEditorError(null);
      setTerrainCertifiedEditorState({
        lineId: line.id,
        lineLabel: `${line.projectName} • ${line.financeurName}`,
        contractNodeId: line.contract.id,
        synthesesContractId,
        synthesesContractLineId,
        realFundingId,
        projectId,
        landPaymentId,
        year: Number(payload.year),
        monthIndex: payload.monthIndex,
        amountEuro: formatTerrainAmountInput(certifiedRow?.amountEuro ?? payload.amountEuro ?? 0),
        selectedLineId: line.id,
        targetLineOptions: buildTerrainCertifiedLineOptions(line),
        pjVerified: certifiedRow?.pjVerified ?? payload.payment?.pjVerified ?? null,
        estimatedIndicatorId: findTerrainLinkedIndicatorId(scopedRows, 1, landPaymentId),
        measuredIndicatorId: findTerrainLinkedIndicatorId(scopedRows, 2, landPaymentId),
        estimatedOptions: buildTerrainCertifiedIndicatorOptions(
          scopedRows,
          1,
          landPaymentId,
          currentCertifiedIndicatorId,
        ),
        measuredOptions: buildTerrainCertifiedIndicatorOptions(
          scopedRows,
          2,
          landPaymentId,
          currentCertifiedIndicatorId,
        ),
        recipientSubcontractorId: initialRecipientSubcontractorId,
        allRows: scopedRows,
      });
    },
    [selectedRecipientSubcontractorId],
  );

  const handleChangeTerrainCertifiedEditor = useCallback(
    (patch: Partial<TerrainCertifiedEditorState>) => {
      setTerrainCertifiedEditorError(null);
      setTerrainCertifiedEditorState((current) => {
        if (!current) return current;
        return { ...current, ...patch };
      });
    },
    [],
  );

  const handleUploadTerrainCertifiedProof = useCallback(
    async (file: File) => {
      const formData = new FormData();
      formData.append("type", "12");
      formData.append("subType", "20");
      formData.append("files[]", file);

      const medias = await postMultipart({
        url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
        data: formData,
      });

      if (!Array.isArray(medias) || !medias[0]?.id) {
        throw new Error(String(medias?.message || "Erreur lors de l'upload du justificatif."));
      }

      setTerrainCertifiedEditorState((current) => {
        if (!current) return current;
        return {
          ...current,
          pjVerified: medias[0],
        };
      });
    },
    [postMultipart],
  );

  const handleCancelTerrainCertifiedEditor = useCallback(() => {
    setTerrainCertifiedEditorState(null);
    setTerrainCertifiedEditorError(null);
  }, []);

  const handleConfirmTerrainCertifiedEditor = useCallback(() => {
    if (!terrainCertifiedEditorState) return;

    const selectedTargetLine =
      terrainCertifiedEditorState.targetLineOptions.find(
        (option) => option.lineId === terrainCertifiedEditorState.selectedLineId,
      ) ||
      terrainCertifiedEditorState.targetLineOptions[0] ||
      null;
    const realFundingId = Number(
      selectedTargetLine?.realFundingId ?? terrainCertifiedEditorState.realFundingId ?? 0,
    );
    const synthesesContractId = Number(
      selectedTargetLine?.synthesesContractId ??
      terrainCertifiedEditorState.synthesesContractId ??
      0,
    );
    const synthesesContractLineId = Number(
      selectedTargetLine?.synthesesContractLineId ??
      terrainCertifiedEditorState.synthesesContractLineId ??
      0,
    );
    const projectId = Number(terrainCertifiedEditorState.projectId || 0);
    const isSameTargetLine =
      !!selectedTargetLine &&
      selectedTargetLine.lineId === terrainCertifiedEditorState.lineId;
    const landPaymentId = isSameTargetLine
      ? Number(terrainCertifiedEditorState.landPaymentId || 0)
      : 0;
    const normalizedAmount = String(terrainCertifiedEditorState.amountEuro || "")
      .replace(/\s/g, "")
      .replace(",", ".")
      .trim();
    const shouldDeleteValue = normalizedAmount === "" || normalizedAmount === "-";
    const amount = shouldDeleteValue ? null : Number(normalizedAmount);
    const recipientSubcontractorId = isPositiveId(
      terrainCertifiedEditorState.recipientSubcontractorId,
    )
      ? Number(terrainCertifiedEditorState.recipientSubcontractorId)
      : null;
    const certifiedSavingCellKey = buildIntervenantTerrainTimelineCellKey(
      terrainCertifiedEditorState.lineId,
      recipientSubcontractorId,
      3,
      terrainCertifiedEditorState.year,
      terrainCertifiedEditorState.monthIndex,
    );

    if (
      (!Number.isFinite(realFundingId) || realFundingId <= 0) &&
      (!Number.isFinite(synthesesContractLineId) || synthesesContractLineId <= 0)
    ) {
      setTerrainCertifiedEditorError("Choisissez le poste de dépenses à lier au certifié.");
      return;
    }
    if (!Number.isFinite(projectId) || projectId <= 0) {
      setTerrainCertifiedEditorError("Le projet du versement à l'intervenant est introuvable.");
      return;
    }
    if (!shouldDeleteValue && (!Number.isFinite(Number(amount)) || Number(amount) <= 0)) {
      setTerrainCertifiedEditorError("Renseignez un montant strictement positif.");
      return;
    }
    if (!shouldDeleteValue && !isPositiveId(terrainCertifiedEditorState.pjVerified?.id)) {
      setTerrainCertifiedEditorError("Ajoutez la pièce jointe de vérification.");
      return;
    }
    if (!shouldDeleteValue && !recipientSubcontractorId) {
      setTerrainCertifiedEditorError(
        "Impossible de rattacher ce versement à l'intervenant sélectionné.",
      );
      return;
    }
    if (
      !shouldDeleteValue &&
      (!isPositiveId(terrainCertifiedEditorState.estimatedIndicatorId) ||
        !isPositiveId(terrainCertifiedEditorState.measuredIndicatorId))
    ) {
      setTerrainCertifiedEditorError(
        "Veuillez d'abord renseigner puis sélectionner un prévu et un réalisé avant d'enregistrer le certifié.",
      );
      return;
    }

    const certifiedIsoDate = `${Number(terrainCertifiedEditorState.year)}-${String(
      terrainCertifiedEditorState.monthIndex + 1,
    ).padStart(2, "0")}-01`;
    const certifiedAmountEuro = Number(Number(amount || 0).toFixed(2));
    const optimisticRecipient = recipientSubcontractorId
      ? {
          id: recipientSubcontractorId,
          name: selectedRealSubcontractor?.name || `Intervenant #${recipientSubcontractorId}`,
        }
      : null;
    const contractMatcher = {
      contractNodeId: terrainCertifiedEditorState.contractNodeId,
      synthesesContractId,
    };
    const optimisticLandPaymentId = landPaymentId > 0 ? landPaymentId : -Date.now();
    const selectedEstimatedIndicatorId = Number(
      terrainCertifiedEditorState.estimatedIndicatorId || 0,
    );
    const selectedMeasuredIndicatorId = Number(
      terrainCertifiedEditorState.measuredIndicatorId || 0,
    );
    const selectedEstimatedRow = terrainCertifiedEditorState.allRows.find(
      (row) => Number(row.projectIndicatorId || 0) === selectedEstimatedIndicatorId,
    );
    const selectedMeasuredRow = terrainCertifiedEditorState.allRows.find(
      (row) => Number(row.projectIndicatorId || 0) === selectedMeasuredIndicatorId,
    );
    const optimisticCertifiedRows = shouldDeleteValue
      ? []
      : buildOptimisticLandPaymentRows({
          landPaymentId: optimisticLandPaymentId,
          amountEuro: certifiedAmountEuro,
          status: 3,
          expectedAt: certifiedIsoDate,
          dateMeasure: certifiedIsoDate,
          dateVerified: certifiedIsoDate,
          realFundingId,
          synthesesContractId,
          synthesesContractLineId,
          projectId,
          projectName: terrainCertifiedEditorState.lineLabel,
          lineTitle: selectedTargetLine?.label || null,
          pjVerified: terrainCertifiedEditorState.pjVerified || null,
          recipientSubcontractorId,
        })
          .filter((paymentRow) => Number(paymentRow.status || 0) === 3)
          .map((paymentRow) => ({
            ...paymentRow,
            recipientSubcontractor: optimisticRecipient,
          }));
    const selectedSiblingRowsForOptimisticLink = shouldDeleteValue
      ? []
      : [selectedEstimatedRow, selectedMeasuredRow]
          .filter((row): row is LinePaymentPreview => !!row)
          .map((row) => ({
            ...row,
            id: isPositiveId(row.projectIndicatorId)
              ? -Number(row.projectIndicatorId)
              : Number(row.id || 0),
            landPaymentId: optimisticLandPaymentId,
            landPaymentStatus: 3,
            realFundingId,
            synthesesContractId,
            synthesesContractLineId,
            lineId: synthesesContractLineId,
            lineTitle: selectedTargetLine?.label || row.lineTitle || null,
            amountLocal: Number(row.amountEuro || 0),
            source: "land_payment",
            recipientSubcontractorId,
            recipientSubcontractor: optimisticRecipient ?? row.recipientSubcontractor ?? null,
          }));
    const selectedCertifiedLinkIndicatorIds = [
      selectedEstimatedIndicatorId,
      selectedMeasuredIndicatorId,
    ].filter((indicatorId) => Number.isFinite(indicatorId) && indicatorId > 0);
    const selectedCertifiedSourceLandPaymentIds = [
      selectedEstimatedRow?.landPaymentId,
      selectedMeasuredRow?.landPaymentId,
      landPaymentId,
    ]
      .map((id) => Number(id || 0))
      .filter((id, index, ids) =>
        Number.isFinite(id) &&
        id > 0 &&
        id !== optimisticLandPaymentId &&
        ids.indexOf(id) === index,
      );
    const optimisticLinkedCertifiedRows = [
      ...selectedSiblingRowsForOptimisticLink,
      ...optimisticCertifiedRows,
    ];
    const optimisticSnapshot = shouldDeleteValue
      ? landPaymentId > 0
        ? groupedContractsQuery.setData((current) =>
            replaceLandPaymentStatusRowsInGroupedData(
              current,
              contractMatcher,
              landPaymentId,
              3,
              [],
            ),
          )
        : null
      : groupedContractsQuery.setData((current) =>
          replaceReconciledLandPaymentRowsInGroupedData(
            current,
            contractMatcher,
            optimisticLandPaymentId,
            selectedCertifiedLinkIndicatorIds,
            optimisticLinkedCertifiedRows,
            selectedCertifiedSourceLandPaymentIds,
          ),
        );
    const submittedCertifiedEditorState = terrainCertifiedEditorState;

    setSavingTerrainCertified(true);
    markTerrainTimelineCellSaving(certifiedSavingCellKey);
    setTerrainCertifiedEditorError(null);
    setTerrainCertifiedEditorState(null);

    void saveTerrainIndicatorToApi(
      {
        project: projectId,
        indicator: 1,
        year: Number(terrainCertifiedEditorState.year),
        month: terrainCertifiedEditorState.monthIndex,
        type: 3,
        value: shouldDeleteValue ? null : amount,
        devise: 0,
        ...(Number.isFinite(realFundingId) && realFundingId > 0
          ? { realFundingId, realfunding: realFundingId }
          : {}),
        ...(Number.isFinite(synthesesContractId) && synthesesContractId > 0
          ? { synthesesContractId }
          : {}),
        ...(Number.isFinite(synthesesContractLineId) && synthesesContractLineId > 0
          ? { synthesesContractLineId }
          : {}),
        ...(!shouldDeleteValue && recipientSubcontractorId
          ? { recipientSubcontractorId }
          : {}),
        pjs: shouldDeleteValue
          ? []
          : [Number(terrainCertifiedEditorState.pjVerified?.id || 0)].filter(
              (mediaId) => mediaId > 0,
            ),
        valueInserted: null,
        desiredFundings: [],
        obtainedFundings: [],
        ...(!shouldDeleteValue
          ? {
              ...(landPaymentId > 0
                ? {
                    landPaymentChoice: "existing",
                    targetLandPaymentId: landPaymentId,
                  }
                : {}),
              estimatedIndicatorId: selectedEstimatedIndicatorId,
              measuredIndicatorId: selectedMeasuredIndicatorId,
            }
          : {}),
      },
      true,
    )
      .then((savedIndicator) => {
        const savedProjectIndicatorId = Number(
          savedIndicator?.projectIndicatorId || savedIndicator?.indicatorId || 0,
        );
        const savedLandPaymentId = Number(savedIndicator?.landPaymentId || 0);
        const savedLandPaymentStatus = Number(savedIndicator?.landPaymentStatus || 3);
        const persistedLinkedCertifiedRows = optimisticLinkedCertifiedRows.map((paymentRow) => {
          const isCertifiedRow = Number(paymentRow.status || 0) === 3;

          return {
            ...paymentRow,
            id:
              isCertifiedRow && savedProjectIndicatorId > 0
                ? -savedProjectIndicatorId
                : paymentRow.id,
            projectIndicatorId:
              isCertifiedRow && savedProjectIndicatorId > 0
                ? savedProjectIndicatorId
                : paymentRow.projectIndicatorId,
            landPaymentId:
              savedLandPaymentId > 0
                ? savedLandPaymentId
                : paymentRow.landPaymentId,
            landPaymentStatus:
              savedLandPaymentStatus > 0
                ? savedLandPaymentStatus
                : paymentRow.landPaymentStatus,
          };
        });
        const persistedCertifiedLinkIndicatorIds = [
          ...selectedCertifiedLinkIndicatorIds,
          savedProjectIndicatorId,
        ].filter((indicatorId) => Number.isFinite(indicatorId) && indicatorId > 0);

        if (
          !shouldDeleteValue &&
          (savedProjectIndicatorId > 0 || savedLandPaymentId > 0)
        ) {
          groupedContractsQuery.setData((current) =>
            replaceReconciledLandPaymentRowsInGroupedData(
              current,
              contractMatcher,
              optimisticLandPaymentId,
              persistedCertifiedLinkIndicatorIds,
              persistedLinkedCertifiedRows,
              selectedCertifiedSourceLandPaymentIds,
            ),
          );
        }

        void refreshContracts().then(() => {
          if (
            !shouldDeleteValue &&
            (savedProjectIndicatorId > 0 || savedLandPaymentId > 0)
          ) {
            groupedContractsQuery.setData((current) =>
              replaceReconciledLandPaymentRowsInGroupedData(
                current,
                contractMatcher,
                savedLandPaymentId > 0 ? savedLandPaymentId : optimisticLandPaymentId,
                persistedCertifiedLinkIndicatorIds,
                persistedLinkedCertifiedRows,
                selectedCertifiedSourceLandPaymentIds,
              ),
            );
          }
        });
      })
      .catch((error: unknown) => {
        if (optimisticSnapshot) {
          groupedContractsQuery.restoreData(optimisticSnapshot);
        }
        setTerrainCertifiedEditorState(submittedCertifiedEditorState);
        setTerrainCertifiedEditorError(
          error instanceof Error
            ? error.message
            : "Erreur lors de la sauvegarde du versement certifié.",
        );
      })
      .finally(() => {
        setSavingTerrainCertified(false);
        unmarkTerrainTimelineCellSaving(certifiedSavingCellKey);
      });
  }, [
    groupedContractsQuery,
    markTerrainTimelineCellSaving,
    refreshContracts,
    saveTerrainIndicatorToApi,
    selectedRealSubcontractor,
    terrainCertifiedEditorState,
    unmarkTerrainTimelineCellSaving,
  ]);

  const handleConfirmTerrainPaymentEditor = useCallback(async () => {
    if (!terrainPaymentEditorState) {
      return;
    }

    const realFundingId = Number(terrainPaymentEditorState.realFundingId || 0);
    const synthesesContractId = Number(terrainPaymentEditorState.synthesesContractId || 0);
    const synthesesContractLineId = Number(terrainPaymentEditorState.synthesesContractLineId || 0);
    const amount = Number(
      String(terrainPaymentEditorState.amountEuro || "")
        .replace(/\s/g, "")
        .replace(",", "."),
    );
    const status = Number(terrainPaymentEditorState.status || 0);
    const recipientSubcontractorId = isPositiveId(
      terrainPaymentEditorState.recipientSubcontractorId,
    )
      ? Number(terrainPaymentEditorState.recipientSubcontractorId)
      : null;

    if (
      (!Number.isFinite(realFundingId) || realFundingId <= 0)
      && (!Number.isFinite(synthesesContractLineId) || synthesesContractLineId <= 0)
    ) {
      setTerrainPaymentEditorError("La ligne à l'intervenant est introuvable.");
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setTerrainPaymentEditorError("Renseignez un montant strictement positif.");
      return;
    }
    if (!terrainPaymentEditorState.expectedAt) {
      setTerrainPaymentEditorError("Renseignez la date prévue.");
      return;
    }
    if (status >= 2 && !terrainPaymentEditorState.dateMeasure) {
      setTerrainPaymentEditorError("Renseignez la date de réalisation.");
      return;
    }
    if (status >= 3 && !terrainPaymentEditorState.dateVerified) {
      setTerrainPaymentEditorError("Renseignez la date de certification.");
      return;
    }
    if (status >= 3 && !isPositiveId(terrainPaymentEditorState.pjVerified?.id)) {
      setTerrainPaymentEditorError("Ajoutez la pièce jointe de vérification.");
      return;
    }
    if (!recipientSubcontractorId) {
      setTerrainPaymentEditorError(
        "Impossible de rattacher ce versement à l'intervenant sélectionné.",
      );
      return;
    }

    setSavingTerrainPayment(true);
    setTerrainPaymentEditorError(null);

    const optimisticLandPaymentId = isPositiveId(terrainPaymentEditorState.landPaymentId)
      ? Number(terrainPaymentEditorState.landPaymentId)
      : -Date.now();
    const optimisticRows = buildOptimisticLandPaymentRows({
      landPaymentId: optimisticLandPaymentId,
      amountEuro: amount,
      status,
      expectedAt: terrainPaymentEditorState.expectedAt,
      dateMeasure: terrainPaymentEditorState.dateMeasure,
      dateVerified: terrainPaymentEditorState.dateVerified,
      realFundingId,
      synthesesContractId,
      synthesesContractLineId,
      projectId: terrainPaymentEditorState.projectId,
      projectName: terrainPaymentEditorState.projectName,
      lineTitle: terrainPaymentEditorState.lineTitle,
      pjVerified: terrainPaymentEditorState.pjVerified,
      recipientSubcontractorId,
    });
    const optimisticSnapshot = groupedContractsQuery.setData((current) =>
      replaceLandPaymentRowsInGroupedData(
        current,
        {
          synthesesContractId,
          realFundingIds: realFundingId > 0 ? [realFundingId] : [],
        },
        optimisticLandPaymentId,
        optimisticRows,
      ),
    );

    try {
      const savedLandPayment = await saveTerrainPaymentToApi({
        ...(Number.isFinite(realFundingId) && realFundingId > 0
          ? { realfunding: realFundingId }
          : {}),
        ...(Number.isFinite(synthesesContractId) && synthesesContractId > 0
          ? { synthesesContractId }
          : {}),
        ...(Number.isFinite(synthesesContractLineId) && synthesesContractLineId > 0
          ? { synthesesContractLineId }
          : {}),
        amount,
        expectedAt: terrainPaymentEditorState.expectedAt,
        status,
        ...(recipientSubcontractorId ? { recipientSubcontractorId } : {}),
        ...(status >= 2 ? { dateMeasure: terrainPaymentEditorState.dateMeasure } : {}),
        ...(status >= 3
          ? {
              dateVerified: terrainPaymentEditorState.dateVerified,
              pjVerified: Number(terrainPaymentEditorState.pjVerified?.id),
            }
          : {}),
        ...(isPositiveId(terrainPaymentEditorState.landPaymentId)
          ? { toEdit: Number(terrainPaymentEditorState.landPaymentId) }
          : {}),
      });
      const savedLandPaymentId = Number(savedLandPayment?.id || savedLandPayment?.landPaymentId || 0);
      if (Number.isFinite(savedLandPaymentId) && savedLandPaymentId > 0) {
        const finalRows = buildOptimisticLandPaymentRows({
          landPaymentId: savedLandPaymentId,
          amountEuro: amount,
          status,
          expectedAt: terrainPaymentEditorState.expectedAt,
          dateMeasure: terrainPaymentEditorState.dateMeasure,
          dateVerified: terrainPaymentEditorState.dateVerified,
          realFundingId,
          synthesesContractId,
          synthesesContractLineId,
          projectId: terrainPaymentEditorState.projectId,
          projectName: terrainPaymentEditorState.projectName,
          lineTitle: terrainPaymentEditorState.lineTitle,
          pjVerified: terrainPaymentEditorState.pjVerified,
          recipientSubcontractorId,
        });
        groupedContractsQuery.setData((current) =>
          replaceLandPaymentRowsInGroupedData(
            current,
            {
              synthesesContractId,
              realFundingIds: realFundingId > 0 ? [realFundingId] : [],
            },
            optimisticLandPaymentId,
            finalRows,
          ),
        );
      }
      void refreshContracts();
      setTerrainPaymentEditorState(null);
    } catch (error: unknown) {
      groupedContractsQuery.restoreData(optimisticSnapshot);
      setTerrainPaymentEditorError(
        error instanceof Error
          ? error.message
          : "Erreur lors de la sauvegarde du versement à l'intervenant.",
      );
    } finally {
      setSavingTerrainPayment(false);
    }
  }, [
    groupedContractsQuery,
    refreshContracts,
    saveTerrainPaymentToApi,
    terrainPaymentEditorState,
  ]);

  const handleCommitTerrainTimelineCell = useCallback(
    async (
      line: IntervenantContractLineItem,
      allRows: LinePaymentPreview[],
      payload: {
        status: 1 | 2 | 3;
        year: number;
        monthIndex: number;
        amountEuro: number | null;
        payment: LinePaymentPreview | null;
        recipientSubcontractorId?: number | null;
      },
    ) => {
      const status = Number(payload.status || 0);
      if (status !== 1 && status !== 2) {
        return;
      }

      const projectId = Number(line.projectId || payload.payment?.projectId || 0);
      const realFundingId = Number(line.realFundingId || payload.payment?.realFundingId || 0);
      const synthesesContractId = Number(
        line.synthesesContractId || payload.payment?.synthesesContractId || 0,
      );
      const synthesesContractLineId = Number(
        line.lineId ||
        payload.payment?.synthesesContractLineId ||
        payload.payment?.lineId ||
        0,
      );
      const recipientSubcontractorId =
        getLandPaymentRecipientId(payload.payment) ??
        (isPositiveId(payload.recipientSubcontractorId)
          ? Number(payload.recipientSubcontractorId)
          : selectedRecipientSubcontractorId);
      const normalizedAmount = Number(payload.amountEuro || 0);
      const shouldDeleteValue =
        payload.amountEuro === null ||
        !Number.isFinite(normalizedAmount) ||
        normalizedAmount <= 0;
      const cellDate = `${Number(payload.year)}-${String(payload.monthIndex + 1).padStart(2, "0")}-01`;
      const existingLandPaymentId = Number(payload.payment?.landPaymentId || 0);
      const landPaymentId = existingLandPaymentId > 0 ? existingLandPaymentId : -Date.now();
      const cellKey = buildIntervenantTerrainTimelineCellKey(
        line.id,
        recipientSubcontractorId,
        status,
        payload.year,
        payload.monthIndex,
      );

      if (!Number.isFinite(projectId) || projectId <= 0) {
        setTerrainPaymentEditorError("Le projet du versement à l'intervenant est introuvable.");
        return;
      }
      if (
        (!Number.isFinite(realFundingId) || realFundingId <= 0) &&
        (!Number.isFinite(synthesesContractLineId) || synthesesContractLineId <= 0)
      ) {
        setTerrainPaymentEditorError("La ligne à l'intervenant est introuvable.");
        return;
      }
      if (!shouldDeleteValue && !recipientSubcontractorId) {
        setTerrainPaymentEditorError(
          "Impossible de rattacher ce versement à l'intervenant sélectionné.",
        );
        return;
      }

      const relatedRows = existingLandPaymentId > 0
        ? allRows.filter((row) => Number(row.landPaymentId || 0) === existingLandPaymentId)
        : [];
      const existingDraft = buildTerrainPaymentEditorDraft(
        allRows,
        payload.payment,
        String(payload.year),
      );
      const optimisticRows = shouldDeleteValue
        ? []
        : buildOptimisticLandPaymentRows({
            landPaymentId,
            amountEuro: Number(normalizedAmount.toFixed(2)),
            status,
            expectedAt:
              status === 1
                ? cellDate
                : existingDraft.expectedAt || relatedRows[0]?.at || cellDate,
            dateMeasure:
              status === 2
                ? cellDate
                : existingDraft.dateMeasure || payload.payment?.dateMeasure || null,
            dateVerified:
              existingDraft.dateVerified || payload.payment?.dateVerified || null,
            realFundingId,
            synthesesContractId,
            synthesesContractLineId,
            projectId,
            projectName: line.projectName,
            lineTitle: line.lineTitle,
            pjVerified: payload.payment?.pjVerified || existingDraft.pjVerified || null,
            recipientSubcontractorId,
          }).filter((paymentRow) => Number(paymentRow.status || 0) === status);

      markTerrainTimelineCellSaving(cellKey);
      setTerrainPaymentEditorError(null);
      setTerrainReconcileState(null);
      setTerrainReconcileError(null);
      setTerrainQuickLinkState(null);
      setTerrainMoveState(null);
      setTerrainMoveError(null);

      const optimisticSnapshot = groupedContractsQuery.setData((current) =>
        replaceLandPaymentStatusRowsInGroupedData(
          current,
          {
            contractNodeId: line.contract.id,
            synthesesContractId,
          },
          landPaymentId,
          status,
          optimisticRows,
        ),
      );

      try {
        const savedIndicator = await saveTerrainIndicatorToApi({
          project: projectId,
          indicator: 1,
          year: Number(payload.year),
          month: payload.monthIndex,
          type: status,
          value: shouldDeleteValue ? null : normalizedAmount,
          devise: 0,
          ...(Number.isFinite(realFundingId) && realFundingId > 0
            ? { realFundingId, realfunding: realFundingId }
            : {}),
          ...(Number.isFinite(synthesesContractId) && synthesesContractId > 0
            ? { synthesesContractId }
            : {}),
          ...(Number.isFinite(synthesesContractLineId) && synthesesContractLineId > 0
            ? { synthesesContractLineId }
            : {}),
          ...(!shouldDeleteValue && recipientSubcontractorId
            ? { recipientSubcontractorId }
            : {}),
        });

        const savedProjectIndicatorId = Number(
          savedIndicator?.projectIndicatorId || savedIndicator?.indicatorId || 0,
        );
        const savedLandPaymentId = Number(savedIndicator?.landPaymentId || 0);
        const savedLandPaymentStatus = Number(
          savedIndicator?.landPaymentStatus || status,
        );
        const savedRecipientSubcontractorId = Number(
          savedIndicator?.recipientSubcontractorId || recipientSubcontractorId || 0,
        );

        if (!shouldDeleteValue && (savedProjectIndicatorId > 0 || savedLandPaymentId > 0)) {
          const persistedRows = optimisticRows.map((paymentRow) => ({
            ...paymentRow,
            id:
              savedProjectIndicatorId > 0
                ? -savedProjectIndicatorId
                : paymentRow.id,
            projectIndicatorId:
              savedProjectIndicatorId > 0
                ? savedProjectIndicatorId
                : paymentRow.projectIndicatorId,
            landPaymentId:
              savedLandPaymentId > 0
                ? savedLandPaymentId
                : paymentRow.landPaymentId,
            landPaymentStatus:
              savedLandPaymentStatus > 0
                ? savedLandPaymentStatus
                : paymentRow.landPaymentStatus,
            recipientSubcontractorId:
              savedRecipientSubcontractorId > 0
                ? savedRecipientSubcontractorId
                : paymentRow.recipientSubcontractorId,
          }));

          groupedContractsQuery.setData((current) =>
            replaceLandPaymentStatusRowsInGroupedData(
              current,
              {
                contractNodeId: line.contract.id,
                synthesesContractId,
              },
              landPaymentId,
              status,
              persistedRows,
            ),
          );
        }

        void refreshContracts();
      } catch (error: unknown) {
        groupedContractsQuery.restoreData(optimisticSnapshot);
        setTerrainPaymentEditorError(
          error instanceof Error
            ? error.message
            : "Erreur lors de la sauvegarde du versement à l'intervenant.",
        );
      } finally {
        unmarkTerrainTimelineCellSaving(cellKey);
      }
    },
    [
      groupedContractsQuery,
      markTerrainTimelineCellSaving,
      refreshContracts,
      saveTerrainIndicatorToApi,
      selectedRecipientSubcontractorId,
      unmarkTerrainTimelineCellSaving,
    ],
  );

  const handleDeleteTerrainPayment = useCallback(
    async (payment: LinePaymentPreview) => {
      const landPaymentId = Number(payment.landPaymentId || 0);
      if (!Number.isFinite(landPaymentId) || landPaymentId <= 0) {
        return;
      }

      const confirmed = window.confirm(
        "Supprimer ce versement à l'intervenant et les statuts qui lui sont liés ?",
      );
      if (!confirmed) {
        return;
      }

      setDeletingTerrainPaymentId(landPaymentId);
      setTerrainPaymentEditorError(null);
      const optimisticSnapshot = groupedContractsQuery.setData((current) =>
        removeLandPaymentRowsFromGroupedData(current, landPaymentId),
      );

      try {
        await deleteTerrainPaymentToApi(landPaymentId);
        void refreshContracts();
        setTerrainPaymentEditorState((current) => {
          if (!current) return current;
          return Number(current.landPaymentId || 0) === landPaymentId ? null : current;
        });
      } catch (error: unknown) {
        groupedContractsQuery.restoreData(optimisticSnapshot);
        setTerrainPaymentEditorError(
          error instanceof Error
            ? error.message
            : "Erreur lors de la suppression du versement à l'intervenant.",
        );
      } finally {
        setDeletingTerrainPaymentId(null);
      }
    },
    [deleteTerrainPaymentToApi, groupedContractsQuery, refreshContracts],
  );

  const handleOpenTerrainReconcile = useCallback(
    (
      line: IntervenantContractLineItem,
      plannedRows: LinePaymentPreview[],
      realizedRows: LinePaymentPreview[],
      certifiedRows: LinePaymentPreview[],
      targetLandPayment?: LinePaymentPreview | null,
    ) => {
      if (!line.realFundingId) {
        setEditorError("Impossible de retrouver le contrat terrain.");
        return;
      }

      const targetLandPaymentId = isPositiveId(targetLandPayment?.landPaymentId)
        ? Number(targetLandPayment?.landPaymentId)
        : null;
      const targetLandPaymentStatus = isPositiveId(targetLandPayment?.landPaymentStatus)
        ? Number(targetLandPayment?.landPaymentStatus)
        : (
          isPositiveId(targetLandPayment?.status)
            ? Number(targetLandPayment?.status)
            : null
        );

      setTerrainReconcileError(null);
      setTerrainMoveState(null);
      setTerrainMoveError(null);
      setTerrainPaymentEditorState(null);
      setTerrainPaymentEditorError(null);
      setTerrainCertifiedEditorState(null);
      setTerrainCertifiedEditorError(null);
      const selectedEstimatedId =
        null === targetLandPaymentId
          ? (plannedRows[0]?.projectIndicatorId ?? null)
          : (
            targetLandPaymentStatus === 2 || targetLandPaymentStatus === 3
              ? (plannedRows.length === 1 ? (plannedRows[0]?.projectIndicatorId ?? null) : null)
              : null
          );
      const selectedMeasuredId =
        null === targetLandPaymentId || targetLandPaymentStatus === 1
          ? (realizedRows[0]?.projectIndicatorId ?? null)
          : (
            targetLandPaymentStatus === 3
              ? (realizedRows.length === 1 ? (realizedRows[0]?.projectIndicatorId ?? null) : null)
              : null
          );
      const selectedVerifiedId =
        null === targetLandPaymentId
        || targetLandPaymentStatus === 1
        || targetLandPaymentStatus === 2
          ? (certifiedRows.length === 1 ? (certifiedRows[0]?.projectIndicatorId ?? null) : null)
          : null;

      setTerrainReconcileState({
        lineId: line.id,
        lineLabel: `${line.projectName} • ${line.financeurName}`,
        synthesesContractId: line.synthesesContractId,
        synthesesContractLineId: line.lineId,
        realFundingId: line.realFundingId,
        landPaymentId: targetLandPaymentId,
        landPaymentStatus: targetLandPaymentStatus,
        plannedRows,
        realizedRows,
        certifiedRows,
        selectedEstimatedId,
        selectedMeasuredId,
        selectedVerifiedId,
      });
    },
    [],
  );

  const handlePickTerrainReconcile = useCallback(
    (
      field: "selectedEstimatedId" | "selectedMeasuredId" | "selectedVerifiedId",
      indicatorId: number | null,
    ) => {
      setTerrainReconcileError(null);
      setTerrainReconcileState((current) => {
        if (!current) return current;

        return {
          ...current,
          [field]: indicatorId,
        };
      });
    },
    [],
  );

  const handleCancelTerrainReconcile = useCallback(() => {
    setTerrainReconcileState(null);
    setTerrainReconcileError(null);
  }, []);

  const handleStartTerrainQuickLink = useCallback((line: IntervenantContractLineItem) => {
    const projectKey = line.id;
    setTerrainReconcileState(null);
    setTerrainReconcileError(null);
    setTerrainPaymentEditorState(null);
    setTerrainPaymentEditorError(null);
    setTerrainCertifiedEditorState(null);
    setTerrainCertifiedEditorError(null);
    setTerrainMoveState(null);
    setTerrainMoveError(null);
    setTerrainQuickLinkState((current) => {
      if (current?.projectKey === projectKey) {
        return null;
      }

      return {
        projectKey,
        label: line.lineTitle || line.title || "Versements à l'intervenant",
        contractNodeId: line.contract.id,
        synthesesContractId: line.synthesesContractId,
        realFundingId: line.realFundingId,
        targetLandPaymentId: null,
        targetLandPaymentStatus: null,
        targetPayment: null,
        targetLandPaymentRows: [],
        selectedEstimatedId: null,
        selectedMeasuredId: null,
        selectedVerifiedId: null,
        selectedEstimatedPayment: null,
        selectedMeasuredPayment: null,
        selectedVerifiedPayment: null,
        error: null,
      };
    });
  }, []);

  const handleCancelTerrainQuickLink = useCallback(() => {
    setTerrainQuickLinkState(null);
  }, []);

  const handlePickTerrainQuickLinkCell = useCallback(
    (
      projectKey: string,
      payload: {
        status: 1 | 2 | 3;
        payment: LinePaymentPreview | null;
      },
      allRows: LinePaymentPreview[],
    ) => {
      const payment = payload.payment;
      if (!payment) return;

      const status = Number(payload.status || payment.status || 0) as 1 | 2 | 3;
      if (![1, 2, 3].includes(status)) return;

      const projectIndicatorId = Number(payment.projectIndicatorId || 0);
      const landPaymentId = Number(payment.landPaymentId || 0);
      const canUseIndicator = Number.isFinite(projectIndicatorId) && projectIndicatorId > 0;
      const canUseLandPayment = Number.isFinite(landPaymentId) && landPaymentId > 0;
      if (!canUseIndicator && !canUseLandPayment) return;

      setTerrainQuickLinkState((current) => {
        if (!current || current.projectKey !== projectKey) return current;

        const next: TerrainQuickLinkState = {
          ...current,
          error: null,
        };

        if (payment.source === "land_payment" && canUseLandPayment) {
          const isSameTarget =
            Number(current.targetLandPaymentId || 0) === landPaymentId;
          const isSameTargetRow =
            isSameTarget && current.targetPayment?.id === payment.id;
          const targetStatus = Number(payment.landPaymentStatus || status);

          if (isPositiveId(current.targetLandPaymentId) && !isSameTarget) {
            if (!canUseIndicator) {
              return {
                ...next,
                error: "Ce versement doit d'abord être lié à une valeur ESG pour être sélectionné.",
              };
            }

            if (status === 1) {
              const isSame = Number(current.selectedEstimatedId || 0) === projectIndicatorId;
              next.selectedEstimatedId = isSame ? null : projectIndicatorId;
              next.selectedEstimatedPayment = isSame ? null : payment;
            } else if (status === 2) {
              const isSame = Number(current.selectedMeasuredId || 0) === projectIndicatorId;
              next.selectedMeasuredId = isSame ? null : projectIndicatorId;
              next.selectedMeasuredPayment = isSame ? null : payment;
            } else {
              const isSame = Number(current.selectedVerifiedId || 0) === projectIndicatorId;
              next.selectedVerifiedId = isSame ? null : projectIndicatorId;
              next.selectedVerifiedPayment = isSame ? null : payment;
            }

            return next;
          }

          if (isSameTarget && !isSameTargetRow) {
            return next;
          }

          const targetLandPaymentRows = allRows.filter(
            (row) => Number(row.landPaymentId || 0) === landPaymentId,
          );

          next.targetLandPaymentId = isSameTargetRow ? null : landPaymentId;
          next.targetLandPaymentStatus = isSameTargetRow ? null : targetStatus;
          next.targetPayment = isSameTargetRow ? null : payment;
          next.targetLandPaymentRows = isSameTargetRow
            ? []
            : (targetLandPaymentRows.length > 0 ? targetLandPaymentRows : [payment]);

          return next;
        }

        if (!canUseIndicator) return next;

        if (status === 1) {
          const isSame = Number(current.selectedEstimatedId || 0) === projectIndicatorId;
          next.selectedEstimatedId = isSame ? null : projectIndicatorId;
          next.selectedEstimatedPayment = isSame ? null : payment;
        } else if (status === 2) {
          const isSame = Number(current.selectedMeasuredId || 0) === projectIndicatorId;
          next.selectedMeasuredId = isSame ? null : projectIndicatorId;
          next.selectedMeasuredPayment = isSame ? null : payment;
        } else {
          const isSame = Number(current.selectedVerifiedId || 0) === projectIndicatorId;
          next.selectedVerifiedId = isSame ? null : projectIndicatorId;
          next.selectedVerifiedPayment = isSame ? null : payment;
        }

        return next;
      });
    },
    [],
  );

  const handleConfirmTerrainQuickLink = useCallback(async () => {
    if (!terrainQuickLinkState) return;

    if (!canSubmitTerrainQuickLink(terrainQuickLinkState)) {
      setTerrainQuickLinkState((current) =>
        current
          ? {
              ...current,
              error:
                "Sélectionnez au moins un prévu et un mesuré, ou complétez le statut manquant de la ligne choisie.",
            }
          : current,
      );
      return;
    }

    const realFundingId = Number(terrainQuickLinkState.realFundingId || 0);
    if (!Number.isFinite(realFundingId) || realFundingId <= 0) {
      setTerrainQuickLinkState((current) =>
        current
          ? {
              ...current,
              error:
                "Impossible de retrouver le poste de dépenses lié à ces versements.",
            }
          : current,
      );
      return;
    }

    setSavingTerrainQuickLink(true);
    setTerrainQuickLinkState((current) =>
      current ? { ...current, error: null } : current,
    );

    const optimisticLandPaymentId = isPositiveId(terrainQuickLinkState.targetLandPaymentId)
      ? Number(terrainQuickLinkState.targetLandPaymentId)
      : -Date.now();
    const optimisticRows = buildOptimisticReconciledLandPaymentRows({
      state: terrainQuickLinkState,
      landPaymentId: optimisticLandPaymentId,
      realFundingId,
    });
    const selectedIndicatorIds = getTerrainQuickLinkSelectedIndicatorIds(terrainQuickLinkState);
    const selectedSourceLandPaymentIds =
      getTerrainQuickLinkSelectedSourceLandPaymentIds(terrainQuickLinkState);
    const optimisticSnapshot = optimisticRows.length > 0
      ? groupedContractsQuery.setData((current) =>
          replaceReconciledLandPaymentRowsInGroupedData(
            current,
            {
              contractNodeId: terrainQuickLinkState.contractNodeId,
              synthesesContractId: terrainQuickLinkState.synthesesContractId,
            },
            optimisticLandPaymentId,
            selectedIndicatorIds,
            optimisticRows,
            selectedSourceLandPaymentIds,
          ),
        )
      : null;
    const submittedQuickLinkState = terrainQuickLinkState;
    if (optimisticRows.length > 0) {
      setTerrainQuickLinkState(null);
    }

    try {
      const savedReconcile = await reconcileTerrainPayloadToApi({
        realFundingId,
        landPaymentId: isPositiveId(terrainQuickLinkState.targetLandPaymentId)
          ? Number(terrainQuickLinkState.targetLandPaymentId)
          : null,
        estimatedIndicatorId: isPositiveId(terrainQuickLinkState.selectedEstimatedId)
          ? Number(terrainQuickLinkState.selectedEstimatedId)
          : null,
        measuredIndicatorId: isPositiveId(terrainQuickLinkState.selectedMeasuredId)
          ? Number(terrainQuickLinkState.selectedMeasuredId)
          : null,
        verifiedIndicatorId: isPositiveId(terrainQuickLinkState.selectedVerifiedId)
          ? Number(terrainQuickLinkState.selectedVerifiedId)
          : null,
      });
      const savedLandPaymentId = Number(savedReconcile?.landPaymentId || 0);
      if (Number.isFinite(savedLandPaymentId) && savedLandPaymentId > 0) {
        const persistedRows = optimisticRows.map((row) => ({
          ...row,
          landPaymentId: savedLandPaymentId,
        }));
        groupedContractsQuery.setData((current) =>
          replaceReconciledLandPaymentRowsInGroupedData(
            current,
            {
              contractNodeId: terrainQuickLinkState.contractNodeId,
              synthesesContractId: terrainQuickLinkState.synthesesContractId,
            },
            optimisticLandPaymentId,
            selectedIndicatorIds,
            persistedRows,
            selectedSourceLandPaymentIds,
          ),
        );
      }
      void refreshContracts();
      setTerrainQuickLinkState(null);
    } catch (error: unknown) {
      if (optimisticSnapshot) {
        groupedContractsQuery.restoreData(optimisticSnapshot);
      }
      setTerrainQuickLinkState((current) =>
        (current ?? submittedQuickLinkState)
          ? {
              ...(current ?? submittedQuickLinkState),
              error:
                error instanceof Error
                  ? error.message
                  : "Erreur lors de la liaison rapide.",
            }
          : current,
      );
    } finally {
      setSavingTerrainQuickLink(false);
    }
  }, [
    groupedContractsQuery,
    reconcileTerrainPayloadToApi,
    refreshContracts,
    terrainQuickLinkState,
  ]);

  const handleOpenTerrainMove = useCallback(
    (
      line: IntervenantContractLineItem,
      payment: LinePaymentPreview,
      targetOptions: TerrainMoveTargetOption[],
    ) => {
      const landPaymentId = Number(payment.landPaymentId || 0);

      if (!Number.isFinite(landPaymentId) || landPaymentId <= 0) {
        setTerrainMoveError("Impossible de retrouver le versement a l'intervenant.");
        return;
      }

      setTerrainReconcileState(null);
      setTerrainReconcileError(null);
      setTerrainQuickLinkState(null);
      setTerrainMoveError(null);
      setTerrainPaymentEditorState(null);
      setTerrainPaymentEditorError(null);
      setTerrainCertifiedEditorState(null);
      setTerrainCertifiedEditorError(null);
      setTerrainMoveState({
        lineId: line.id,
        paymentRowId: payment.id,
        landPaymentId,
        selectedTargetLineId: targetOptions[0]?.lineId ?? null,
        targetOptions,
      });
    },
    [],
  );

  const handleChangeTerrainMoveTarget = useCallback((targetLineId: number | null) => {
    setTerrainMoveError(null);
    setTerrainMoveState((current) => {
      if (!current) return current;

      return {
        ...current,
        selectedTargetLineId: targetLineId,
      };
    });
  }, []);

  const handleCancelTerrainMove = useCallback(() => {
    setTerrainMoveState(null);
    setTerrainMoveError(null);
  }, []);

  const handleConfirmTerrainMove = useCallback(async () => {
    if (!terrainMoveState) {
      return;
    }

    const landPaymentId = Number(terrainMoveState.landPaymentId || 0);
    const targetLineId = Number(terrainMoveState.selectedTargetLineId || 0);

    if (!Number.isFinite(landPaymentId) || landPaymentId <= 0) {
      setTerrainMoveError("Le versement a l'intervenant est introuvable.");
      return;
    }
    if (!Number.isFinite(targetLineId) || targetLineId <= 0) {
      setTerrainMoveError("Choisissez la ligne cible.");
      return;
    }

    setMovingTerrainPayment(true);
    setTerrainMoveError(null);
    const optimisticSnapshot = groupedContractsQuery.setData((current) =>
      moveLandPaymentRowsInGroupedData(current, landPaymentId, targetLineId),
    );
    const submittedMoveState = terrainMoveState;
    setTerrainMoveState(null);

    try {
      await moveTerrainPayloadToApi({
        landPaymentId,
        targetLineId,
      });
      void refreshContracts();
    } catch (error: unknown) {
      groupedContractsQuery.restoreData(optimisticSnapshot);
      setTerrainMoveState(submittedMoveState);
      setTerrainMoveError(
        error instanceof Error ? error.message : "Erreur lors du déplacement du versement a l'intervenant.",
      );
    } finally {
      setMovingTerrainPayment(false);
    }
  }, [groupedContractsQuery, moveTerrainPayloadToApi, refreshContracts, terrainMoveState]);

  const handleConfirmTerrainReconcile = useCallback(
    async () => {
      if (!terrainReconcileState) {
        return;
      }

      const estimatedIndicatorId = Number(terrainReconcileState.selectedEstimatedId || 0);
      const measuredIndicatorId = Number(terrainReconcileState.selectedMeasuredId || 0);
      const verifiedIndicatorId = Number(terrainReconcileState.selectedVerifiedId || 0);
      const landPaymentId = Number(terrainReconcileState.landPaymentId || 0);
      const landPaymentStatus = Number(terrainReconcileState.landPaymentStatus || 0);

      if (!Number.isFinite(landPaymentId) || landPaymentId <= 0) {
        if (!Number.isFinite(estimatedIndicatorId) || estimatedIndicatorId <= 0) {
          setTerrainReconcileError("Choisissez un versement a l'intervenant prévu.");
          return;
        }
        if (!Number.isFinite(measuredIndicatorId) || measuredIndicatorId <= 0) {
          setTerrainReconcileError("Choisissez un versement a l'intervenant réalisé.");
          return;
        }
      } else if (landPaymentStatus === 1) {
        if (!Number.isFinite(measuredIndicatorId) || measuredIndicatorId <= 0) {
          setTerrainReconcileError("Choisissez un versement a l'intervenant réalisé.");
          return;
        }
      } else if (landPaymentStatus === 2) {
        if (
          (!Number.isFinite(estimatedIndicatorId) || estimatedIndicatorId <= 0)
          && (!Number.isFinite(verifiedIndicatorId) || verifiedIndicatorId <= 0)
        ) {
          setTerrainReconcileError("Choisissez un versement a l'intervenant prévu et/ou certifié.");
          return;
        }
      } else if (landPaymentStatus === 3) {
        if (
          (!Number.isFinite(estimatedIndicatorId) || estimatedIndicatorId <= 0)
          && (!Number.isFinite(measuredIndicatorId) || measuredIndicatorId <= 0)
        ) {
          setTerrainReconcileError("Choisissez un versement a l'intervenant prévu et/ou réalisé.");
          return;
        }
      }

      setSavingTerrainReconcile(true);
      setTerrainReconcileError(null);

      try {
        await reconcileTerrainPayloadToApi({
          realFundingId: Number(terrainReconcileState.realFundingId || 0),
          landPaymentId:
            Number.isFinite(landPaymentId) && landPaymentId > 0 ? landPaymentId : null,
          estimatedIndicatorId:
            Number.isFinite(estimatedIndicatorId) && estimatedIndicatorId > 0
              ? estimatedIndicatorId
              : null,
          measuredIndicatorId:
            Number.isFinite(measuredIndicatorId) && measuredIndicatorId > 0
              ? measuredIndicatorId
              : null,
          verifiedIndicatorId:
            Number.isFinite(verifiedIndicatorId) && verifiedIndicatorId > 0
              ? verifiedIndicatorId
              : null,
        });
        await refreshContracts();
        setTerrainReconcileState(null);
      } catch (error: unknown) {
        setTerrainReconcileError(
          error instanceof Error ? error.message : "Erreur lors du rapprochement.",
        );
      } finally {
        setSavingTerrainReconcile(false);
      }
    },
    [reconcileTerrainPayloadToApi, refreshContracts, terrainReconcileState],
  );

  const handleEditorSave = useCallback(async () => {
    if (!editorState) return;

    const isEditMode = editorState.mode === "edit";
    const financeurId = Number(
      isEditMode
        ? editorState.contract?.financier?.id || editorState.financeurId || 0
        : editorState.financeurId || 0,
    );
    if (!Number.isFinite(financeurId) || financeurId <= 0) {
      setEditorError("Choisissez un financeur.");
      return;
    }

    const contractDate = normalizeContractDateInput(
      isEditMode
        ? editorState.contract?.contractDate || editorState.contractDate
        : editorState.contractDate,
    );
    if (!contractDate) {
      setEditorError("Renseignez la date du contrat.");
      return;
    }

    const year = String(
      isEditMode
        ? /^\d{4}$/.test(editorState.initialYear)
          ? editorState.initialYear
          : editorState.year
        : editorState.year,
    ).trim();
    if (!/^\d{4}$/.test(year)) {
      setEditorError("Renseignez une annee valide au format YYYY.");
      return;
    }

    const amountValue = parseAmountInput(editorState.amountEuro);
    if (amountValue === null || amountValue <= 0) {
      setEditorError("Renseignez un montant valide.");
      return;
    }

    const title = isEditMode
      ? toTrimmedText(editorState.contract?.title) || toTrimmedText(editorState.title) || "Contrat"
      : toTrimmedText(editorState.title) || "Contrat";
    const notes = isEditMode
      ? toTrimmedText(editorState.contract?.notes) || String(editorState.notes || "").trim()
      : String(editorState.notes || "").trim();

    setSavingEditor(true);
    setEditorError(null);

    try {
      if (editorState.mode === "create") {
        const selectedContract = editorState.createSource === "existing"
          ? editorState.contract
          : null;

        if (!selectedContract) {
          await savePayloadToApi({
            financeurId,
            title,
            contractDate,
            amountEuro: amountValue.toFixed(2),
            notes,
            yearGroups: [
              {
                year,
                projectRows: [
                  {
                    projectId: editorState.projectId,
                    projectName: editorState.projectName,
                    title: toTrimmedText(editorState.lineTitle) || title,
                    amountEuro: amountValue.toFixed(2),
                    ...(editorState.initialDesiredFundingIds.length === 0 &&
                    editorState.desiredFundingSelectionTouched &&
                    editorState.desiredFundingIds.length > 0
                      ? {
                          desiredFundingIds: Array.from(
                            new Set(editorState.desiredFundingIds),
                          ),
                          desiredFundingAllocations:
                            editorState.desiredFundingAllocations,
                        }
                      : {}),
                  },
                ],
              },
            ],
          });
        } else {
          const currentYearGroups = buildEditableYearGroupsFromContract(selectedContract);
          const nextYearGroups = appendLineToYearGroups(
            currentYearGroups,
            editorState,
            amountValue.toFixed(2),
            year,
          );
          const sourceType = String(selectedContract.sourceType || "").trim();
          const isLegacy =
            sourceType === "legacy_real_funding" ||
            sourceType === "legacy_real_funding_group";

          if (isLegacy) {
            const legacyRealFundingIds = extractLegacyRealFundingIds(
              nextYearGroups,
              selectedContract,
            );
            if (!legacyRealFundingIds.length) {
              throw new Error("Impossible de retrouver les contrats legacy a migrer.");
            }

            await savePayloadToApi({
              sourceType: "legacy_real_funding",
              realFundingIds: legacyRealFundingIds,
              financeurId,
              title,
              contractDate,
              amountEuro: sumYearGroupsAmount(nextYearGroups),
              notes,
              yearGroups: mapYearGroupsToPayload(nextYearGroups),
            });
          } else {
            const contractId = extractSynthesesContractId(selectedContract);
            if (!contractId) {
              throw new Error("Identifiant contrat manquant.");
            }

            await savePayloadToApi({
              contractId,
              financeurId,
              title,
              contractDate,
              amountEuro: sumYearGroupsAmount(nextYearGroups),
              notes,
              yearGroups: mapYearGroupsToPayload(nextYearGroups),
            });
          }
        }
      } else {
        const contract = editorState.contract;
        if (!contract) {
          throw new Error("Contrat introuvable pour cette modification.");
        }

        const sourceType = String(contract.sourceType || "").trim();
        const isLegacy =
          sourceType === "legacy_real_funding" ||
          sourceType === "legacy_real_funding_group";

        if (isLegacy) {
          if (!editorState.targetRealFundingId) {
            throw new Error("Identifiant legacy manquant.");
          }

          await savePayloadToApi({
            sourceType: "legacy_real_funding",
            realFundingIds: [editorState.targetRealFundingId],
            financeurId,
            title,
            contractDate,
            amountEuro: amountValue.toFixed(2),
            notes,
            yearGroups: [
              {
                year,
                projectRows: [
                  {
                    realFundingId: editorState.targetRealFundingId,
                    projectId: editorState.projectId,
                    projectName: editorState.projectName,
                    title: toTrimmedText(editorState.lineTitle) || title,
                    amountEuro: amountValue.toFixed(2),
                    ...(editorState.initialDesiredFundingIds.length === 0 &&
                    editorState.desiredFundingSelectionTouched &&
                    editorState.desiredFundingIds.length > 0
                      ? {
                          desiredFundingIds: Array.from(
                            new Set(editorState.desiredFundingIds),
                          ),
                          desiredFundingAllocations:
                            editorState.desiredFundingAllocations,
                        }
                      : {}),
                  },
                ],
              },
            ],
          });
        } else {
          const contractId = extractSynthesesContractId(contract);
          if (!contractId) {
            throw new Error("Identifiant contrat manquant.");
          }

          const currentYearGroups = buildEditableYearGroupsFromContract(contract);
          const nextYearGroups = updateOrMoveLineInYearGroups(
            currentYearGroups,
            editorState,
            amountValue.toFixed(2),
            year,
          );

          await savePayloadToApi({
            contractId,
            financeurId,
            title,
            contractDate,
            amountEuro: sumYearGroupsAmount(nextYearGroups),
            notes,
            yearGroups: mapYearGroupsToPayload(nextYearGroups),
          });
        }
      }

      await refreshContracts();
      setEditorState(null);
      setEditorError(null);
      scrollToTargetNode(`project-${editorState.projectId}`);
    } catch (error: unknown) {
      setEditorError(
        error instanceof Error ? error.message : "Erreur lors de l'enregistrement.",
      );
    } finally {
      setSavingEditor(false);
    }
  }, [editorState, refreshContracts, savePayloadToApi]);

  const handleDeleteLine = useCallback(
    async (line: IntervenantContractLineItem) => {
      const confirmed = window.confirm(
        "Supprimer cette ligne de contrat ? Cette action est definitive.",
      );
      if (!confirmed) return;

      setDeletingLineId(line.id);
      setEditorError(null);

      try {
        const sourceType = String(line.contract.sourceType || "").trim();
        const isLegacy =
          sourceType === "legacy_real_funding" ||
          sourceType === "legacy_real_funding_group";

        if (isLegacy) {
          if (!line.realFundingId) {
            throw new Error("Identifiant legacy manquant.");
          }
          await deletePayloadToApi({
            sourceType: "legacy_real_funding",
            realFundingIds: [line.realFundingId],
          });
        } else {
          const contractId = extractSynthesesContractId(line.contract);
          if (!contractId) {
            throw new Error("Identifiant contrat manquant.");
          }

          const currentYearGroups = buildEditableYearGroupsFromContract(line.contract);
          const nextYearGroups = removeLineFromYearGroups(
            currentYearGroups,
            line.lineId,
            line.realFundingId,
          );

          if (nextYearGroups.length === 0) {
            await deletePayloadToApi({ contractId });
          } else {
            const financeurId = Number(line.contract.financier?.id || 0);
            if (!Number.isFinite(financeurId) || financeurId <= 0) {
              throw new Error("Financeur invalide pour ce contrat.");
            }

            await savePayloadToApi({
              contractId,
              financeurId,
              title: toTrimmedText(line.contract.title) || "Contrat",
              contractDate: normalizeContractDateInput(line.contract.contractDate),
              amountEuro: sumYearGroupsAmount(nextYearGroups),
              notes: toTrimmedText(line.contract.notes),
              yearGroups: nextYearGroups.map((group) => ({
                year: group.year,
                projectRows: group.projectRows.map((row) => ({
                  lineId: row.lineId,
                  realFundingId: row.realFundingId,
                  projectId: row.projectId,
                  projectName: row.projectName,
                  title: row.title,
                  amountEuro: row.amountEuro,
                })),
              })),
            });
          }
        }

        await refreshContracts();
      } catch (error: unknown) {
        setEditorError(
          error instanceof Error ? error.message : "Erreur lors de la suppression.",
        );
      } finally {
        setDeletingLineId(null);
      }
    },
    [deletePayloadToApi, refreshContracts, savePayloadToApi],
  );

  return (
    <TooltipProvider delayDuration={120}>
      <Dialog
        open={terrainCertifiedEditorState !== null}
        onOpenChange={(open) => {
          if (open || savingTerrainCertified) return;
          handleCancelTerrainCertifiedEditor();
        }}
      >
        <DialogContent className="sm:max-w-[640px]">
          <DialogTitle className="text-xl font-semibold text-slate-900">
            Certifier le versement à l&apos;intervenant
          </DialogTitle>

          {terrainCertifiedEditorState ? (
            <div className="space-y-4">
              <div className="rounded-2xl border border-orange-100 bg-orange-50/70 px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-orange-700">
                  Suivi de performance ESG
                </div>
                <div className="mt-1 text-sm text-slate-700">
                  {terrainCertifiedEditorState.lineLabel}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {INTERVENANT_TIMELINE_MONTH_COLUMNS.find(
                    (month) => month.index === terrainCertifiedEditorState.monthIndex,
                  )?.label || "Mois"}{" "}
                  {terrainCertifiedEditorState.year}
                </div>
              </div>

              {terrainCertifiedEditorError ? (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {terrainCertifiedEditorError}
                </div>
              ) : null}

              <div className="grid gap-3 md:grid-cols-2">
                <label className="space-y-1.5 text-sm">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Montant certifié €
                  </span>
                  <input
                    value={terrainCertifiedEditorState.amountEuro}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      if (
                        nextValue === "" ||
                        nextValue === "-" ||
                        /^\d*([,.]\d*)?$/.test(nextValue.replace(/\s/g, ""))
                      ) {
                        handleChangeTerrainCertifiedEditor({ amountEuro: nextValue });
                      }
                    }}
                    className={SYNTHESES_INPUT_CLASS}
                    placeholder="-"
                  />
                  <span className="block text-[11px] text-slate-500">
                    Laissez vide ou saisissez - pour supprimer la valeur.
                  </span>
                </label>

                <div className="space-y-1.5 text-sm">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Pièce jointe
                  </span>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2">
                    <div className="text-xs text-slate-600">
                      Preuve de vérification
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <input
                        type="file"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (!file) return;
                          void handleUploadTerrainCertifiedProof(file).catch((error: unknown) => {
                            setTerrainCertifiedEditorError(
                              error instanceof Error
                                ? error.message
                                : "Erreur lors de l'upload du justificatif.",
                            );
                          });
                          event.currentTarget.value = "";
                        }}
                        className="block max-w-full text-xs text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-white file:px-3 file:py-2 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-50"
                      />
                      {terrainCertifiedEditorState.pjVerified?.id ? (
                        <div className="flex items-center gap-2">
                          <a
                            href={getTerrainMediaHref(terrainCertifiedEditorState.pjVerified)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs font-medium text-emerald-700 underline underline-offset-2"
                          >
                            {getTerrainMediaLabel(terrainCertifiedEditorState.pjVerified)}
                          </a>
                          <button
                            type="button"
                            className="text-xs font-medium text-rose-600 hover:text-rose-700"
                            onClick={() =>
                              handleChangeTerrainCertifiedEditor({ pjVerified: null })
                            }
                          >
                            Retirer
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-500">
                          Aucun justificatif ajouté
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                <div className="text-sm font-semibold text-slate-900">
                  Lier au poste de dépenses
                  <span className="ml-1 text-red-500">*</span>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-slate-600">
                  Choisissez le poste du contrat sur lequel ce certifié doit
                  apparaître. C&apos;est l&apos;équivalent de la liaison faite
                  depuis le suivi de performance ESG.
                </p>

                <div className="mt-3 space-y-2">
                  {terrainCertifiedEditorState.targetLineOptions.length > 0 ? (
                    terrainCertifiedEditorState.targetLineOptions.map((option) => {
                      const isSelected =
                        terrainCertifiedEditorState.selectedLineId === option.lineId;

                      return (
                        <button
                          key={`intervenant-certified-target-line-${option.lineId}`}
                          type="button"
                          onClick={() =>
                            handleChangeTerrainCertifiedEditor({
                              selectedLineId: option.lineId,
                            })
                          }
                          className={cn(
                            "flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left text-sm transition",
                            isSelected
                              ? "border-emerald-400 bg-white text-slate-950 ring-1 ring-emerald-200"
                              : "border-emerald-100 bg-white/70 text-slate-700 hover:border-emerald-300 hover:bg-white",
                          )}
                          aria-pressed={isSelected}
                        >
                          <span className="min-w-0 truncate">{option.label}</span>
                          <span
                            className={cn(
                              "h-2.5 w-2.5 shrink-0 rounded-full border",
                              isSelected
                                ? "border-emerald-600 bg-emerald-600"
                                : "border-slate-300 bg-white",
                            )}
                          />
                        </button>
                      );
                    })
                  ) : (
                    <div className="rounded-xl border border-rose-100 bg-white px-3 py-2 text-xs text-rose-700">
                      Aucun poste de dépenses disponible pour ce projet.
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                <div className="text-sm font-semibold text-slate-900">
                  Associer d&apos;autres indicateurs à ce poste
                  <span className="ml-1 text-red-500">*</span>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-slate-600">
                  Comme dans le suivi de performance ESG, un certifié doit être
                  rattaché à un prévu et à un réalisé déjà saisis. Ils peuvent
                  être sur un autre mois.
                </p>

                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <label className="space-y-1.5 text-sm">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Prévu
                    </span>
                    <select
                      value={terrainCertifiedEditorState.estimatedIndicatorId ?? ""}
                      onChange={(event) =>
                        handleChangeTerrainCertifiedEditor({
                          estimatedIndicatorId: event.target.value
                            ? Number(event.target.value)
                            : null,
                        })
                      }
                      className={SYNTHESES_INPUT_CLASS}
                    >
                      <option value="">Choisir un prévu</option>
                      {terrainCertifiedEditorState.estimatedOptions.map((option) => (
                        <option
                          key={`intervenant-certified-estimated-${option.id}`}
                          value={option.id}
                          disabled={option.disabled}
                        >
                          {option.label}
                          {option.disabled ? " - déjà lié à un autre certifié" : ""}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="space-y-1.5 text-sm">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Réalisé
                    </span>
                    <select
                      value={terrainCertifiedEditorState.measuredIndicatorId ?? ""}
                      onChange={(event) =>
                        handleChangeTerrainCertifiedEditor({
                          measuredIndicatorId: event.target.value
                            ? Number(event.target.value)
                            : null,
                        })
                      }
                      className={SYNTHESES_INPUT_CLASS}
                    >
                      <option value="">Choisir un réalisé</option>
                      {terrainCertifiedEditorState.measuredOptions.map((option) => (
                        <option
                          key={`intervenant-certified-measured-${option.id}`}
                          value={option.id}
                          disabled={option.disabled}
                        >
                          {option.label}
                          {option.disabled ? " - déjà lié à un autre certifié" : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className={SYNTHESES_SECONDARY_BUTTON_CLASS}
                  onClick={handleCancelTerrainCertifiedEditor}
                  disabled={savingTerrainCertified}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className={SYNTHESES_PRIMARY_BUTTON_CLASS}
                  onClick={handleConfirmTerrainCertifiedEditor}
                  disabled={savingTerrainCertified}
                >
                  {savingTerrainCertified ? "Enregistrement..." : "Sauvegarder"}
                </button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={terrainPaymentEditorState !== null}
        onOpenChange={(open) => {
          if (open || savingTerrainPayment) return;
          handleCancelTerrainPaymentEditor();
        }}
      >
        <DialogContent className="sm:max-w-[620px]">
          <DialogTitle className="text-xl font-semibold text-slate-900">
            Modifier le versement à l&apos;intervenant
          </DialogTitle>

          {terrainPaymentEditorState ? (
            <div className="space-y-4">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                  {getLandStatusLabel(Number(terrainPaymentEditorState.status || 1))}
                </div>
                <div className="mt-1 text-sm text-slate-700">
                  {terrainPaymentEditorState.lineLabel}
                </div>
                {selectedRealSubcontractor ? (
                  <div className="mt-1 text-xs text-slate-500">
                    Destinataire : {selectedRealSubcontractor.name}
                  </div>
                ) : null}
              </div>

              {terrainPaymentEditorError ? (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {terrainPaymentEditorError}
                </div>
              ) : null}

              <div className="grid gap-3 md:grid-cols-2">
                <label className="space-y-1.5 text-sm">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Montant €
                  </span>
                  <input
                    type="text"
                    value={terrainPaymentEditorState.amountEuro}
                    onChange={(event) =>
                      handleChangeTerrainPaymentEditor({
                        amountEuro: event.target.value,
                      })
                    }
                    className={SYNTHESES_INPUT_CLASS}
                    placeholder="0,00"
                  />
                </label>

                <label className="space-y-1.5 text-sm">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Date prévue
                  </span>
                  <input
                    type="date"
                    value={terrainPaymentEditorState.expectedAt}
                    onChange={(event) =>
                      handleChangeTerrainPaymentEditor({
                        expectedAt: event.target.value,
                      })
                    }
                    className={SYNTHESES_INPUT_CLASS}
                  />
                </label>

                {Number(terrainPaymentEditorState.status || 0) >= 2 ? (
                  <label className="space-y-1.5 text-sm">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Date de réalisation
                    </span>
                    <input
                      type="date"
                      value={terrainPaymentEditorState.dateMeasure}
                      onChange={(event) =>
                        handleChangeTerrainPaymentEditor({
                          dateMeasure: event.target.value,
                        })
                      }
                      className={SYNTHESES_INPUT_CLASS}
                    />
                  </label>
                ) : null}

                {Number(terrainPaymentEditorState.status || 0) >= 3 ? (
                  <label className="space-y-1.5 text-sm">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Date de certification
                    </span>
                    <input
                      type="date"
                      value={terrainPaymentEditorState.dateVerified}
                      onChange={(event) =>
                        handleChangeTerrainPaymentEditor({
                          dateVerified: event.target.value,
                        })
                      }
                      className={SYNTHESES_INPUT_CLASS}
                    />
                  </label>
                ) : null}
              </div>

              {Number(terrainPaymentEditorState.status || 0) >= 3 ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2">
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Pièce jointe de vérification
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <input
                      type="file"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (!file) return;
                        void handleUploadTerrainPaymentProof(file).catch((error: unknown) => {
                          setTerrainPaymentEditorError(
                            error instanceof Error
                              ? error.message
                              : "Erreur lors de l'upload du justificatif.",
                          );
                        });
                        event.currentTarget.value = "";
                      }}
                      className="block max-w-full text-xs text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-white file:px-3 file:py-2 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-50"
                    />
                    {terrainPaymentEditorState.pjVerified?.id ? (
                      <div className="flex items-center gap-2">
                        <a
                          href={getTerrainMediaHref(terrainPaymentEditorState.pjVerified)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-medium text-emerald-700 underline underline-offset-2"
                        >
                          {getTerrainMediaLabel(terrainPaymentEditorState.pjVerified)}
                        </a>
                        <button
                          type="button"
                          className="text-xs font-medium text-rose-600 hover:text-rose-700"
                          onClick={() =>
                            handleChangeTerrainPaymentEditor({ pjVerified: null })
                          }
                        >
                          Retirer
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-500">
                        Aucun justificatif ajouté
                      </span>
                    )}
                  </div>
                </div>
              ) : null}

              <div className="flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  className={SYNTHESES_SECONDARY_BUTTON_CLASS}
                  onClick={handleCancelTerrainPaymentEditor}
                  disabled={savingTerrainPayment}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className={cn(
                    SYNTHESES_PRIMARY_BUTTON_CLASS,
                    savingTerrainPayment ? "cursor-not-allowed opacity-70" : "",
                  )}
                  onClick={handleConfirmTerrainPaymentEditor}
                  disabled={savingTerrainPayment}
                >
                  {savingTerrainPayment ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[260px_minmax(0,1fr)]">
        <aside className="rounded-xl border bg-white p-4 xl:sticky xl:top-6 xl:h-fit">
          <div className="mb-3 text-xs uppercase tracking-wide text-slate-500">
            Organisations
          </div>

          {subcontractorsQuery.loading || subcontractorsQuery.isFetching ? (
            <IntervenantListSkeleton />
          ) : (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setSelectedSubcontractorId(UNASSIGNED_SUBCONTRACTOR_ID)}
                className={cn(
                  "w-full rounded-xl border p-3 text-left transition",
                  isUnassignedProjectsSelected
                    ? "border-amber-300 bg-amber-50/80 shadow-sm"
                    : "border-amber-200 bg-amber-50/35 hover:border-amber-300 hover:bg-amber-50",
                )}
              >
                <div className="text-sm font-medium text-slate-900">
                  {UNASSIGNED_SUBCONTRACTOR_LABEL}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {unassignedProjectsLoading || programsCatalogQuery.loading || programsCatalogQuery.isFetching
                    ? "Calcul en cours"
                    : `${unassignedProjects.length} projet${unassignedProjects.length > 1 ? "s" : ""}`}
                </div>
              </button>
              {subcontractors.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                  Aucun intervenant trouve.
                </div>
              ) : null}
              {subcontractors.map((subcontractor) => {
                const isActive = subcontractor.id === selectedSubcontractorId;
                return (
                  <button
                    key={subcontractor.id}
                    type="button"
                    onClick={() => setSelectedSubcontractorId(subcontractor.id)}
                    className={cn(
                      "w-full rounded-xl border p-3 text-left transition",
                      isActive
                        ? "border-emerald-300 bg-emerald-50/70 shadow-sm"
                        : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50",
                    )}
                  >
                    <div className="text-sm font-medium text-slate-900">
                      {subcontractor.name}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {subcontractor.memberCount > 1
                        ? `${subcontractor.memberCount} intervenants`
                        : subcontractor.email || "Email non renseigne"}
                    </div>
                    {subcontractor.fullAccess ? (
                      <div className="mt-2 inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                        Acces complet
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}
        </aside>

        <section className="min-w-0 space-y-4">
          {!selectedSubcontractor ? (
            <div className="rounded-xl border bg-white p-6 text-sm text-slate-500">
              Selectionnez une organisation pour voir ses projets affectes.
            </div>
          ) : (
            <>
              <div
                ref={stickyHeaderRef}
                data-syntheses-sticky-header="true"
                className={cn(
                  "rounded-b-2xl",
                  isStickyHeaderActive ? "shadow-[0_18px_24px_-22px_rgba(15,23,42,0.4)]" : "shadow-none",
                )}
              >
                <div className="space-y-4 pb-4">
                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                    <div className="border-b border-slate-200 bg-gradient-to-r from-emerald-50/90 via-white to-sky-50/60 px-4 py-4">
                      <div className="min-w-0 space-y-2">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                            {isUnassignedProjectsSelected ? "Affectation" : "Organisation"}
                          </div>
                          <div className="text-xl font-semibold text-slate-900">
                            {selectedSubcontractor.name}
                          </div>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1">
                              {isUnassignedProjectsSelected
                                ? "Aucun intervenant local financé associé"
                                : selectedSubcontractor.memberCount > 1
                                  ? `${selectedSubcontractor.memberCount} intervenants`
                                  : selectedSubcontractor.email || "Email non renseigne"}
                            </span>
                            {!isUnassignedProjectsSelected && selectedSubcontractor.fullAccess ? (
                              <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 font-medium text-emerald-700">
                                Acces complet
                              </span>
                            ) : null}
                          </div>
                          <div className="mt-3 flex flex-wrap items-center gap-1.5">
                            {intervenantYearFilterOptions.map((yearOption) => (
                              <button
                                key={`intervenant-header-year-${yearOption}`}
                                type="button"
                                onClick={() =>
                                  setSelectedYearBySubcontractor((current) => ({
                                    ...current,
                                    [String(selectedSubcontractorId || "")]: yearOption,
                                  }))}
                                className={cn(
                                  "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition",
                                  selectedIntervenantYear === yearOption
                                    ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                    : "border-slate-200 bg-white/90 text-slate-600 hover:bg-white",
                                )}
                              >
                                {yearOption === "all" ? "Tout" : yearOption}
                              </button>
                            ))}
                            <SectionHint className="ml-1">
                              <div className="space-y-1.5">
                                <div>
                                  {isUnassignedProjectsSelected
                                    ? "Le filtre principal suit les années trouvées dans les contrats des projets sans intervenant affecté."
                                    : "Le filtre principal suit les années trouvées dans les contrats de cette organisation."}
                                </div>
                                <ul className="list-disc space-y-1 pl-4">
                                  <li>les cartes du haut se mettent à jour</li>
                                  <li>la liste des projets se limite aux projets ayant un contrat sur cette année</li>
                                  <li>la timeline terrain garde son propre choix d&apos;année mois par mois</li>
                                </ul>
                              </div>
                            </SectionHint>
                          </div>
                      </div>
                    </div>

                    <div className="p-4">
                      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        <StatCard
                          icon={<BriefcaseBusiness className="h-4 w-4" />}
                          label="Montant total contrats"
                          value={formatEuro(summaryTotals.totalContractsEuro)}
                          loading={!accessReady || !contractsReady}
                          tone="teal"
                        />
                        <StatCard
                          icon={<CalendarDays className="h-4 w-4" />}
                          label="Versements a l'intervenant"
                          value={formatEuro(summaryTotals.terrainPaymentsEuro)}
                          loading={!accessReady || !contractsReady}
                          tone="olive"
                        />
                        <StatCard
                          icon={<BriefcaseBusiness className="h-4 w-4" />}
                          label={structureShareLabel}
                          value={formatEuro(summaryTotals.structureShareEuro)}
                          loading={!accessReady || !contractsReady}
                          tone="rose"
                        />
                      </div>
                    </div>

                    <div className="border-t border-slate-200 px-4 pb-4 pt-3">
                      <div className="mb-3 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                        <div className="flex items-start gap-2">
                          <div className="space-y-1">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                              Timeline
                            </div>
                            <div className="text-sm font-medium text-slate-900">
                              Versements à l&apos;intervenant par mois
                            </div>
                            <div className="text-xs text-slate-500">
                              Prévu, réalisé et certifié, agrégés sur les projets visibles.
                            </div>
                          </div>
                          <SectionHint className="mt-0.5">
                            <div className="space-y-1.5">
                              <div>La timeline terrain agrège les versements des projets actuellement visibles.</div>
                              <ul className="list-disc space-y-1 pl-4">
                                <li>prévu = attendu</li>
                                <li>réalisé = exécuté sur le terrain</li>
                                <li>certifié = réalisé avec pièce de vérification</li>
                              </ul>
                            </div>
                          </SectionHint>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {terrainTimelineAvailableYears.map((year) => {
                            const isActive = year === selectedTerrainTimelineYear;

                            return (
                              <button
                                key={`intervenant-terrain-timeline-year-${year}`}
                                type="button"
                                onClick={() =>
                                  setSelectedTerrainTimelineYearByScope((current) => ({
                                    ...current,
                                    [`${String(selectedSubcontractorId || "")}:${selectedIntervenantYear}`]:
                                      year,
                                  }))}
                                className={cn(
                                  "rounded-full border px-3 py-1 text-xs font-medium transition",
                                  isActive
                                    ? "border-emerald-300 bg-emerald-600 text-white"
                                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50",
                                )}
                              >
                                {year}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {!accessReady || !contractsReady ? (
                        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-40 rounded-full" />
                            <Skeleton className="h-24 w-full rounded-xl" />
                          </div>
                        </div>
                      ) : (
                        <IntervenantTerrainMonthlyTimeline
                          yearSummary={selectedTerrainTimelineSummary}
                        />
                      )}
                    </div>
                  </div>

                  {activeAccessError ? (
                    <div className="border-t border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700">
                      {activeAccessError}
                    </div>
                  ) : groupedContractsQuery.error ? (
                    <div className="border-t border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-700">
                      Impossible de charger les contrats projet.
                    </div>
                  ) : !accessReady ? (
                    <div className="border-t border-slate-200 bg-slate-50/40 px-4 py-4">
                      <div className="space-y-4">
                        <Skeleton className="h-11 w-full rounded-2xl" />
                        <Skeleton className="h-72 w-full rounded-2xl" />
                      </div>
                    </div>
                  ) : visibleAssignedProjects.length === 0 ? (
                    <div className="border-t border-slate-200 bg-slate-50/40 px-4 py-6 text-sm text-slate-500">
                      {isUnassignedProjectsSelected
                        ? "Aucun projet non affecté sur cette année."
                        : "Aucun projet affecte a cette organisation sur cette année."}
                    </div>
                  ) : (
                    <div className="border-t border-slate-200 bg-slate-50/35">
                      <div className="space-y-2 p-4">
                        <div>
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                                {isUnassignedProjectsSelected ? "Projets non affectés" : "Projets affectés"}
                              </div>
                              <SectionHint>
                                <div className="space-y-1.5">
                                  <div>
                                    {isUnassignedProjectsSelected
                                      ? "Ces projets ne sont associés à aucun intervenant local financé."
                                      : "Chaque projet apparaît une seule fois, même s'il possède plusieurs contrats."}
                                  </div>
                                  <ul className="list-disc space-y-1 pl-4">
                                    <li>cliquez sur une carte pour afficher son détail en dessous</li>
                                    <li>le montant sur la carte cumule les contrats du projet visibles ici</li>
                                  </ul>
                                </div>
                              </SectionHint>
                            </div>
                            <div className="text-[11px] text-slate-500">
                              {visibleAssignedProjects.length} projet{visibleAssignedProjects.length > 1 ? "s" : ""}
                            </div>
                          </div>
                          <CustomHorizontalScroller compact>
                            <div className="flex min-w-max gap-3 pb-1">
                              {visibleAssignedProjects.map((project) => {
                                const projectVisual =
                                  projectVisualCatalog.get(Number(project.id || 0)) || null;
                                const isSelected = selectedProjectCardId === project.id;

                                return (
                                  <div
                                    key={project.id}
                                    className="relative flex w-[220px] shrink-0 pb-3"
                                  >
                                    <div
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => {
                                        setSelectedProjectCardIdBySubcontractor((current) => ({
                                          ...current,
                                          [String(selectedSubcontractorId || "")]: project.id,
                                        }));
                                      }}
                                      onKeyDown={(event) => {
                                        if (event.key !== "Enter" && event.key !== " ") return;
                                        event.preventDefault();
                                        setSelectedProjectCardIdBySubcontractor((current) => ({
                                          ...current,
                                          [String(selectedSubcontractorId || "")]: project.id,
                                        }));
                                      }}
                                      className={cn(
                                        "flex h-full w-full cursor-pointer flex-col overflow-hidden rounded-[15px] border bg-white text-left shadow-sm transition focus:outline-none focus:ring-2 focus:ring-emerald-200",
                                        isSelected
                                          ? "border-emerald-300 ring-2 ring-emerald-200"
                                          : "border-slate-200 hover:border-slate-300",
                                      )}
                                    >
                                      <div className="relative h-[126px] overflow-hidden bg-slate-700">
                                        <img
                                          src={resolveProjectPictureHref(projectVisual?.pictureUri)}
                                          alt={project.name}
                                          className="size-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
                                        <img
                                          src="/folder.svg"
                                          alt=""
                                          className="absolute left-3 top-3 w-5 opacity-95"
                                        />
                                        <div className="absolute bottom-3 right-3 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-slate-900 shadow-sm">
                                          {formatEuro(project.totalAmountEuro)}
                                        </div>
                                      </div>
                                      <div className="flex min-h-[78px] flex-1 flex-col justify-between space-y-1 px-3 py-3">
                                        <div className="flex items-start justify-between gap-2">
                                          <div
                                            title={project.name}
                                            className="line-clamp-2 text-sm font-medium leading-5 text-slate-900"
                                          >
                                            {project.name}
                                          </div>
                                          {getProjectDashboardHref(project.id) ? (
                                            <a
                                              href={getProjectDashboardHref(project.id) || undefined}
                                              target="_blank"
                                              rel="noreferrer noopener"
                                              onClick={(event) => event.stopPropagation()}
                                              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
                                              title="Ouvrir le projet dans un nouvel onglet"
                                            >
                                              <ExternalLink className="h-3.5 w-3.5" />
                                            </a>
                                          ) : null}
                                        </div>
                                        <div className="text-xs text-slate-500">
                                          {projectVisual?.programName || project.programName || "Programme non renseigné"}
                                        </div>
                                      </div>
                                    </div>
                                    {isSelected ? (
                                      <div className="pointer-events-none absolute bottom-[5px] left-1/2 z-10 h-4 w-4 -translate-x-1/2 rotate-45 rounded-[2px] border-b border-r border-emerald-300 bg-white" />
                                    ) : null}
                                  </div>
                                );
                              })}
                            </div>
                          </CustomHorizontalScroller>
                        </div>

                        <div className="-mt-1 space-y-4">
                          {selectedAssignedProject ? (
                            <ProjectSection
                              key={selectedAssignedProject.id}
                              project={selectedAssignedProject}
                              selectedYear={selectedIntervenantYear}
                              contractsReady={contractsReady}
                              recipientMemberIds={selectedRecipientMemberIds}
                              recipientOptions={selectedRealSubcontractor?.memberOptions || []}
                              savingTerrainTimelineCellKeys={savingTerrainTimelineCellKeys}
                              terrainMoveState={terrainMoveState}
                              terrainMoveError={terrainMoveError}
                              movingTerrainPayment={movingTerrainPayment}
                              terrainQuickLinkState={terrainQuickLinkState}
                              savingTerrainQuickLink={savingTerrainQuickLink}
                              onCommitTerrainTimelineCell={handleCommitTerrainTimelineCell}
                              onOpenTerrainCertifiedTimelineEditor={(
                                line,
                                allRows,
                                payload,
                              ) =>
                                handleOpenTerrainCertifiedTimelineEditor(
                                  line,
                                  allRows,
                                  payload,
                                )
                              }
                              onStartTerrainQuickLink={handleStartTerrainQuickLink}
                              onCancelTerrainQuickLink={handleCancelTerrainQuickLink}
                              onPickTerrainQuickLinkCell={handlePickTerrainQuickLinkCell}
                              onConfirmTerrainQuickLink={handleConfirmTerrainQuickLink}
                              onOpenTerrainMove={handleOpenTerrainMove}
                              onCancelTerrainMove={handleCancelTerrainMove}
                              onChangeTerrainMoveTarget={handleChangeTerrainMoveTarget}
                              onConfirmTerrainMove={handleConfirmTerrainMove}
                            />
                          ) : null}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </section>

      </div>
    </TooltipProvider>
  );
}

export default SynthesesIntervenantsBoard;
